import { ARC_MS, type Arc, SHAFT_MS, ClientCreature, DIR_INDEX, FISH_SWIM_MS, Game, NOVA_OUT, Picked, SPELL_COLOR, shotMs, sparkMs, UNSEEN, WEB_FLIGHT_MS, WHIP_MS, WHIP_RETRACT_MS } from "./game";
import { bodiesFirst, bodySprite, isBody } from "./corpses";
import { blockTile, byDepth, coversNorth, coversWest, dimmed, DoorAt, evenWash, softDimmed, washPatch, walkerKey, doorFacets, doorFacing, doorPlane, faceTransform, FACE_SHADE, facePath, faceSprites, ghostOf, groundRegions, hobbitDoorPiece, inclineAt, inclineFacets, inclineOf, inFace, inPoly, isRaised, Job, LADDER_W, ladderAt, ladderFace, patternOf, pickAt, pickThrough, Piece, PIECE_PAD, pieceOf, propFacets, propPicture, proppedBy, Pt, raisedPiece, shadowOf, Shown, solidAt, stairFacets, stairPicture, stairwellPicture, standAt, standingDoor, standingKey, STOREY, TREE_PX, TREE_SCALE, isRoof, outlineOnTop, treeCut, underRoof, wallMass } from "./oblique";
import { walkable } from "./pathfind";
import { isRampTile, rampDir } from "./ramps";
import { shrubPicture } from "./shrub";
import { creaturesOverNames, namesBehind, type BehindName, type Figure } from "./namefit";
import { PARTY_TREE_FOOT, partyTreePicture } from "./partytree";
import { SHIP_FOOT, shipPicture } from "./ship";
import { SPIRE_FOOT, iceSpirePicture } from "./icespire";
import { ChestKind, chestCoversNorth, chestFacets, chestKindOf, chestPicture, isStandingChest } from "./chest";
import { BLOOD_RGB, TILE_COLORS, bloodSprite, BLAZES, CREATURE_H, creatureSprite, doorFace, riderSprite, drawSkullMark, FLICKER_MS, FLICKERS, fishSprite, groundSprite, hatchSprite, inclineSprite, inkAt, inkTop, inkTopUnder, ladderShaftSprite, ladderSprite, rockCell, SPAN_EAST, SPAN_MID, SPAN_ONE, SPAN_TURNED, SPAN_WEST, SPATTER_RGB, spatterSprite, TILE, tileSprite, STANDING_TREE_ITEMS, WATERFALL_ITEM } from "./sprites";
import { Tile } from "./types";
import type { OpenSpace, UnseenLight } from "./types";
import { drawMissile } from "./missiles";
import { keptContext } from "./paper";

/**
 * The colours the aggro rings are drawn in, with the debug switch on
 * (2026-10-16: "Show creature clank as a red outline in debug, show
 * alertness as blue"). Blue is alertness, how far a creature notices
 * others: dashed, and faint and dotted for a monster that cannot hear
 * you at all however close you stand. Red
 * is clank, how far off a creature is noticed: round a monster its own
 * noise, round you yours, darker when your gear has taken it below
 * nothing. A blue square touching a red one is somebody noticing somebody.
 */
const AGGRO_ALERT = "rgba(80,150,255,0.95)";
const AGGRO_CLANK = "rgba(255,50,50,0.9)";
const AGGRO_CLANK_HUSHED = "rgba(150,20,30,0.85)";
const AGGRO_DEAF = "rgba(80,150,255,0.45)";

/**
 * The zooms, in screen pixels a tile, closest first. Thirty-two is the
 * sprites' own size and the view the world is designed around; two is the
 * old minimap's coarsest, a whole country in a window.
 *
 * It is a size and not a tile count, which is the whole of the change
 * asked for on 2026-09-20: \"Don't lock the aspect ratio of the main
 * window. Show whatever we can in the main window." How many tiles are on
 * screen is now a fact about the window's shape rather than a number
 * written down here — a tall window shows a tall piece of the world and a
 * wide one a wide piece, and neither is letterboxed to make it match the
 * other. The one number left is how big a tile is.
 *
 * Under FLAT_BELOW pixels a tile the ground is painted as flat colour
 * instead of drawn: see TILE_COLORS, and asked again — "Allow zooming out
 * to the point it looks like the old minimap, where indistinguishable
 * tiles use the appropriate colouring they had".
 */
const ZOOMS = [64, 48, 44, 38, 32, 24, 16, 12, 8, 5, 3, 2];
/**
 * The rung the world is drawn at until somebody says otherwise, and the
 * one the reset button goes back to: 38 (see the end of this note). It was 32, the sprites' own size, which is what every piece
 * of art in the game was drawn for.
 *
 * It is not the closest rung and has not been since 2026-09-21 ("add an
 * extra level of zoom in"), which put 48 above it; 64 went above that on
 * 2026-09-22, asked for in the same words. So the default sits two rungs
 * down from the top rather than at the end of the ladder, and every place
 * that treated "closest" and "home" as the same number had to learn the
 * difference — the reset lands here rather than at rung zero, and greys
 * itself here rather than there.
 *
 * A magnified sprite is the price of a rung above 32, and it is a price
 * worth naming. Both of the rungs above draw a 32-pixel sprite bigger
 * than it was painted, with smoothing off, so what you get is the same
 * picture with bigger pixels — which is what zooming into pixel art is,
 * and is why nobody is moved up here without asking. 64 is the better of
 * the two for it: it is exactly twice 32, so every source pixel becomes a
 * clean 2x2 block, where 48 is one and a half and has to make some
 * pixels two wide and some one. That is also why there is no rung above
 * 64 — three times the art is a wall of colour, not a closer look — and
 * why 64 rather than, say, 40.
 *
 * Text is not magnified at any of this, since the canvas rasterises
 * glyphs through the view transform at the display's own resolution.
 *
 * Since 2026-10-10 the default is 44, not 32 ("the main window tiles
 * should be 44x44"): a tile is then as big as the smallest thing a finger
 * can be asked to hit, the same 44 every button's hit region now has.
 * 44 is one and three eighths of the art, so like 48 it draws some source
 * pixels a screen pixel wider than others; that is what was asked for.
 * 32 stays a rung, two out, and a zoom somebody chose and was remembered
 * (as a size, see zoomPx) still comes back as they left it.
 *
 * 38 went in between 44 and 32 on 2026-10-16 ("Add 38 to the zoom
 * levels"), a step out from the default. Like 44 and 48 it draws some
 * source pixels a screen pixel wider than others.
 *
 * Since 2026-10-17 the default is 38 ("Make the default zoom 38px"),
 * one and three sixteenths of the art: a step out from 44, more of the
 * world on a phone, and 44 stays a rung in.
 */
const ZOOM_DEFAULT = 3;

/**
 * Under this many screen pixels a tile, the ground is flat colour and the
 * creatures are dots. A 32-pixel sprite squeezed into six is mush, and
 * mush that costs as much to draw as the real thing; the flat colours are
 * what the minimap used at these sizes and they read better than what
 * they replace, which is the argument the minimap always made for itself.
 *
 * It is exported because the map view faces the identical question and
 * must not answer it with a second number. That page zooms out to two
 * pixels a tile to fit the whole world, and it draws monster stations with
 * the same creature sprites this file draws creatures with, so "how small
 * is too small for a sprite" is one fact about the artwork rather than one
 * fact about each page. See drawSpawns in mapview.ts.
 */
export const FLAT_BELOW = 8;
/** The closest zoom, in pixels a tile, the land's names are written at:
 *  the last of the close view's rungs before the flat map, and every one
 *  of the map's (see drawPlaceNames). */
export const PLACE_NAMES_AT = 8;

/**
 * How big to make the canvas, in tiles, before the page has told us how big
 * it actually is. Nothing but that: the window has no shape of its own and
 * has not had one since 2026-09-20, and this is the size of one frame drawn
 * against a canvas whose clientWidth is still zero.
 *
 * It was VIEW_W = 15 and VIEW_H = 11 until 2026-09-22 — Tibia's classic
 * window, the shape this game was first built to — and by then it was the
 * last thing in the client still stating one. The server read it across the
 * file boundary to work out what a player could see, so the shape outlived
 * every line that drew with it. Asked for that day: "check it and other
 * things no longer use the old aspect ratios."
 *
 * Square, and 19, because that is the server's view ring across: the most
 * the world will ever tell this client about at once (see viewR). A canvas
 * that has not been measured yet is therefore sized to exactly what there
 * could be to draw on it, rather than to the shape of somebody else's
 * client from 1997.
 */
export const VIEW_FALLBACK = 19;

/** The wash laid over protection-zone ground. */
const PZ_TINT = "rgba(120, 180, 255, 0.14)";

/**
 * How bright the world is drawn, tile by tile: four steps, added up.
 * Asked for on 2026-10-13: "50% brightness should be the default
 * everywhere. -25% for areas visible on a different level. +25% within the
 * radius of a light. +25% within the player's own alertness".
 *
 * So a tile of your own floor that you know is at half its brightness
 * (BRIGHT_BASE), a half more inside any light's radius (LIGHT_LIFT, since
 * 2026-10-14: "+50% in range of a light capped at 100% brightness"; it was
 * a quarter), and a quarter more within your own alertness (ALERT_LIFT, over the
 * `you.alert` tiles the server sends: one, or five with a great ring on;
 * 2026-10-13: "It's not 2 tiles, it's whatever the player's alertness
 * is"). The two lifts are separate and the sum is capped at the whole: a
 * lit tile is whole, your alertness past the light is at three quarters
 * ("What we want is extra alertness tiles being beyond the light to appear
 * around 75% when underground"), and there is no light of the eyes'
 * own under the ground (2026-10-13: "There's no implicit light in 2
 * tiles. It's whatever your alertness is adds 25%"), so in a cave with no
 * lamp the tiles round you are at three quarters. A tile of another level, a floor looked at up
 * or down or the country under a roof, is a quarter down from that
 * (LEVEL_DIM), so a lit floor looked at is at three quarters. Above the
 * ground every tile is lit by the sky (2026-10-13: "Above ground there is
 * no 10 tiles of light, everywhere above ground is always lit"), which
 * lifts it as a light does, a half (2026-10-14: "Above ground,
 * everything is lit 100%"): the country you know is whole wherever it is,
 * and a level drawn round the one looked at — the roofs over the street,
 * the country under a tower — at three quarters. Under the ground a floor
 * you look at is a half where no light of it reaches. Ground you have never seen is black, because
 * it is never drawn.
 *
 * THE LEVEL LOOKED AT READS OVER THE REST (2026-10-16: "When I'm at ground
 * level looking at a higher level, the currently active level should be
 * brighter than the surrounding visible levels. Integrate this with the
 * existing brightness rules"). The level looked at is your own, or the one
 * the floor buttons show; every other level drawn with it — the roofs over
 * the street, the country under a tower, the street under a look up — is a
 * quarter down from it, and gets no step for your alertness. So on the
 * ground the street is whole and the roofs at three quarters; on a look up
 * the roofs are whole, as if you stood on them (2026-10-16: "when viewing
 * another level by using the level chip, don't apply the -25% for
 * offlevel"), and the street round them at three quarters. Each thing is
 * washed by its own level's brightness, not the scene's.
 *
 * EACH STEP IS A STEP, BLENDED AT ITS EDGE. The edge of a light is where
 * the tile goes from the whole to a half, or to three quarters inside your
 * alertness, and the edge of your alertness in the dark is where it goes
 * from three quarters to a half. The fades that came
 * before (2026-09-25: "make light gradually transition to dimmed at the
 * edges", then two tiles of it inside the light) were a ramp from the
 * light to the dim; the four steps were asked for in place of all of it.
 * What is drawn of a step since 2026-10-18 ("blend light at transition
 * points") is each tile at its own brightness in its middle, going over
 * into the next tile's across the edge between them, rather than a hard
 * line along it: see layBlended. The steps are the same numbers; the
 * floor only shades from one to the next where they meet.
 *
 * SEEING A THING AND SEEING THE FLOOR ARE TWO QUESTIONS. A creature is
 * drawn if and only if it stands inside some light's radius — litAt,
 * below — or within your own alertness on your own floor, and not by how bright the floor under it came out, since the
 * floor is drawn at a half all over a level a player has walked. The dim
 * is for the room; the light is for what is in it. What stands in a light
 * is washed with the floor it stands on (brightAt), and what fades a thing
 * by the dark — a name, a number, a puff of dust — asks darkAt, which is
 * nought anywhere a light reaches and one past them all; within your
 * alertness draw takes it as nought too.
 *
 * THE FLOOR IS DRAWN, THE CREATURES ARE SENT. The ground and its items
 * come within the whole view ring and the client paints the dark
 * over them. Which creatures come at all is the server's since 2026-10-13
 * ("To see and target something, they must be within alertness tiles, or
 * within alertness+target's clank and standing on a lit tile"), because
 * that is also what can be targeted: past your alertness, only what stands
 * in a light you can see, out to your alertness and its clank (unheard and
 * litFor in world.go), the clank under the ground being at least the light
 * a thing carries. The lit test below still runs over what is sent, and
 * passes everything the server's own did.
 *
 * WHY IT IS A SQUARE. Distance in this world is Chebyshev — a diagonal
 * step costs what a straight one does — so the tiles within two of you are
 * a five-by-five square, and a light or an alertness of two is that square. A round pool of light
 * would be prettier and would lie about every corner of it: the corner
 * tile is two steps away exactly like the edge tile, and a creature there
 * is exactly as close. The aggro rings are squares for the same reason,
 * and the same argument is written out on them.
 *
 * NUMBERS, PLURAL, BECAUSE EVERY LIGHT IS SEEN. A light is a thing in the
 * world and not a private sense: a friend standing in the dark with a lamp
 * lights the floor round themself, and you see it lit, exactly as they
 * do. Alertness is not: a friend's is theirs. So the
 * server sends every player's light on their creature as well as yours in
 * `you.light`, and a tile is lit if ANY light in view reaches it (litAt,
 * below). Each light is measured from where its bearer is drawn standing,
 * the interpolated position, so a walking friend's light walks with them.
 * The list of lights is built once a frame, in draw, and handed to
 * everything that asks. Only lights on the viewer's own floor count: a
 * player seen down a hole is a storey away, and their lamp lights that
 * storey. Alertness is not like that: it is yours, and only the tiles
 * round you, on your own floor, get its step (2026-10-13: "the player's
 * own alertness").
 *
 * No lights at all means no darkness at all: nothing has been told yet how
 * far anybody sees, and the world is drawn as it is rather than half dark.
 */
const BRIGHT_BASE = 0.5;
/** A level other than the one you stand on, a quarter down: see BRIGHT_BASE. */
const LEVEL_DIM = 0.25;
/** Inside the radius of any light on the level, a half up, so a lit tile of
 *  your own floor is whole whatever your alertness: see BRIGHT_BASE. */
const LIGHT_LIFT = 0.5;
/** Within your own alertness, a quarter more: see BRIGHT_BASE. */
const ALERT_LIFT = 0.25;

/**
 * The levels drawn under a level above the ground, dimmed, lowest first:
 * the ground and every level between it and this one, and nothing under
 * the ground. Asked for on 2026-10-07: "when looking up levels above
 * ground, show lower levels dimmed down to the ground level". A rooftop
 * or a summit looked at on its own was a few islands of roof in a black
 * sea, and the country they stand in was the one thing that said where
 * they are; now it is drawn under them, as it would be seen from up
 * there, faded so the level being looked at is still the one that reads.
 *
 * Nothing under the ground, ever: a cave is a storey of rock away from
 * the sky, as terrainOf has it, and a level above the ground drawn over a
 * cave would be the map saying the two are one place. Empty for the
 * ground and under it, which is every scene as it was before.
 *
 * Exported because the map view's level buttons ask the same question of
 * the same world (see draw in mapview.ts), and must not answer it twice.
 */
export function levelsUnder(z: number): number[] {
  const out: number[] = [];
  for (let lz = 0; lz < z; lz++) out.push(lz);
  return out;
}

/**
 * How much of itself a level at or above the ground is drawn when you are
 * not standing on it: BRIGHT_BASE less LEVEL_DIM, plus LIGHT_LIFT since
 * above the ground is always lit, three quarters (2026-10-14: "Above
 * ground, everything is lit 100%"; a half from 2026-10-13). The map view
 * lays the levels under a raised one, lowest first, and then its own
 * background over them all once at 1 − UNDER_KEEP before the level shown
 * goes on top, so each shows at this much of itself however far down it
 * is. Until then each was washed a storey's worth before the next went on,
 * so the ground under a tower's top was a quarter of a half. See
 * levelsUnder. The game's own views wash each level by brightAt instead,
 * which comes to the same stood on a roof or on a look up from the street
 * (2026-10-16: "the currently active level should be brighter than the
 * surrounding visible levels"; a look draws its level whole since the same
 * day).
 */
export const UNDER_KEEP = 0.75;

/**
 * The colour a monster's name is written in over its head: a bright red,
 * a shade lighter than #ff3333 so it still reads on the dark of a cave
 * floor (2026-10-07: "switch monsters to use the brighter red as
 * suggested"). It was the orange NPC_NAME is until then, which monsters
 * and the world's people shared. No player colour is ever red-like, so a
 * red name over a head says monster and nothing else.
 *
 * Exported and named once so that anything else that ought to read as a
 * monster's name takes the same red rather than a near copy of it.
 */
export const MONSTER_NAME = "#ff4040";

/**
 * The colour of the world's own people's names, Grubb and the rest: the
 * orange every creature that was not a player wore until 2026-10-07. They
 * kept it when the monsters went red, so the two read apart at a glance —
 * the one you can talk to and the one that will bite you.
 */
export const NPC_NAME = "#f96";

/** A player's name when they have no colour of their own yet. */
const PLAYER_NAME = "#ee6";

/**
 * The colour a creature's name is written in: a player's own colour, the
 * world's people orange, and every monster MONSTER_NAME. One rule for both
 * scenes' nameplates, which used to spell it out each for themselves.
 */
export function nameColour(c: { kind: string; color?: string; npc?: boolean; invisible?: boolean; ally?: boolean; allycolor?: string }): string {
  // An admin gone /invisible is named in grey, whatever their colour: see
  // GHOST_ALPHA.
  if (c.invisible) return GHOST_NAME;
  if (c.kind === "player") return c.color || PLAYER_NAME;
  // A monster convinced to fight for a wizard is named in their colour,
  // so it reads as theirs rather than as one more red name (2026-10-26).
  if (c.ally) return c.allycolor || PLAYER_NAME;
  return c.npc ? NPC_NAME : MONSTER_NAME;
}

/**
 * The least a monster's name is ever faded to by the dark over the tile it
 * stands on. A plate is faded by alpha, 1 - darkness, and until 2026-10-13
 * the last tiles of a light stood at a third and two thirds of the way to
 * dark (there is no fade now, and a plate in a light is whole); the old
 * orange held up at a third, but red on a dark floor at a third is all but
 * gone, so a monster at the edge of your light had a body and no name
 * (seen in a preview of the red on 2026-10-07). The floor only holds up
 * names that are drawn at all, which is the lit list, so it says nothing
 * about anything the dark is hiding. The health bar under it still fades
 * all the way, as every other plate's does.
 */
export const MONSTER_NAME_FLOOR = 0.8;

/**
 * Puts the players last in a list of nameplates, keeping the order within
 * each, so a player's name and bar are laid over a monster's beside them
 * (2026-10-28: "prefer to draw the player over the monster in this kind
 * of set up", where a forest troll's name was written over the player's).
 */
function playersLast<T extends { c: { kind: string } }>(plates: T[]): void {
  plates.sort((a, b) => Number(a.c.kind === "player") - Number(b.c.kind === "player"));
}

/**
 * How opaque a creature's name is drawn, given the darkness `d` over the
 * tile it stands on: 1 - d, held up to MONSTER_NAME_FLOOR for a monster.
 */
export function nameAlpha(c: { kind: string; npc?: boolean; invisible?: boolean }, d: number): number {
  // An admin gone /invisible fades with their figure: see GHOST_ALPHA.
  if (c.invisible) return (1 - d) * GHOST_ALPHA;
  const a = 1 - d;
  return c.kind === "player" || c.npc ? a : Math.max(MONSTER_NAME_FLOOR, a);
}

/**
 * How dark a point `dist` tiles from the bearer of a light that reaches
 * `light` tiles is, for the things drawn in it: nought inside the reach,
 * one past it, and nothing between since 2026-10-13, when the fade went
 * (see BRIGHT_BASE). A light of nought is no light and no dark.
 */
export function darknessAt(dist: number, light: number): number {
  if (light <= 0) return 0;
  return dist <= light ? 0 : 1;
}

/**
 * How much black is laid over a tile drawn at brightness `b`: the rest of
 * it, 1 − b, over ground the player knows. Over ground they do not, the
 * whole of it, black, unless the tile is in a light: something standing on
 * a tile the map has not caught up with is in the light all the same.
 *
 * It is a function of its own since the oblique view (2026-09-27,
 * "implement the oblique view with south and east faces visible"), which
 * lays the dark in two places: over the ground, a square a tile, in
 * drawDarkness as it always was; and over each block's top and faces and
 * each thing standing on a raised floor as it is painted, since those are
 * drawn in depth order and a wash laid over the finished frame would
 * darken the front of a wall by the ground behind it. Two places laying
 * one wash is one rule, written here once.
 */
export function washAt(b: number, known: boolean): number {
  return known || b > BRIGHT_BASE ? 1 - b : 1;
}

/**
 * A light in the cave: where its bearer is drawn standing, in tiles and
 * fractional while they walk, how far it reaches, and the level it is on.
 * The level matters only in a hollow open across levels (see
 * lightReaches); left out, the light is on the floor it lights.
 */
export interface LightSource {
  x: number;
  y: number;
  light: number;
  z?: number;
  /** The viewer's alertness, on the viewer's own light and no other. */
  alert?: number;
  /** Where the alertness is measured from: the tile the viewer stands on,
   *  which is the tile they are walking to from the moment the step
   *  starts. The light itself walks with them, from x and y. */
  ax?: number;
  ay?: number;
  /** The colour part of this light is drawn in, "red", "blue", "white" or
   *  "rainbow" (see rainbowTint), and how
   *  far that part reaches: see tintAt. Absent for plain light. */
  tint?: string;
  tintR?: number;
}

/**
 * The colours a light can be tinted, as two r,g,b each. Asked for on
 * 2026-10-13: "Can we add a red or blue tint to certain types of light?"
 * Red is a fire's, an orange-red, the fire elementals' round Durin's Bane;
 * blue is the elven lights', the phial of Galadriel and Sting glowing at
 * orcs; white the other side's, seen by wraith-sight. Which light is which colour is the server's to say
 * (World.tintOf); a colour not named here is drawn plain.
 *
 * `light` is the colour of the light itself, which what it falls on is
 * multiplied by (at TINT_STRENGTH), as a white floor lit red is red and a
 * black one stays black: it takes the other colours out. `glow` is a little of it laid
 * back on over the top (screen, at TINT_GLOW), so the tinted floor is not
 * only darker but lit. A wash of the colour alone greyed a brown floor
 * under blue rather than turning it blue, and tinting by hue alone was
 * paint rather than light (both tried on 2026-10-13).
 */
export const TINTS: Readonly<Record<string, { light: RGB; glow: RGB }>> = {
  red: { light: [255, 160, 100], glow: [255, 92, 24] },
  blue: { light: [115, 165, 255], glow: [70, 140, 255] },
  // The white of the other side (2026-10-20): what wraith-sight sees one
  // who lives in both worlds shine with, Galadriel, as Frodo saw
  // Glorfindel. A silver-white, cool beside a torch. See otherSideLight.
  white: { light: [225, 235, 255], glow: [255, 255, 255] },
};

/** A colour as red, green and blue, nought to 255 each. */
type RGB = readonly [number, number, number];

/**
 * How strongly a tint's light is multiplied in, and how much of its glow
 * is laid back over what it lights: see TINTS. Both were full and a fifth
 * until 2026-10-17 ("Make the red and blue light sources more gentle"),
 * which washed a whole room solid blue round a phial; now what it falls
 * on keeps most of its own colour and takes a cast of the light's.
 */
export const TINT_STRENGTH = 0.45;
export const TINT_GLOW = 0.1;

/**
 * How strongly a tint is laid on the ground above it, on the bearer's own
 * tile, where the sky and not the light is what the ground is seen by
 * (2026-10-17: "Above ground, coloured light sources should still be
 * discernable"); it fades from there to nothing at the edge of its reach.
 */
export const DAYLIGHT_TINT = 0.5;

/**
 * And how much of its glow is screened back over the ground there, more
 * than underground's TINT_GLOW: on grass a tint multiplied in alone reads
 * as dirt, and it is the glow that makes it read as a light.
 */
export const DAYLIGHT_GLOW = 0.45;

/**
 * The colour the point (x, y) is lit in, or none: every tinted light whose
 * tinted part reaches it (a Chebyshev square of `tintR`, as a light's reach
 * is) blended together, each weighed by how close the point is to it. A
 * light counts for all of itself on its own tile and less each tile out,
 * as the square of what is left of its reach, so its own colour holds near
 * it and gives way to its neighbour's towards the edge. Asked of a level,
 * only lights that reach that level count, as for litAt.
 *
 * Blended since 2026-10-14 ("Can you blend lights?"): where the Balrog
 * walks into a phial's light the floor between them goes from red through
 * purple to blue. Until then the light of the shorter reach took the tile
 * whole. A colour of light the client does not know adds nothing.
 */
export function tintAt(x: number, y: number, lights: readonly LightSource[], z?: number, space?: OpenSpace): { light: RGB; glow: RGB; near: number; cover: number } | undefined {
  let sum = 0, near = 0, cover = 0;
  const light = [0, 0, 0], glow = [0, 0, 0];
  let lightChroma = 0, glowChroma = 0;
  for (const l of lights) {
    const r = l.tintR ?? 0;
    const t = l.tint === "rainbow" ? rainbowTint(x - l.x, y - l.y) : l.tint ? TINTS[l.tint] : undefined;
    if (!t || !(r > 0)) continue;
    if (z !== undefined && !lightReaches(l, x, y, z, space)) continue;
    // Weighed too by how much of the tile its reach covers, so a tint
    // carried mid-step glides with its bearer as the light does: see
    // lightCover.
    const c = lightCover(l, r, x, y);
    if (c <= 0) continue;
    const d = Math.max(Math.abs(x - l.x), Math.abs(y - l.y));
    const w = c * ((r + 1 - Math.min(d, r + 1)) / (r + 1)) ** 2;
    if (!(w > 0)) continue;
    sum += w;
    near = Math.max(near, w);
    cover = Math.max(cover, c);
    for (let k = 0; k < 3; k++) {
      light[k] += w * t.light[k];
      glow[k] += w * t.glow[k];
    }
    lightChroma += w * chroma(t.light);
    glowChroma += w * chroma(t.glow);
  }
  if (sum <= 0) return undefined;
  return { light: vivid(light.map((c) => c / sum), lightChroma / sum), glow: vivid(glow.map((c) => c / sum), glowChroma / sum), near, cover };
}

/**
 * The Arkenstone's light (2026-11-03: "exaggerate the rainbow colors with
 * the arkenstone"), which has no one colour: white on its bearer's own
 * tile, and round them the whole spectrum laid out on the ground like
 * light through a prism, red to violet as the hours go round a clock,
 * by the way from the stone to the tile (dx, dy). The wheel turns slowly
 * by the clock, so the colours walk round the bearer.
 */
export function rainbowTint(dx: number, dy: number): { light: RGB; glow: RGB } {
  if (dx === 0 && dy === 0) return TINTS.white;
  const turn = typeof performance === "undefined" ? 0 : performance.now() / 12000;
  const hue = ((Math.atan2(dy, dx) / (2 * Math.PI) + turn) % 1 + 1) % 1;
  // A hue as red, green and blue, each channel a triangle round the wheel.
  const at = (h: number, lo: number) => {
    const c = (n: number) => {
      const k = (n + h * 6) % 6;
      return Math.round(255 - (255 - lo) * Math.max(0, Math.min(1, k, 4 - k)));
    };
    return [c(5), c(3), c(1)] as const;
  };
  return { light: at(hue, 70), glow: at(hue, 0) };
}

/** How coloured a colour is: its brightest channel less its dimmest. */
function chroma(c: readonly number[]): number {
  return Math.max(c[0], c[1], c[2]) - Math.min(c[0], c[1], c[2]);
}

/**
 * A blend of lights pushed back out to `want` of colour (see chroma),
 * about the grey it would fade to (2026-10-14: "Make it punchier"). Red
 * and blue averaged come out a dusty mauve, a grey with a little colour
 * left in it, because each takes from the other what makes it red or
 * blue; spread back out to as coloured as the lights it came from, the
 * mauve is a vivid magenta-purple, which is what red and blue light
 * meeting looks like. A blend of one colour is that colour, untouched.
 */
function vivid(c: readonly number[], want: number): RGB {
  const have = chroma(c);
  const mean = (c[0] + c[1] + c[2]) / 3;
  const k = have > 0 ? want / have : 1;
  const out = c.map((v) => Math.round(Math.min(255, Math.max(0, mean + (v - mean) * k))));
  return [out[0], out[1], out[2]];
}

/**
 * Whether (x, y) on level z is in the hollow `space`: on its floor of that
 * level.
 */
export function openFloorHas(space: OpenSpace, z: number, x: number, y: number): boolean {
  return space.floors.some((f) => f.z === z && x >= f.x && y >= f.y && x < f.x + f.w && y < f.y + f.h);
}

/**
 * Whether the light `l` reaches the tile (x, y) on level z, as far as the
 * levels go — the distance is darkAt's and litAt's to measure. A light
 * lights its own floor, as it always has; and in a hollow open across
 * levels, a light standing in it lights the whole hollow, every level of
 * it. Asked for on 2026-10-02: "Light like from the spell should apply to
 * other levels when underground. For example I should be able to see the
 * battlements and central compound of the orc fort from the other
 * locations and vice versa". So a lamp on the walk round the orcs' wall
 * lights the yard under it, and a lamp in the yard the walk over it; a
 * lamp in the tunnel beside the cavern, which is on the walk's level but
 * in the rock, lights the tunnel and nothing of the cavern. It is the rule
 * the server explores by (Map.lightReaches), so the floor drawn lit and
 * the floor sent are the same floor.
 *
 * Above the ground a light is cast down too, onto every level under its
 * own down to the ground (2026-10-21: "light should be cast to lower
 * levels down to ground when standing above ground level"): a torch on a
 * roof or a tower top lights the street round the building under it.
 */
export function lightReaches(l: LightSource, x: number, y: number, z: number, space: OpenSpace | undefined): boolean {
  if (l.z === undefined || l.z === z) return true;
  if (z >= 0 && l.z > z) return true;
  return space !== undefined && openFloorHas(space, l.z, Math.round(l.x), Math.round(l.y)) && openFloorHas(space, z, x, y);
}

/**
 * How dark the point (x, y) is under every light in `lights` at once, for
 * the things drawn there — a name, a health bar, a number, a puff of dust:
 * nought if any light reaches it, one if none does, and nought when there
 * are no lights at all. The floor's own brightness is brightAt's.
 *
 * Asked of a level `z`, in the hollow `space` the viewer stands in, only
 * the lights that reach that level count (lightReaches): in the orcs'
 * cavern a lamp on the walk lights the yard and one in the tunnel does
 * not (2026-10-02: "Light like from the spell should apply to other levels
 * when underground"). Asked of no level, every light counts, as it always
 * did.
 */
export function darkAt(x: number, y: number, lights: readonly LightSource[], z?: number, space?: OpenSpace): number {
  let darkest = -1;
  for (const l of lights) {
    if (!(l.light > 0)) continue;
    if (z !== undefined && !lightReaches(l, x, y, z, space)) continue;
    const d = darknessAt(Math.max(Math.abs(x - l.x), Math.abs(y - l.y)), l.light);
    if (darkest < 0 || d < darkest) darkest = d;
    if (darkest === 0) break;
  }
  // A light in view that reaches nothing here leaves it dark; no lights
  // at all is nothing yet told how far anybody sees, and nothing dark.
  return darkest >= 0 ? darkest : lights.length > 0 ? 1 : 0;
}

/**
 * How bright a tile of level z is drawn, from nought to one: BRIGHT_BASE,
 * less LEVEL_DIM on a level other than the one looked at, `lookZ`, plus
 * LIGHT_LIFT inside the radius of a light that reaches it (litAt) or on a
 * level at or above the ground, plus ALERT_LIFT within the
 * viewer's own alertness on their own level `meZ` while they look at it, and
 * never past the whole. Asked for on 2026-10-13: "50% brightness should be the default everywhere.
 * -25% for areas visible on a different level. +25% within the radius of
 * a light. +25% within the player's own alertness". With no lights at all
 * it is the whole of it, nothing yet having said how far anybody sees.
 *
 * Asked of no level, it is the viewer's own, and looking at no level,
 * they are looking at their own.
 */
export function brightAt(x: number, y: number, lights: readonly LightSource[], z?: number, space?: OpenSpace, meZ?: number, lookZ?: number, sky = 1): number {
  if (lights.length === 0) return 1;
  let b = BRIGHT_BASE;
  // The level looked at is the one that reads, and every level drawn round
  // it is a quarter down from it (2026-10-16: "the currently active level
  // should be brighter than the surrounding visible levels"). A look at a
  // level you are not on, by the floor buttons, draws it as bright as if
  // you stood on it (2026-10-16: "when viewing another level by using the
  // level chip, don't apply the -25% for offlevel. so above ground, viewing
  // another above ground level should be 100% light"); only your alertness
  // stays on your own level.
  const look = lookZ ?? meZ;
  const away = look !== undefined && meZ !== undefined && look !== meZ;
  const aside = z !== undefined && look !== undefined && z !== look;
  if (aside) b -= LEVEL_DIM;
  // Above the ground is always lit (2026-10-13: "Above ground there is no
  // 10 tiles of light, everywhere above ground is always lit").
  const level = z ?? meZ;
  // A light lifts a half (2026-10-14: "What if our earlier light formula
  // was +50% in range of a light capped at 100% brightness. What we want
  // is extra alertness tiles being beyond the light to appear around 75%
  // when underground"), and so does the sky ("Above ground, everything is
  // lit 100%").
  //
  // By night the sky lights nothing and the surface is a cave's (2026-10-21:
  // "Add a day night cycle above ground"), and at dusk and dawn it lifts
  // part of the way: `sky` is how much of its light is left, one by day.
  const skyLift = level !== undefined && level >= 0 ? sky : 0;
  // How much of the tile a light covers rather than whether its middle is
  // in one, so a light carried mid-step lifts the floor round where its
  // bearer is drawn and glides with them: see litCoverAt.
  b += LIGHT_LIFT * Math.min(1, Math.max(skyLift, litCoverAt(x, y, lights, z, space)));
  // Drawn round where the viewer is drawn, gliding with them through a
  // step (2026-10-22: "darkvision light doesn't seem to move smoothly with
  // the player"), though what is seen within it is still alertAt's, from
  // the tile they are walking to: see alertCoverAt.
  if (!away && !aside) b += ALERT_LIFT * alertCoverAt(x, y, lights);
  return Math.min(1, Math.max(0, b));
}

/**
 * Whether (x, y) is within the viewer's alertness of them: the one light
 * that carries an alertness, the viewer's own (see lightsOn), a Chebyshev
 * square from the tile they stand on (ax, ay) — the one they are walking
 * to, from the start of the step — or, given none, from where they are
 * drawn.
 */
export function alertAt(x: number, y: number, lights: readonly LightSource[]): boolean {
  for (const l of lights) {
    if ((l.alert ?? 0) > 0 && Math.max(Math.abs(x - (l.ax ?? l.x)), Math.abs(y - (l.ay ?? l.y))) <= (l.alert ?? 0)) return true;
  }
  return false;
}

/**
 * `f`, a value a tile, at the point (x, y), part way between tiles: the
 * four tiles round it blended by how near it is to each one's middle, as
 * the floor's picture is blended by smoothing (layBlended). On a tile's
 * middle it is that tile's.
 */
export function between(f: (tx: number, ty: number) => number, x: number, y: number): number {
  const x0 = Math.floor(x), y0 = Math.floor(y), fx = x - x0, fy = y - y0;
  const row = (ty: number) => (fx > 0 ? f(x0, ty) * (1 - fx) + f(x0 + 1, ty) * fx : f(x0, ty));
  return fy > 0 ? row(y0) * (1 - fy) + row(y0 + 1) * fy : row(y0);
}

/**
 * How much of the tile (x, y) the viewer's alertness covers, for drawing
 * the floor: the square of it round where the viewer is drawn rather than
 * round the tile they stand on, so the step brighter it is drawn glides
 * with them as their light does (see lightCover). Standing still it is
 * alertAt's square exactly. Mid-step the tiles ahead come up as the viewer
 * comes on, rather than the whole edge ahead the moment the step starts
 * (2026-10-13: "when moving the alertness brightness bonus doesn't
 * immediately appear for the tile being moved to"), which gave way to
 * gliding on 2026-10-22 ("darkvision light doesn't seem to move smoothly
 * with the player"). What is seen there is still alertAt's.
 */
export function alertCoverAt(x: number, y: number, lights: readonly LightSource[]): number {
  let most = 0;
  for (const l of lights) {
    if (!((l.alert ?? 0) > 0)) continue;
    most = Math.max(most, lightCover(l, l.alert ?? 0, x, y));
    if (most >= 1) break;
  }
  return most;
}

/**
 * Whether the point (x, y) stands inside the reach of any light in
 * `lights`: within `light` tiles of its bearer, as a Chebyshev square.
 * What decides whether a creature is drawn at all: a creature is drawn
 * where a light reaches and nowhere else, whatever the floor under it is
 * drawn as. A light that reaches nothing lights nothing, and no lights at
 * all is nothing lit, which is why only the cave scene asks.
 *
 * Asked of a level, it is held to the lights that reach that level, as
 * darkAt is: a creature on the walk round the orcs' yard is seen from the
 * yard in a lamp's light from either level (2026-10-02).
 */
export function litAt(x: number, y: number, lights: readonly LightSource[], z?: number, space?: OpenSpace): boolean {
  for (const l of lights) {
    if (!(l.light > 0)) continue;
    if (z !== undefined && !lightReaches(l, x, y, z, space)) continue;
    if (Math.max(Math.abs(x - l.x), Math.abs(y - l.y)) <= l.light) return true;
  }
  return false;
}

/**
 * How much of the tile (x, y) a light's reach covers, nought to one: the
 * tile a square a side round its middle, and the reach of `r` tiles the
 * square of 2r+1 tiles round where the bearer is drawn. With the bearer
 * standing on a tile it is one inside the reach and nought out of it, as
 * the Chebyshev test is; with them drawn part way through a step, the
 * tiles along the edge of the reach are covered in part, the near edge
 * going as the far one comes, so the light drawn round them glides with
 * them rather than jumping a tile at the half-way point (2026-10-22: "when
 * walking, center the light graphical effects on the creature's position,
 * even though the effective position of the light is different"). What is
 * lit for seeing a thing is still litAt's, and the server's.
 */
export function lightCover(l: { x: number; y: number }, r: number, x: number, y: number): number {
  if (!(r >= 0)) return 0;
  const span = (at: number, from: number) => Math.min(1, Math.max(0, r + 1 - Math.abs(at - from)));
  return span(x, l.x) * span(y, l.y);
}

/**
 * How much of the tile (x, y) is in the reach of the lights in `lights`,
 * for drawing the floor: the most any one of them covers (lightCover),
 * held to the lights that reach the level as litAt is. Where litAt is true
 * of a tile's middle for a bearer standing still, this is one.
 */
export function litCoverAt(x: number, y: number, lights: readonly LightSource[], z?: number, space?: OpenSpace): number {
  let most = 0;
  for (const l of lights) {
    if (!(l.light > 0)) continue;
    if (z !== undefined && !lightReaches(l, x, y, z, space)) continue;
    most = Math.max(most, lightCover(l, l.light, x, y));
    if (most >= 1) break;
  }
  return most;
}

/**
 * The lights of those in view the viewer cannot see, as lightsOn and
 * tintsOn take the creatures they draw lights for: each a creature of no
 * kind standing where the light is, with an id no creature has (ids are
 * positive), so it is never taken for the viewer. Nothing else is drawn
 * of them. See State.lights.
 */
export function bearerless(ls: readonly UnseenLight[]): { c: { id: number; kind: string; z: number; light?: number; tint?: string; tintr?: number }; p: { x: number; y: number } }[] {
  return ls.map((l, i) => ({ c: { id: -1 - i, kind: "", z: l.z ?? 0, light: l.light, tint: l.tint, tintr: l.tintr }, p: { x: l.x, y: l.y } }));
}

/**
 * The coloured lights above the ground this frame, for the tint and
 * nothing else (2026-10-17: "Above ground, coloured light sources should
 * still be discernable"): the sky lights everything up here, so these
 * light nothing and darken nothing, but a torch, the phial or a fire still
 * colours the ground round whoever carries it. The viewer's at the camera,
 * as lightsOn has it, and everybody else's on the surface where they are
 * drawn; the server sends a tint above the ground and no light (tintOf).
 */
export function tintsOn(
  you: { id: number; tint?: string; tintr?: number } | null | undefined,
  cam: { x: number; y: number },
  all: readonly { c: { id: number; z: number; tint?: string; tintr?: number }; p: { x: number; y: number } }[],
): LightSource[] {
  const tints: LightSource[] = [];
  if (you?.tint && (you.tintr ?? 0) > 0) tints.push({ x: cam.x, y: cam.y, light: 0, z: 0, tint: you.tint, tintR: you.tintr });
  for (const { c, p } of all) {
    if (c.id === you?.id || c.z < 0 || !c.tint || !((c.tintr ?? 0) > 0)) continue;
    tints.push({ x: p.x, y: p.y, light: 0, z: 0, tint: c.tint, tintR: c.tintr });
  }
  return tints;
}

/**
 * Every light on the viewer's floor this frame: the viewer, at the
 * position the camera sits on, by `you.light`; and every other player
 * standing on the same level, at the position they are drawn at, by the
 * light the server put on their creature. The viewer is taken from `you`
 * rather than from their own creature in `all`, which carries the same
 * number, so the one place the viewer's light is read stays the one it
 * always was; their creature is skipped so they are not counted twice.
 * Monsters carry no light and a player on another level is a storey away.
 * Built once a frame and handed to everything that asks, never per tile.
 *
 * Except a monster that is a fire (2026-09-28: "Add 2 fire elementals in
 * the room with durin's bane"): fire is its own light, so a fire elemental
 * lights the cave round it like a lamp. The server sends a light on a
 * monster's creature only for such a kind (lightOf), so any creature that
 * carries one counts, and no rule about which kinds shine is kept here.
 *
 * Above ground it is the viewer alone, for their alertness: nobody carries
 * a light up there, the sky lighting it all (2026-10-13: "Remove the
 * surface 10"), and another player's never lifted the wash round them
 * (2026-09-24: "other players above ground should not undim the map due to
 * range from the player"). Under the ground a friend's lamp still lights the
 * cave round them for you, which is what a lamp is.
 *
 * And in a hollow open across levels, every light in it on every level of
 * it, each with its level (2026-10-02: "Light like from the spell should
 * apply to other levels when underground. For example I should be able to
 * see the battlements and central compound of the orc fort from the other
 * locations and vice versa"): standing in the orcs' yard, the lamp of
 * somebody on the walk over you lights the yard round under them, and
 * yours lights the walk over you. `space` is the hollow the viewer stands
 * in, and a light on another level of it counts only standing in it: one
 * in the tunnel beside the cavern is in the rock. What each lights is
 * lightReaches' to say.
 */
export function lightsOn(
  you: { id: number; light?: number; alert?: number; dark?: number; blind?: number; tint?: string; tintr?: number } | null | undefined,
  meZ: number,
  cam: { x: number; y: number },
  all: readonly { c: { id: number; kind: string; z: number; light?: number; tint?: string; tintr?: number }; p: { x: number; y: number } }[],
  space?: OpenSpace,
  tile?: { x: number; y: number },
  night = false,
): LightSource[] {
  const lights: LightSource[] = [];
  // The square drawn a step brighter is your dark sight since 2026-10-19,
  // what you see without a light, which is your alertness but for a great
  // ring's wearer (3 of 5); a server that sends none is an older one.
  // An admin who sees everything has it all as blind sight (you.blind).
  const sight = Math.max(you?.dark ?? you?.alert ?? 0, you?.blind ?? 0);
  if (you && ((you.light ?? 0) > 0 || sight > 0)) lights.push({ x: cam.x, y: cam.y, light: you.light ?? 0, z: meZ, alert: sight, ax: tile?.x, ay: tile?.y, tint: you.tint, tintR: you.tintr });
  // By night the surface is lit as a cave is, by everybody's light on the
  // level (2026-10-21: "Add a day night cycle above ground"), and by every
  // light above the ground, which lights the levels under its own down to
  // the ground (see lightReaches): what each light reaches is asked level
  // by level.
  if (meZ >= 0 && !night) return lights;
  for (const { c, p } of all) {
    const across = space !== undefined && openFloorHas(space, c.z, Math.round(p.x), Math.round(p.y));
    const surface = meZ >= 0 && c.z >= 0;
    if (c.id === you?.id || (c.z !== meZ && !across && !surface) || !((c.light ?? 0) > 0)) continue;
    lights.push({ x: p.x, y: p.y, light: c.light ?? 0, z: c.z, tint: c.tint, tintR: c.tintr });
  }
  return lights;
}

/**
 * All that picking a tile's sprite needs of the world: the ground plane, and
 * any level of it. `Game` satisfies this, which is how the game itself passes
 * itself to variantAt and underfoot below.
 *
 * It is written as an interface rather than as `Game` so that a caller with a
 * world but no session — the map view, which reads a served map out of JSON
 * and never connects — picks its sprites through exactly the same functions
 * the game does. A second copy of them would be a second answer to "which
 * picture is this tile", and the two would drift.
 */
export interface TileSource {
  tileAt(x: number, y: number): number;
  tileOn(z: number, x: number, y: number): number | undefined;
}

/*
 * There was a wash over the nursery here — amber, laid under the world and
 * harder on the minimap — and then there was no wash, and now there is no
 * nursery either (asked for on 2026-09-21: "Remove the noob/nursery area
 * and rearrange the map with the castle in the middle, and progressively
 * stronger creatures as you travel outwards. Each of the special areas
 * like plains of havoc, orc camp, and dwarf hold are distributed evenly
 * and radially around the map."). The east country over the third
 * drawbridge is ordinary wilderness, the same as the country over the
 * other two, and there is nothing about it for a tint to say.
 *
 * The argument the wash lost is worth keeping, because it is the argument
 * anybody will make for the next one. Where a rule about walking applies is
 * a fact about the world and a player wants to see it. What answered it was
 * that the world said it already: the country was cut off by water on every
 * side, there was one drawbridge, and a bridge is the plainest line the
 * world has — a boundary drawn in terrain rather than in tint, and terrain
 * reads the same at every zoom, in every palette, and to somebody who is not
 * looking for it. The wash on top of that was a third of the map wearing a
 * colour that means "a rule lives here", over grass, wood, water and rock
 * alike.
 *
 * The protection zone's blue is a different case and stays: the castle's
 * line runs through a room, across a floor that looks the same on both
 * sides of it, and there is nothing in the world to see.
 */

/**
 * A health bar over a head is drawn as heavily as its owner is tough, so a
 * rat and a demon do not wear the same bar: what is in front of you says
 * how much of it there is before you have hit it once. The scale is the
 * square root of the number, because the numbers themselves run from ten
 * to seven hundred and a bar proportional to that is either a smudge at
 * one end or a banner at the other. That much is what it always was. What
 * is new is where the bar puts the curve once it has run out of room.
 *
 * A creature stands on a tile thirty-two pixels across and the curve
 * wanted seventy-one of them for a demon — a bar more than twice as wide
 * as the thing wearing it, lying across its neighbours' heads and saying,
 * of two demons side by side, nothing about which bar was whose. So the
 * length stops four pixels inside the tile, two a side. Two is enough that
 * the bar visibly falls short of the edge and that two of them standing
 * next to each other have four pixels of ground between them, and it is
 * little enough that the cap lands above the bear: everything up to a
 * hundred and ten hitpoints wears exactly the bar it always wore, which is
 * every creature through to the bear and a character through to level
 * seven.
 *
 * Past the cap the curve is still climbing, and what it buys there is
 * height. The rule is that the bar keeps the area the square root asked
 * for — what it cannot have in length it takes in thickness — so a demon's
 * bar is a rat's length and two and a half times its weight. That is one
 * curve driving both dimensions rather than a second scale invented for
 * the top of the range, and because a pixel of height is worth seven of
 * length it arrives as steps rather than as a jump: four up to a bear,
 * five at a cyclops, six at a level-twenty player, eight at a dragon, ten
 * at a demon.
 *
 * It thickens downwards, out of the line it hangs from under its own
 * name, rather than growing both ways: growing both ways would lift the
 * top edge half a pixel for every pixel of weight, and the shared edge the
 * eye lines a bar up along under its own nameplate is the whole of what
 * makes two of them comparable.
 *
 * Where the weight now goes is up. A bar used to thicken down into the gap
 * between the name and the head, which worked while every name in the
 * world sat at one fixed height above its tile; nameplateY places the
 * bar's *bottom* edge over the creature's actual head instead, so the
 * thickness pushes the name up and away rather than reaching down into the
 * face. A row of creatures is therefore read by how far the names are
 * lifted off them, not by how far the bars come down.
 */
const HP_BAR_MIN = 12;
const HP_BAR_MAX = TILE - 4;
const HP_BAR_H = 4;
export const hpBarShape = (maxhp: number): { w: number; h: number } => {
  // What the curve asks for, floored so that the weakest thing in the
  // world still has a bar wide enough to read a colour off.
  const want = Math.max(HP_BAR_MIN, 2.7 * Math.sqrt(Math.max(1, maxhp)));
  // Below the cap the ratio is one, so the ordinary creature's bar is the
  // height it always was without a branch here saying so: the flat bar
  // falls out of the same arithmetic as the thick one.
  return { w: Math.round(Math.min(HP_BAR_MAX, want)), h: Math.round(HP_BAR_H * Math.max(1, want / HP_BAR_MAX)) };
};

/**
 * How much clear air is left between the bottom of a creature's health bar
 * and the topmost pixel of the creature under it, and how far under the
 * name the bar hangs. The gap is none: the bar sits right on the head
 * (2026-10-20: "show health bar as close to the top of a creatures head as
 * possible"; it was three pixels). The bar's own black border keeps it
 * apart from the head it rests on. Three under the name.
 */
const HEAD_GAP = 0;
const BAR_DROP = 3;

/**
 * Where a creature's name sits: `HEAD_GAP` of clear air above the topmost
 * pixel that creature actually paints, with its own bar's thickness and
 * the drop under the name counted back off that.
 *
 * It used to be Y - 18 — a fixed distance above the tile, the same for
 * everything in the world — and everything in the world is not the same
 * size. Bottom-aligned sprites put a rat's nose 15 pixels *below* the top
 * of its tile and a demon's horns 31 above it, so one offset from the tile
 * meant a 26-pixel hole over a rat, a 26-pixel overlap into a demon's
 * face, and every value between the two for the kinds in the middle: 16
 * over a wolf, 9 over a kobold, 20 inside a cyclops. The label was being
 * placed relative to the one thing it is not about.
 *
 * What it is measured off now is the sprite, through inkTop, which is the
 * only honest source: a sprite's canvas is mostly margin (see the note on
 * inkTop), so its height says nothing about where the head is, and a
 * per-outfit table of head heights is a second copy of a fact the drawing
 * already states.
 *
 * The anchored edge is the *bottom* of the bar, not the name. That is a
 * change to how a thick bar reads and it is the right way round: a bar
 * grows downwards out of its nameplate, so anchoring the name would let a
 * demon's ten-pixel bar eat its own clearance and land back in the horns,
 * which is where we started. Anchored at the bottom, every bar in the
 * world rests on the head of the creature it belongs to (HEAD_GAP) and
 * weight pushes the name up instead. What is lost is that bars no longer
 * share one line across a row of different creatures — but they could not:
 * the moment a label follows its own creature's head, two creatures of
 * different heights have labels at different heights. Two of a kind still
 * line up exactly.
 *
 * There is deliberately no clamp on how high this may go. A tall
 * creature's label now reaches further north than it did — a demon's name
 * lands about where the nameplate of a player standing on the tile due
 * north would be, overlapping it by a few pixels, which it never did
 * before. The alternative is worse in every case that matters: any clamp
 * tight enough to prevent that collision puts the label back down over the
 * demon's own horns, which is the bug. And the space the label reaches
 * into is space the demon's own body is already filling, so it still reads
 * as the demon's. Anything better than this is a layout pass over every
 * nameplate on screen, which is a different piece of work.
 */
function nameplateY(Y: number, spr: HTMLCanvasElement, maxhp: number): number {
  // The head is measured under the bar only (see inkTopUnder), so a weapon
  // or a wing reaching higher beside the head does not lift the bar off it.
  const { w, h } = hpBarShape(maxhp);
  const head = Y + TILE - spr.height + inkTopUnder(spr, w);
  return head - HEAD_GAP - h - BAR_DROP;
}

/** In-world labels use the page's UI font so the canvas and the panels read alike. */
const UI_LABEL_FONT = '"Segoe UI", system-ui, -apple-system, sans-serif';
let LABEL_FONT = UI_LABEL_FONT;
/**
 * The font the names and words over the world are drawn in: the page's UI
 * font, or Departure Mono while the ⋯ menu's pixel font is on (2026-10-01:
 * "Add a setting in the menu that uses departure mono as the font"), so the
 * canvas reads like the panels either way. null is the UI font.
 */
export function setLabelFont(family: string | null) {
  LABEL_FONT = family ?? UI_LABEL_FONT;
  labelPixel = family !== null;
}
/** Whether the labels are in the pixel font, which wants its sizes snapped. */
let labelPixel = false;
/**
 * The canvas font for a label of a size: the UI font's semibold as it
 * always was, or the pixel font at the nearest multiple of eleven pixels,
 * the grid it is drawn on, and in its one weight, since a faked bold of a
 * pixel font is a smear (2026-10-01: "Snap to 11px where it makes sense").
 */
function labelFont(size: number): string {
  return labelPixel ? `${labelPx(size)}px ${LABEL_FONT}` : `600 ${size}px ${LABEL_FONT}`;
}
/** A label's size in pixels as it will be drawn: snapped while the pixel font is on. */
function labelPx(size: number): number {
  return labelPixel ? Math.max(11, Math.round(size / 11) * 11) : size;
}
/**
 * A label in world coordinates on any context: the renderer's own, or the
 * layer a name is laid on to go behind somebody (see nameBehind). The font
 * size is in world pixels and the glyphs are rasterised through the
 * context's transform, so they gain detail as the view is scaled up; a
 * round-joined outline keeps them legible on any ground.
 */
function drawLabel(
  ctx: CanvasRenderingContext2D,
  s: string,
  x: number,
  y: number,
  size: number,
  color: string,
  align: CanvasTextAlign = "left",
  family = LABEL_FONT,
) {
  if (family === LABEL_FONT) size = labelPx(size);
  ctx.font = family === LABEL_FONT ? labelFont(size) : `600 ${size}px ${family}`;
  ctx.textAlign = align;
  ctx.textBaseline = "alphabetic";
  ctx.lineJoin = "round";
  ctx.miterLimit = 2;
  ctx.lineWidth = size / 5;
  ctx.strokeStyle = "rgba(0,0,0,0.85)";
  ctx.strokeText(s, x, y);
  ctx.fillStyle = color;
  ctx.fillText(s, x, y);
}
const COUNT_FONT = "ui-monospace, Consolas, monospace";

/**
 * The room a speech bubble is given: five tiles across and two high,
 * directly above the speaker (five since 2026-10-07: "fit chat messages in
 * the main window in the 5 horizontal tiles above the speakers head, not
 * just the current 3"). Whatever is said is fitted into it — wrapped
 * to the width, and shrunk until it fits the height — so one player cannot
 * paper over the map with a long sentence, and a short one still reads
 * large. Only something too long for even the smallest size overruns, and
 * it overruns upwards.
 */
const SPEECH_TILES_W = 5;
const SPEECH_TILES_H = 2;
const SPEECH_MAX = 11;
const SPEECH_MIN = 5;
/**
 * How far off the edge of the viewport a reported line is set in, and the
 * smallest type it is ever set in. Half is what a line said off screen is
 * drawn at, so it reads as a thing happening elsewhere at a glance rather
 * than as something beside you; the floor is there because half of the
 * smallest size words are ever set in would not be small type, it would be
 * a smudge. Four is the point at which the stroke behind the letters still
 * has a letter to sit behind.
 */
const SPEECH_OFFSCREEN_SCALE = 0.5;
const SPEECH_EDGE_MIN = 4;
/**
 * How far a bubble's bottom line is kept clear of the nameplate under it.
 * Ten is the name's own type size and two is air, which is the whole of
 * the arithmetic: a name is drawn from its baseline upwards, so a line
 * resting exactly on that baseline has the name's capitals through it.
 *
 * It matters now in a way it did not before. A bubble used to sit a fixed
 * 28 pixels over its tile, which cleared the nameplate by about four —
 * and four is less than the height of the letters, so every long line has
 * always had its last row printed through the top of the name. Nobody
 * noticed, because speech is short and the row that collides is usually
 * the only row. An answer is not short: a look at a player in full kit is
 * six lines, and the sixth lands on the name every time.
 */
const SPEECH_NAME_CLEAR = 12;
/** How far in from the viewport's edge a reported line is pinned. */
const SPEECH_EDGE_INSET = 6;
/**
 * How far off, in tiles, somebody can be and still have what they say —
 * words or a spell's words — pinned to the edge of your screen (asked for
 * on 2026-09-27: "just limit how far they can be and still have the spell
 * words show pinned to the edge. if player speech isn't already limited,
 * introduce a common limit for them of 15 tiles"). Since 2026-10-08 the
 * server sends nothing said from further off (speechRange in world.go), so
 * this is the same line drawn twice. Counted the way
 * every distance in the world is, the larger of the two.
 */
export const SPEECH_PIN_RANGE = 15;

/**
 * How far off a yell is still pinned to the edge of the view: twice a
 * say's reach (2026-10-08: "add the ability to yell ... it puts the say in
 * all caps, and can be heard by players 30 tiles away"); the server's
 * yellRange.
 */
export const YELL_PIN_RANGE = 30;

/** Interpolated tile-space position of a creature at time now. */
export function creaturePos(c: ClientCreature, now: number): { x: number; y: number; moving: boolean } {
  if (c.moveDur <= 0) return { x: c.x, y: c.y, moving: false };
  // Never before the step's start: a step is only begun once its moment has
  // come (see Game.advance), but the frame's clock and the one a message
  // was read at are not the same reading.
  const t = Math.max(0, Math.min(1, (now - c.moveStart) / c.moveDur));
  return { x: c.fromX + (c.x - c.fromX) * t, y: c.fromY + (c.y - c.fromY) * t, moving: t < 1 };
}

/**
 * The picture a creature is being drawn as at this instant: its outfit and
 * gear in its current facing, on the frame of the walk cycle it has got to.
 * Legs swing at 150ms a step, and only while it is actually between tiles.
 *
 * This exists as one function because names and health bars are drawn in a
 * pass of their own, after every sprite, so that nothing standing further
 * south paints over them — and now that a nameplate's height is measured
 * off the sprite, that second pass needs the same picture the first one
 * drew. Two copies of this argument list, one in each pass, is a nameplate
 * that quietly measures the wrong frame the day somebody adds something to
 * the sprite's key and only remembers one of them. Asking creatureSprite
 * again costs a map lookup: the sprites are cached, and the second pass
 * asks for one that was built moments earlier in the first.
 */
function creatureSpriteNow(c: ClientCreature, moving: boolean, now: number): HTMLCanvasElement {
  // A fire does not stand still, so what flickers turns its frames over
  // standing as well as walking (2026-09-28: "Add 2 fire elementals in the
  // room with durin's bane"). See FLICKERS.
  const walkFrame = moving
    ? 1 + (Math.floor(now / 150) & 1)
    : FLICKERS.has(c.outfit) ? Math.floor(now / FLICKER_MS) % 3 : 0;
  // What blazes burns on a clock of its own besides (see BLAZES).
  const frame = walkFrame + (BLAZES.has(c.outfit) ? 3 * (Math.floor(now / FLICKER_MS) % 3) : 0);
  // A rider's horse walks; the rider sits still on it (see riderSprite).
  const spr = c.riding
    ? riderSprite(c.outfit, c.lastDir, frame, c, c.color, c.level, c.locked, c.admin, c.race, c.hair, c.steed)
    : creatureSprite(c.outfit, c.lastDir, frame, c, c.color, c.level, c.locked, c.admin, c.race, c.hair);
  const faded = seeThrough(spr, c.fade);
  return c.invisible ? ghostly(faded) : faded;
}

/**
 * How much of an admin gone /invisible is drawn: the figure, its name and
 * its bar at this much of their opacity, the figure in greys as well.
 * Asked for on 2026-10-07: "when a player/admin is invisible, show them
 * greyed out". Only the admin themself and the other admins are ever sent
 * one (see ClientCreature.invisible), so this is how they tell at a glance
 * that nobody else can see them: present, but not quite there. Enough is
 * left to make out the outfit, the gear and which way they face.
 */
export const GHOST_ALPHA = 0.5;

/**
 * The poison's green: the colour a beat of it lands in (the server's
 * poisonColor) and the colour of what is left of a dose on the health
 * bar, so the stretch at the end of the bar and the numbers coming off it
 * are plainly the one thing.
 */
export const POISON_GREEN = "#7ed957";

const ghostCache = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>();

/**
 * spr greyed out, for an admin gone /invisible: every pixel taken to its
 * brightness, lifted a little towards a pale grey so a dark tunic does not
 * go to a black hole, and made GHOST_ALPHA see-through. Baked into a copy
 * of the sprite and kept with it, as dimmed is, so every place that draws
 * the figure — the scene, a glimpse up a passage, the outline behind a
 * wall, the nameplate measuring its head — draws the same grey one without
 * being told. A pixel with anything in it keeps something, so the figure's
 * edges, and where its head is (inkTop), stay where they were.
 */
export function ghostly(spr: HTMLCanvasElement): HTMLCanvasElement {
  const known = ghostCache.get(spr);
  if (known) return known;
  const out = document.createElement("canvas");
  out.width = spr.width;
  out.height = spr.height;
  const ctx = keptContext(out);
  ctx.drawImage(spr, 0, 0);
  if (out.width > 0 && out.height > 0) {
    const img = ctx.getImageData(0, 0, out.width, out.height);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      const a = d[i + 3];
      if (a === 0) continue;
      const v = Math.round((0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) * 0.7 + 190 * 0.3);
      d[i] = d[i + 1] = v;
      d[i + 2] = Math.min(255, v + 8);
      d[i + 3] = Math.max(1, Math.round(a * GHOST_ALPHA));
    }
    ctx.putImageData(img, 0, 0);
  }
  ghostCache.set(spr, out);
  return out;
}

const fadeCache = new WeakMap<HTMLCanvasElement, Map<number, HTMLCanvasElement>>();

/**
 * spr as see-through as a player sent with `fade` parts in a hundred is:
 * every pixel at 1 - fade/100 of its opacity (2026-10-13: "use opacity to
 * match a players fading"). The server counts a great ring on the finger
 * as 30 of it and seven tenths of the ring's fading on top. Baked into a copy
 * kept with the sprite, one for each level of fading, as ghostly is, so
 * every place that draws the figure draws the faded one. Nothing faded, or
 * no fade at all, is spr itself. The name and the bar are not faded: they
 * are read, not looked at.
 */
export function seeThrough(spr: HTMLCanvasElement, fade: number | undefined): HTMLCanvasElement {
  if (!fade || fade <= 0) return spr;
  let byFade = fadeCache.get(spr);
  if (!byFade) {
    byFade = new Map();
    fadeCache.set(spr, byFade);
  }
  const known = byFade.get(fade);
  if (known) return known;
  const out = document.createElement("canvas");
  out.width = spr.width;
  out.height = spr.height;
  const ctx = keptContext(out);
  ctx.globalAlpha = Math.max(0, 1 - fade / 100);
  ctx.drawImage(spr, 0, 0);
  byFade.set(fade, out);
  unfadedOf.set(out, spr);
  return out;
}

const unfadedOf = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>();

/**
 * The sprite seeThrough faded spr from, or spr itself if it is not one:
 * what a figure is outlined from (see ghostOf), so the outline of a
 * faded player is as plain as anybody's however little of the figure is
 * left to see.
 */
export function unfaded(spr: HTMLCanvasElement): HTMLCanvasElement {
  return unfadedOf.get(spr) ?? spr;
}

/** A "#rgb" colour as the grey of its brightness, for a ghost's bar. */
function greyed(hex: string): string {
  const [r, g, b] = [1, 2, 3].map((i) => parseInt(hex[i], 16) * 17);
  const v = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
  return `rgb(${v},${v},${Math.min(255, v + 8)})`;
}

/** The colour an admin gone /invisible is named in: see GHOST_ALPHA. */
const GHOST_NAME = "#c8ccd2";

/**
 * Which sprite variant a tile is drawn at. Most tiles take a sprinkle of
 * variety from where they lie, so that a field of grass is not a grid of
 * one picture. A drawbridge takes something else: three tiles of it side
 * by side have to read as one span, so the variant says which part of the
 * span this tile is, worked out from whether its neighbours are bridge
 * too. Only the outer two then draw an end, and the planks and kerbs run
 * off the tile edges to meet next door.
 */
export function variantAt(g: TileSource, t: Tile, x: number, y: number): number {
  // A ramp's variant is which way it climbs, so it is drawn that way round
  // (2026-09-27: "fix stairs so they render properly when a west/east
  // ramp"). See rampDir.
  if (isRampTile(t)) return rampDir(g, t, x, y);
  // Rock is a cut of one picture of the mountain, the one where it lies,
  // so a range runs on without a seam (see rockSheet).
  if (t === Tile.Rock) return rockCell(x, y);
  if (t !== Tile.Bridge) return (x * 7 + y * 13) & 3;
  const w = g.tileAt(x - 1, y) === Tile.Bridge;
  const e = g.tileAt(x + 1, y) === Tile.Bridge;
  if (w || e) return w && e ? SPAN_MID : w ? SPAN_EAST : SPAN_WEST;
  // Nothing either side: either a span laid the other way — the east
  // gate's bridge, which crosses a moat running north and south — or a
  // single tile of timber standing on its own.
  const n = g.tileAt(x, y - 1) === Tile.Bridge;
  const s = g.tileAt(x, y + 1) === Tile.Bridge;
  if (!n && !s) return SPAN_ONE;
  return SPAN_TURNED + (n && s ? SPAN_MID : n ? SPAN_EAST : SPAN_WEST);
}

/**
 * What a ladder is standing on. The tile itself cannot say — a ladder tile
 * replaces the ground it was cut into — so it is read off the neighbours:
 * the commonest floor around it, which is boards inside a tower, cave
 * earth underground, dirt in a yard. Everything that is not floor (walls,
 * water, other passages) is left out of the count, and a ladder with no
 * floor at all beside it falls back to cave earth below ground and dirt
 * above, which is where the two kinds of ladder in the world actually are.
 */
export function underfoot(g: TileSource, z: number, x: number, y: number): Tile {
  const counts = new Map<number, number>();
  let best = z < 0 ? Tile.Cave : Tile.Dirt, most = 0;
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (!dx && !dy) continue;
      const t = g.tileOn(z, x + dx, y + dy);
      if (t === undefined || !FLOOR_TILES.has(t)) continue;
      const n = (counts.get(t) ?? 0) + 1;
      counts.set(t, n);
      if (n > most) [best, most] = [t as Tile, n];
    }
  }
  return best;
}

/** Tiles that are floor: something a ladder could be standing on. */
const FLOOR_TILES = new Set<number>([
  Tile.Grass, Tile.Dirt, Tile.Sand, Tile.Stone, Tile.Flowers, Tile.Cobble,
  Tile.Temple, Tile.Swamp, Tile.Roof, Tile.Depot, Tile.Cave, Tile.Bridge,
  Tile.Hilltop, Tile.Rampart, Tile.Tower, Tile.Snow, Tile.Thatch,
]);

/**
 * Tile to screen, in world pixels, for the frame being drawn. It is a
 * name rather than the shape written out at each of the passes that take
 * it, and the name earns its place twice: the passes read as what they
 * do, and a method signature carries no braces of its own, which is what
 * lets a reader — and the tests, which count braces from the first one —
 * find where a method's body actually starts.
 *
 * The third argument is the level, or any height in storeys, that the
 * point stands at, since the oblique view (2026-09-27, "implement the
 * oblique view with south and east faces visible"): every storey above the
 * viewer's own floor is drawn STOREY pixels up and to the left, and every
 * storey below it as far down and to the right. Left out, it is the
 * viewer's own floor, where nothing moves at all — so everything that
 * only ever happens where the viewer stands, their own ring, the names over the heads beside them, reads exactly as it did.
 */
type ToScreen = (tx: number, ty: number, z?: number) => { sx: number; sy: number };

/** A sprite just drawn, and where its top-left corner went. */
type Placed = { spr: HTMLCanvasElement; X: number; Y: number };

/**
 * Which levels the close view draws for a viewer, lowest first, and what
 * stands on each of them as it is drawn. Under the ground it is the
 * viewer's own floor and nothing else: the surface is a storey of rock
 * away. On the ground or above it, the ground and every level over it.
 *
 * A roof lifted so a viewer on the ground can see into a building is not
 * there at all, as far as this says: it is not drawn, and a click on the
 * room under it lands in the room. The draw and the click ask this one
 * function, so they cannot disagree about what is standing where.
 *
 * Under the ground in a hollow open across levels it is every level of the
 * hollow, as the surface's are every level under the sky (2026-10-02:
 * "Light like from the spell should apply to other levels when
 * underground. For example I should be able to see the battlements and
 * central compound of the orc fort from the other locations and vice
 * versa"). Standing in the orcs' yard you are drawn the walk over it, and
 * on the walk the yard under it — the other level only where it is in the
 * hollow, so the yard is not drawn the tunnel beside the cavern, which is
 * on the walk's level but in the rock. Your own floor is drawn whole, as
 * it always was. `space` is the hollow, for the lights to be built over.
 *
 * `eyeZ` is the floor it is all seen from: the viewer's own, or the one
 * they are looking at with the floor buttons, which since 2026-10-13 is
 * drawn as if they stood on it where they are ("when viewing different
 * levels, they should be rendered the same way as regular levels, the
 * oblique applies everywhere"). The roofs lifted and the hollow are that
 * floor's, at the viewer's own x and y.
 */
function terrainOf(
  g: Game,
  me: ClientCreature | null | undefined,
  eyeZ = me?.z ?? 0,
  now = performance.now(),
): { levels: number[]; shown: Shown; lifted: (z: number, x: number, y: number) => boolean; space: OpenSpace | undefined; sig: string } {
  const hidden = me && eyeZ >= 0 ? g.hiddenRoofs(me.x, me.y, eyeZ) : new Set<number>();
  // Whether (x, y) on level z is lifted off for the viewer: on a lifted
  // roof, or over one, since what stands on a storey lifted to see into
  // the room under it goes with it. The Prancing Pony's thatch is over its
  // room upstairs, which is over its ground floor (2026-10-08), so from
  // its ground floor both go.
  const space = me && eyeZ < 0 ? g.openSpaceAt(me.x, me.y, eyeZ) : undefined;
  // Under the ground there are no roofs, but in a hollow the level over
  // the viewer can stand over their head all the same: the walk over the
  // orcs' gateway, a tower's floor over the room under it. That is taken
  // off too, and what stands on it with it (see overheadBelow).
  const overhead = me && space ? g.overheadBelow(me.x, me.y, eyeZ) : undefined;
  const levels = eyeZ < 0 ? (space ? [...new Set(space.floors.map((f) => f.z))].sort((a, b) => a - b) : [eyeZ]) : [0, ...[...g.levels.keys()].filter((lz) => lz > 0).sort((a, b) => a - b)];
  // And whatever of any level over the viewer is drawn over them or over a
  // tile they could step to, with all of it that joins on (see
  // levelsInTheWay).
  const inTheWay =
    me && eyeZ === (me.z ?? 0)
      ? levelsInTheWay(g, creaturePos(me, now), me.x, me.y, eyeZ, levels, (z, x, y) => !space || openFloorHas(space, z, x, y))
      : undefined;
  const lifted = (z: number, x: number, y: number): boolean => {
    if (overhead && z > eyeZ && overhead(x, y)) return true;
    if (inTheWay && inTheWay.size > 0) for (let l = eyeZ + 1; l <= z; l++) if (inTheWay.has(`${l},${x},${y}`)) return true;
    if (hidden.size === 0) return false;
    for (let l = eyeZ + 1; l <= z; l++) if (hidden.has(g.roofAt(x, y, l) ?? -1)) return true;
    return false;
  };
  const drawn = new Set(levels);
  const shown: Shown = (z, x, y) => {
    if (!drawn.has(z)) return undefined;
    if (lifted(z, x, y)) return undefined;
    if (space && z !== eyeZ && !openFloorHas(space, z, x, y)) return undefined;
    return g.tileOn(z, x, y);
  };
  // Everything shown answers from besides the map itself, as a string
  // that is the same exactly when shown would answer the same: what is
  // worked out from shown can be kept while it is (see TileMemo).
  const sig = [
    eyeZ,
    levels.join(","),
    [...hidden].join(","),
    space ? g.open.indexOf(space) : "",
    overhead && me ? `${me.x},${me.y}` : "",
    inTheWay ? [...inTheWay].join(";") : "",
  ].join("|");
  return { levels, shown, lifted, space, sig };
}

/**
 * What the scene works out about each tile from the map and what of it is
 * shown — which piece of a wall stands there, which floor lies beside it,
 * whether a floor hangs over nothing — kept from frame to frame while
 * none of that changes (2026-10-31, from "Implement the other
 * improvements", phone lag): a town asked it of every tile in view every
 * frame, the same questions with the same answers. `begin` is given a
 * string that changes whenever an answer could, and forgets everything
 * when it does.
 */
/** A number for a tile of a level, for keying maps asked of every tile. */
function tileKey(x: number, y: number, z: number): number {
  return ((z + 64) * 8192 + (y + 64)) * 8192 + (x + 64);
}

class TileMemo {
  private sig = "";
  private maps: Map<number, unknown>[] = [];
  begin(sig: string): this {
    if (sig !== this.sig) {
      this.sig = sig;
      for (const m of this.maps) m?.clear(); // a hole where a table was never asked for
    }
    return this;
  }
  /** The answers kept as `which`, by tileKey. */
  table<T>(which: number): Map<number, T> {
    return (this.maps[which] ??= new Map()) as Map<number, T>;
  }
  /** The answer kept as `which` for (x, y, z), worked out by `f` the first time. */
  of<T>(which: number, x: number, y: number, z: number, f: () => T): T {
    const m = this.table<T>(which);
    const k = tileKey(x, y, z);
    if (m.has(k)) return m.get(k)!;
    const v = f();
    m.set(k, v);
    return v;
  }
}

/**
 * What of the levels over a viewer is in the way, as level,x,y keys: not
 * drawn, so the viewer is never hidden under a level and never has one
 * drawn over where they could walk next. One rule for every level, roof,
 * hill, mountain or the walk round a hollow, asked for on 2026-10-22:
 * "if there's a tile you can move to from your current position that's
 * covered by another level, remove that level", then "it should be a
 * general rule for anytime a level above would occlude you but not
 * drawing it would fix that", and of hills and mountains, "no exception".
 * Before it, roofs over the figure went (2026-10-19: "when walking under
 * an overhang like this, upper levels should not be drawn until the
 * player is properly visible") and a hilltop drawn over somebody at the
 * foot of the hill stayed.
 *
 * A level l over the viewer at (x, y) on level z is drawn half a tile up
 * and west for every storey it stands over them. It is in the way where a
 * tile of it covers some of the viewer's figure — its tile and the half
 * tile over it a figure stands up into (CREATURE_H), `at` being where the
 * figure is drawn this instant, mid-step or not; a tile's top is half a
 * tile higher again if it is raised, and its faces run down to its foot
 * — or where its floor is drawn over the whole of a tile the viewer could
 * step to, their own or one beside it: the one tile there, a whole number
 * of tiles up and west, or all four it straddles half way. The tops of a
 * level's walls do not count for that, or the museum's storey under the
 * Mathom-house's slate, whose west wall's top meets the slate's edge,
 * would go with the slate.
 *
 * What is in the way goes with everything of its level that joins on to
 * it, side by side, out to WAY_REACH tiles, which is past the edge of the
 * view: the whole roof, the whole walk round the orcs' yard, the whole
 * hill. `has` says whether a level is drawn at a place at all (in a
 * hollow, only its own rectangle of each level is).
 */
export function levelsInTheWay(
  g: Pick<Game, "tileOn">,
  at: { x: number; y: number },
  x: number,
  y: number,
  z: number,
  levels: number[],
  has: (z: number, x: number, y: number) => boolean = () => true,
): Set<string> {
  const out = new Set<string>();
  const L = at.x, R = at.x + 1, B = at.y + 1, T = B - CREATURE_H / TILE;
  const E = 1e-6;
  for (const l of levels) {
    if (l <= z) continue;
    const there = (tx: number, ty: number) => has(l, tx, ty) && g.tileOn(l, tx, ty) !== undefined;
    const seeds: [number, number][] = [];
    // Over the figure.
    for (let ty = Math.floor(T) - 1; ty <= Math.ceil(B) + l - z; ty++) {
      for (let tx = Math.floor(L) - 1; tx <= Math.ceil(R) + l - z; tx++) {
        if (!there(tx, ty)) continue;
        const foot = (l - z) / 2, top = foot + (isRaised(g.tileOn(l, tx, ty)!) ? 0.5 : 0);
        if (tx - top >= R - E || tx + 1 - foot <= L + E || ty - top >= B - E || ty + 1 - foot <= T + E) continue;
        seeds.push([tx, ty]);
      }
    }
    // Over a tile to step to.
    const s = (l - z) / 2;
    const offs = [...new Set([Math.floor(s), Math.ceil(s)])];
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const nx = x + dx, ny = y + dy;
        if (!walkable(g.tileOn(z, nx, ny))) continue;
        const over: [number, number][] = [];
        for (const oy of offs) for (const ox of offs) over.push([nx + ox, ny + oy]);
        if (over.every(([tx, ty]) => there(tx, ty))) seeds.push(...over);
      }
    }
    // And all that joins on.
    while (seeds.length > 0) {
      const [tx, ty] = seeds.pop()!;
      const k = `${l},${tx},${ty}`;
      if (out.has(k) || Math.abs(tx - x) > WAY_REACH || Math.abs(ty - y) > WAY_REACH || !there(tx, ty)) continue;
      out.add(k);
      seeds.push([tx - 1, ty], [tx + 1, ty], [tx, ty - 1], [tx, ty + 1]);
    }
  }
  return out;
}

/**
 * How far from the viewer, in tiles either way, levelsInTheWay follows a
 * level that is in the way: past the edge of any view, so what is taken
 * off is never seen to stop, and no further, so a mountain range is not
 * walked from end to end every frame.
 */
const WAY_REACH = 24;

/** The red, green and blue of a colour written #rgb or #rrggbb. */
export function hexRGB(hex: string): number[] {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** The column of the map nearest x: x itself on the map, its edge's off it. */
function edgeX(g: { mapW: number }, x: number): number {
  return Math.max(0, Math.min(g.mapW - 1, x));
}

/** The row of the map nearest y: y itself on the map, its edge's off it. */
function edgeY(g: { mapH: number }, y: number): number {
  return Math.max(0, Math.min(g.mapH - 1, y));
}

/**
 * The ground the close view lays at (x, y), on the map or past its edge:
 * the nearest tile of the map, but desert past the east edge (2026-10-17:
 * "to the east, the terrain should be desert"), where the world runs on
 * into the dry lands beyond Rhûn and Khand. Past an edge never seen it is
 * UNSEEN, so nobody is shown what lies beyond ground they have not been to.
 */
function groundPast(g: { mapW: number; mapH: number; tileAt(x: number, y: number): number }, x: number, y: number): number {
  const t = g.tileAt(edgeX(g, x), edgeY(g, y));
  return x >= g.mapW && t !== UNSEEN ? Tile.Sand : t;
}

/** What TileMemo keeps, one table each. */
const MEMO_OVERHANG = 0,
  MEMO_FLOOR = 1,
  MEMO_SLABBED = 2,
  MEMO_REGIONS = 3,
  MEMO_PIECE = 4,
  MEMO_RAMP = 5;

/** Tiles of ground kept past the window each way: see layGroundKept. */
const GROUND_KEPT_PAST = 4;

/** The most backing pixels to a CSS pixel the world is drawn at: see resize. */
export const MAX_DPR = 2;

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  /**
   * Which of ZOOMS the world is drawn at. Read by the draw, by the
   * letterboxing and by every click, so all three agree about how much
   * world is on the glass.
   */
  private zoom = ZOOM_DEFAULT;
  camX = 0; // camera centre in tile units (fractional)
  camY = 0;
  private dpr = 1;
  private scale = 1; // world pixels -> device pixels
  private offX = 0; // letterbox offset of the viewport within the canvas, in device pixels
  private offY = 0;
  /**
   * Where the last frame found doors, for a click to see the same wall
   * over a doorway that the frame drew: see pickAt and paintedBy.
   */
  private doorsSeen: DoorAt | undefined;
  /**
   * The flat scene's ground, a pixel a tile and one canvas a floor, painted
   * the first time a floor is looked at from far out and kept: see
   * flatFloor. flatFloorsOf is the ground they were painted from, so a
   * new world from a new welcome throws them away. Tiles seen since are
   * added to them a pixel at a time as they come (Game.takeRevealed).
   */
  private flatFloors = new Map<number, HTMLCanvasElement>();
  private flatFloorsOf: Uint8Array | null = null;
  /**
   * A tile size off the zoom ladder, for the map view, which zooms smoothly
   * and draws the world through this renderer (2026-10-27: "the map viewer
   * should render the same way as the main client"). Null in the game,
   * where the ladder is the whole of it.
   */
  freePx: number | null = null;
  /**
   * Whether the backing store is held to MAX_DPR (see resize), or drawn
   * at the screen's own pixel ratio, however high. Switched from the menu
   * (2026-11-02: "add a toggle for the pixel scaling thing we optimized.
   * to enable/disable it"); the next frame's resize takes it up. Off
   * unless chosen, so the world is drawn as it was before the cap (later
   * that day: "default the pixel scaling to full, as it was before the
   * optimization change").
   */
  dprCapped = false;

  constructor(private canvas: HTMLCanvasElement, private game: Game) {
    this.ctx = canvas.getContext("2d")!;
    this.resize();
  }

  /**
   * How many tiles across and down the window is showing right now: the
   * canvas, in its own pixels, over how big a tile is. Fractional on
   * purpose — a window is rarely a whole number of tiles wide, and
   * rounding it to one is exactly the letterboxing this replaced.
   */
  private viewW(): number {
    return this.worldW() / TILE;
  }
  private viewH(): number {
    return this.worldH() / TILE;
  }
  /** And the same in world pixels, which is what the drawing works in. */
  private worldW(): number {
    return (this.canvas.width || TILE) / this.scale;
  }
  private worldH(): number {
    return (this.canvas.height || TILE) / this.scale;
  }
  /** How big a tile is on the glass, which is what the zoom sets. */
  private tilePx(): number {
    return this.freePx ?? ZOOMS[this.zoom];
  }

  /**
   * A mark that is n pixels across on the glass, given in the world
   * pixels the drawing works in.
   *
   * Everything drawn is multiplied by tilePx/TILE on its way to the
   * screen, so a mark of w world pixels lands as w * tilePx / TILE
   * screen pixels: to hold a size on the glass the world size has to go
   * the other way as the zoom comes in. A fixed world size does the
   * opposite of what a mark is for — the six world pixels the radar's
   * dots used to be were six screen pixels at the closest zoom and one
   * and a half at the furthest one that still draws them, which is where
   * they were found (2026-09-20: "Scale the red dots for monsters out of
   * view when clairvoyance is active at different levels of zoom"): the
   * dots faded out exactly as the zoom that needs them arrived.
   *
   * This is for marks and nothing else. A sprite, a tile and a nameplate
   * are all things in the world and are meant to shrink with it; a dot
   * is a pointer at a thing too far off to draw, and a pointer that
   * shrinks with the map is a pointer nobody can see.
   */
  private markPx(n: number): number {
    return (n * TILE) / this.tilePx();
  }

  /**
   * A tile-sized block of flat colour, laid so that it meets its
   * neighbours exactly.
   *
   * The world is drawn through a scale transform, and at most zooms that
   * scale is not a whole number: a tile is 32 world pixels and 5 screen
   * pixels at the far end of the ladder, which is 0.15625 device pixels
   * to the world pixel. A rectangle whose edges are rounded to whole
   * *world* pixels then lands on arbitrary fractions of a device pixel,
   * and the canvas answers a partly-covered pixel by blending it with
   * what is under it — which here is the black the frame was cleared to.
   * Every tile is drawn a hair short of its neighbour, every boundary
   * picks up a little of that black, and the flat view comes out with a
   * dark grid ruled over it (asked for on 2026-09-21: "Fix the grid that appears
   * when at some zoom levels"). It was worst at five pixels a tile,
   * where the whole map was cross-hatched.
   *
   * So the rounding is done where it means something. Both edges are
   * snapped to whole device pixels and the width is the difference, so
   * one tile's right edge is the next one's left edge exactly: no pixel
   * is left partly covered, nothing blends with the background, and no
   * line appears. Tiles come out a device pixel wider or narrower than
   * each other at fractional zooms, which is what drawing a five-and-a-
   * bit-pixel tile on a pixel grid honestly looks like.
   */
  private fillTile(sx: number, sy: number, across = 1) {
    const s = this.scale;
    const x0 = Math.round(sx * s) / s,
      y0 = Math.round(sy * s) / s;
    // `across` tiles side by side in one rectangle, which is the same
    // pixels as that many laid one by one, their shared edges snapped
    // where each would have met the next.
    const x1 = (across === 1 ? Math.round((sx + TILE) * s) : Math.round((sx + across * TILE) * s)) / s,
      y1 = Math.round((sy + TILE) * s) / s;
    this.ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
  }

  /**
   * A tile of floor, laid as fillTile lays a block of colour: its edges
   * snapped to whole device pixels, so it meets its neighbours exactly.
   * Drawn at a rounded world pixel instead, a floor tile's edges land on
   * fractions of a device pixel at every zoom that is not a whole multiple
   * of the art, which is most of them (38 px is 1.1875 device pixels to the
   * world pixel). Where the browser draws the canvas on the graphics card,
   * as it does on most desktops and phones, it answers each partly-covered
   * edge pixel with a blend of the tile and the black the frame was
   * cleared to, and the ground comes out ruled with thin dark lines along
   * the tile rows or columns (2026-10-19, Matt's screenshot: "what happened
   * here?"). A tile stretched a device pixel one way or the other is a
   * pixel of grass nobody can see; a line is a grid everybody can.
   *
   * `part` lays only a part of the square, in world pixels from its
   * top-left: the floor beside a wall (layGround).
   */
  private drawTileImage(
    img: CanvasImageSource,
    X: number,
    Y: number,
    part: { x0: number; y0: number; x1: number; y1: number } = { x0: 0, y0: 0, x1: TILE, y1: TILE },
  ) {
    const s = this.scale;
    const snap = (v: number) => Math.round(v * s) / s;
    const x0 = snap(X + part.x0),
      y0 = snap(Y + part.y0);
    const x1 = snap(X + part.x1),
      y1 = snap(Y + part.y1);
    if (x1 <= x0 || y1 <= y0) return;
    this.ctx.drawImage(img, part.x0, part.y0, part.x1 - part.x0, part.y1 - part.y0, x0, y0, x1 - x0, y1 - y0);
  }

  /**
   * The surface's ground as drawn, kept from frame to frame (2026-10-31,
   * from "Implement the other improvements", phone lag): laying a few
   * hundred tiles, and working out the floor beside every wall, each
   * frame was most of what a town cost, and the ground of a town does not
   * change between frames. It is laid once over the window and a margin
   * round it, a picture for each frame of the water, and the picture is
   * laid on the scene a frame at a time. It is laid again when the window
   * leaves it, the zoom changes, ground is seen for the first time, a
   * door comes into view or the protection zones change.
   *
   * The picture is drawn at the scene's own scale, its tiles' edges on
   * its own device pixels as the scene's are on the scene's, and it is
   * laid at a whole device pixel; a tile's edges then fall at most a
   * pixel from where laying it straight would put them, and the ground
   * holds still under a moving camera rather than shimmering a pixel
   * this way and that.
   */
  /** What the scene works out of the terrain, kept between frames: see TileMemo. */
  private tileMemo = new TileMemo();
  private hangMemo = new TileMemo();
  private groundKept: {
    key: string;
    pz: unknown;
    pzBelow: unknown;
    x0: number;
    x1: number;
    y0: number;
    y1: number;
    dx: number;
    dy: number;
    pics: (HTMLCanvasElement | undefined)[];
    spare: HTMLCanvasElement[];
  } | null = null;
  /**
   * Set when the browser would not give the ground its picture (iOS
   * Safari refuses a canvas once its pages' canvases add up past its
   * limit, and the whole frame went black when walking: 2026-11-01,
   * "moving and the screen goes black now"). The ground is then drawn
   * tile by tile every frame, as it was before the picture.
   */
  private groundKeptOff = false;
  /** Give the pictures' memory back: a canvas cut to nothing frees it at once, which iOS needs. */
  private dropGroundKept() {
    const k = this.groundKept;
    if (k) for (const c of [...k.spare, ...k.pics]) if (c) c.width = c.height = 0;
    this.groundKept = null;
  }
  /**
   * Lay the surface ground from the picture kept for it, and say whether
   * it was; false means the caller lays it tile by tile.
   */
  private layGroundKept(x0: number, x1: number, y0: number, y1: number, at: { sx: number; sy: number }, phase: number, era: string, tile: (x: number, y: number, X: number, Y: number) => void): boolean {
    if (this.groundKeptOff) return false;
    try {
      return this.layGroundPicture(x0, x1, y0, y1, at, phase, era, tile);
    } catch (e) {
      console.warn("ground picture refused, drawing the ground tile by tile", e);
      this.groundKeptOff = true;
      this.dropGroundKept();
      return false;
    }
  }
  private layGroundPicture(x0: number, x1: number, y0: number, y1: number, at: { sx: number; sy: number }, phase: number, era: string, tile: (x: number, y: number, X: number, Y: number) => void): boolean {
    const g = this.game;
    const s = this.scale;
    const key = `${era}|${s}`;
    let k = this.groundKept;
    if (!k || k.key !== key || k.pz !== g.pz || k.pzBelow !== g.pzBelow || x0 < k.x0 || x1 > k.x1 || y0 < k.y0 || y1 > k.y1) {
      const m = GROUND_KEPT_PAST;
      // The pictures are kept for the next window when they are as big
      // as it needs (a walk only moves the window), so walking allocates
      // nothing; a bigger window (a zoom, a turn of the phone) starts
      // afresh and frees the old ones.
      const m2 = (n: number) => Math.ceil((n + 2 * m) * TILE * s) + 1;
      const need = { w: m2(x1 - x0 + 1), h: m2(y1 - y0 + 1) };
      let spare = k ? [...k.spare, ...k.pics.filter((c): c is HTMLCanvasElement => !!c)] : [];
      if (spare.some((c) => c.width !== need.w || c.height !== need.h)) {
        for (const c of spare) c.width = c.height = 0;
        spare = [];
      }
      k = this.groundKept = {
        key,
        pz: g.pz,
        pzBelow: g.pzBelow,
        x0: x0 - m,
        x1: x1 + m,
        y0: y0 - m,
        y1: y1 + m,
        dx: Math.round((x0 - m) * TILE * s),
        dy: Math.round((y0 - m) * TILE * s),
        pics: [],
        spare,
      };
    }
    let pic = k.pics[phase];
    if (!pic) {
      pic = k.pics[phase] = k.spare.pop() ?? document.createElement("canvas");
      const w = Math.ceil((k.x1 - k.x0 + 1) * TILE * s) + 1, h = Math.ceil((k.y1 - k.y0 + 1) * TILE * s) + 1;
      if (pic.width !== w || pic.height !== h) {
        pic.width = w;
        pic.height = h;
      }
      const pctx = pic.getContext("2d");
      if (!pctx || pic.width !== w || pic.height !== h) throw new Error(`no ${w}x${h} canvas for the ground`);
      pctx.setTransform(1, 0, 0, 1, 0, 0);
      pctx.clearRect(0, 0, w, h);
      pctx.imageSmoothingEnabled = false;
      pctx.setTransform(s, 0, 0, s, -k.dx, -k.dy);
      // Laid by the scene's own tile painters, which paint on this.ctx.
      const scene = this.ctx;
      this.ctx = pctx;
      try {
        for (let y = k.y0; y <= k.y1; y++) for (let x = k.x0; x <= k.x1; x++) tile(x, y, x * TILE, y * TILE);
      } finally {
        this.ctx = scene;
      }
    }
    // Where the picture's corner goes: tile (0, 0) is drawn at a whole
    // world pixel (see the ground loop's Math.round), which the picture
    // took as its nought.
    const ctx = this.ctx;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(pic, k.dx + Math.round(Math.round(at.sx) * s), k.dy + Math.round(Math.round(at.sy) * s));
    ctx.restore();
    return true;
  }

  /**
   * Drag the view, in the CSS pixels a pointer moves in. The conversion
   * to tiles belongs here because the zoom does: a drag of forty pixels
   * is a tile and a bit at the closest zoom and thirteen tiles at the
   * furthest, and the hand expects the ground to keep up with the finger
   * at both. The pan itself is the game's, so that a step of the
   * player's clears it along with the level peek — see Game.pan.
   */
  dragPan(dxCss: number, dyCss: number) {
    const px = this.tilePx();
    this.game.panBy(dxCss / px, dyCss / px);
  }

  /**
   * Step the zoom in or out and report where it ended up, so the buttons
   * can grey themselves at the ends of the range. Nothing else changes:
   * the camera is on the player at every zoom and stays there, so zooming
   * out shows more of the world round them rather than moving the view
   * off them.
   */
  zoomBy(by: number): { at: number; of: number } {
    this.zoom = Math.max(0, Math.min(ZOOMS.length - 1, this.zoom + by));
    return this.zoomAt();
  }

  /** The zoom as a place in the range, for the buttons: 0 closest. */
  zoomAt(): { at: number; of: number } {
    return { at: this.zoom, of: ZOOMS.length - 1 };
  }

  /** Set it directly, for a choice remembered from last session. */
  setZoom(at: number): { at: number; of: number } {
    if (Number.isInteger(at) && at >= 0 && at < ZOOMS.length) this.zoom = at;
    return this.zoomAt();
  }

  /**
   * How big a tile is at the rung the world is drawn at, and the rung
   * that draws a tile that big. This pair is what the choice is
   * remembered as between sessions, rather than the rung's number.
   *
   * The number is a position in a list, and the list has now changed
   * twice at the close end: every remembered 0 meant 32 before the first
   * rung was added and would silently have meant 48 after it, and 64
   * after the second, for everybody who had ever touched the zoom. A size
   * does not move when the ladder grows, and a size the ladder no longer
   * offers lands on the nearest rung that exists, which is the right
   * answer to a ladder that has been rewritten under a stored choice.
   * Adding 64 cost nothing at all because of this, which is the whole
   * point of having done it this way.
   */
  zoomPx(): number {
    return this.tilePx();
  }
  setZoomPx(px: number): { at: number; of: number } {
    if (Number.isFinite(px) && px > 0) {
      let best = this.zoom;
      for (let i = 0; i < ZOOMS.length; i++) {
        if (Math.abs(ZOOMS[i] - px) < Math.abs(ZOOMS[best] - px)) best = i;
      }
      this.zoom = best;
    }
    return this.zoomAt();
  }

  /**
   * Home: the size the world is designed around, whatever rung that is.
   * The button that asks for it does not know the number and should not
   * — the ladder is this file's business, and the day a rung was added
   * at the close end is the day a button that reset by walking to rung
   * zero would quietly have started resetting to the wrong place.
   */
  resetZoom(): { at: number; of: number } {
    this.zoom = ZOOM_DEFAULT;
    return this.zoomAt();
  }

  /** Whether the world is drawn at the size it is designed around. */
  atDefaultZoom(): boolean {
    return this.zoom === ZOOM_DEFAULT;
  }

  /**
   * How many whole tiles across and down the window shows at the rung it
   * is at now, for the note a change of zoom puts up (2026-10-16: "When
   * changing the zoom, show the tile size in pixels, and the dimensions of
   * the view in tiles"). It reads the canvas' displayed size rather than
   * viewW, since that waits on the next frame's resize to catch up with a
   * new rung, and the note goes up before that frame is drawn. Rounded,
   * because the window is rarely a whole number of tiles and "21.4 tiles"
   * is a measurement nobody asked for.
   */
  viewTiles(): { w: number; h: number } {
    const px = this.tilePx();
    return {
      w: Math.round((this.canvas.clientWidth || VIEW_FALLBACK * px) / px),
      h: Math.round((this.canvas.clientHeight || VIEW_FALLBACK * px) / px),
    };
  }

  /**
   * Match the backing store to the canvas' displayed size in device pixels.
   *
   * The world is then drawn through a scale transform rather than by stretching a
   * 480x352 image: sprites are still magnified with smoothing off, but anything the
   * canvas rasterises itself — text above all — is produced at the display's real
   * resolution instead of being blown up along with the pixel art.
   */
  private resize() {
    // At most two backing pixels to a CSS pixel (2026-10-31, from
    // "Implement the other improvements", phone lag): a phone's screen is
    // two and a half to three, and every frame then fills twice the
    // pixels of two for a sharpness that is hard to see on a screen that
    // small, while it is what holds a phone's frame rate down. The
    // browser stretches the backing store over the rest.
    // The cap is chosen in the menu, and off unless it is (see dprCapped).
    this.dpr = this.dprCapped ? Math.min(window.devicePixelRatio || 1, MAX_DPR) : window.devicePixelRatio || 1;
    const w = Math.max(1, Math.round((this.canvas.clientWidth || VIEW_FALLBACK * this.tilePx()) * this.dpr));
    const h = Math.max(1, Math.round((this.canvas.clientHeight || VIEW_FALLBACK * this.tilePx()) * this.dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
      this.ctx.imageSmoothingEnabled = false; // resizing the backing store resets the context
    }
    // World pixels to device pixels. The zoom says how big a tile is on
    // the glass, and a tile is TILE world pixels, so the scale falls
    // straight out of it — and nothing is letterboxed, because there is
    // no fixed shape left to letterbox: the window shows as much of the
    // world as it has room for, in whatever shape it happens to be.
    this.scale = (this.tilePx() * this.dpr) / TILE;
    this.offX = 0;
    this.offY = 0;
  }

  /**
   * Draw a label in world coordinates. The font size is given in world pixels and the
   * canvas rasterises the glyphs through the view transform, so they gain detail as the
   * view is scaled up instead of turning into magnified blocks. A round-joined outline
   * replaces the offset drop shadow the old low-resolution canvas needed.
   */
  /**
   * The health bar under a nameplate: centred on the creature's tile, as
   * big as its full health is big — long up to the width of the tile and
   * then thick, see hpBarShape — and filled with what is left of it. The
   * colour is the fraction, not the size, so a hurt dragon and a hurt rat
   * go yellow and then red at the same point.
   *
   * Past the fill comes the healing still owed — what the potion working
   * on you will have added by the time its window runs out. Only your own
   * bar ever has any: the projection is a fact about you, sent with you,
   * and nothing on the wire says where anybody else's healing is taking
   * them. So projected defaults to hp, which is every other creature in
   * the world and is also you with nothing running, and in that case the
   * stretch is zero wide and the bar is drawn exactly as it always was.
   * There is no branch here for the ordinary case because the ordinary
   * case is what the arithmetic already does.
   *
   * The shade is the bar's own colour lifted most of the way to white,
   * which is the rule the stat panel's bar follows too. A pale green after
   * a green fill reads as more of the same bar arriving; a colour of its
   * own would read as a second thing happening to you, and a darker one as
   * damage — darkness on a health bar already means something lost. Pale
   * also keeps it clear of everything else painted around a head, none of
   * which is washed out.
   *
   * And the end of the fill is the poison's when a dose is running in
   * you: the health it will still take, painted over the last of what you
   * have in the green its numbers land in (2026-10-27: "show the projected
   * part of health lost to poison as a green component of the health
   * bar"). Inside the fill and not past it, because it is health you have
   * now and are going to lose, where the potion's stretch is health you
   * have not got yet. Like the projection it is only ever yours, and with
   * no dose it is nothing wide.
   */
  private healthBar(ctx: CanvasRenderingContext2D, X: number, y: number, hp: number, maxhp: number, projected = hp, grey = false, poison = 0) {
    const frac = Math.max(0, Math.min(1, hp / maxhp));
    // Both of the bar's dimensions come from the one place that decides
    // them, so nothing drawn here can disagree with how big the bar is
    // meant to be: a height written out again as a number would be one
    // careless edit away from a fill hanging out of its own border.
    const { w, h } = hpBarShape(maxhp);
    const x = Math.round(X + TILE / 2 - w / 2);
    // An admin gone /invisible has their bar faded with their figure (see
    // GHOST_ALPHA), over whatever fade the dark already put on it, and put
    // back as it was at the end.
    const faded = ctx.globalAlpha;
    if (grey) ctx.globalAlpha = faded * GHOST_ALPHA;
    ctx.fillStyle = "#000";
    ctx.fillRect(x, y, w, h);
    // Both colours come out of one conditional so that the thresholds the
    // fill changes colour at cannot drift from the ones the projection
    // does: two ternaries side by side would be one careless edit away
    // from a bar whose two halves disagreed about how hurt you are.
    // Red is three tenths and under since 2026-09-27 ("make it so rats and
    // wolves run away on red health. define red health"): the server's
    // redHealth, where a rat or a wolf turns and runs. It was orange from
    // three tenths to one, and red below that; the last tenth is a darker
    // red now, so the bar still says when something is nearly dead.
    const [hue, paler] =
      frac > 0.6 ? ["#0c0", "#8e8"] : frac > 0.3 ? ["#cc0", "#ee8"] : frac > 0.1 ? ["#d22", "#e99"] : ["#900", "#d77"];
    // And greyed: the fill still says how much is left, in greys that keep
    // the colours' order of brightness.
    const [solid, ahead] = grey ? [greyed(hue), greyed(paler)] : [hue, paler];
    // The border is one pixel on every side, so the fill is inset by one
    // and short by two in both directions whatever the bar's size. A bar
    // of any height therefore divides into border, fill and border with
    // nothing left over and nothing sitting off centre — which a fill of
    // some fixed depth inside a growing border would not.
    const inner = w - 2;
    const innerH = h - 2;
    const filled = Math.round(inner * frac);
    ctx.fillStyle = solid;
    ctx.fillRect(x + 1, y + 1, filled, innerH);
    // Clamped at zero because a negative width is not nothing to a canvas:
    // fillRect draws a backwards rectangle, and that one would paint the
    // pale shade back over the end of the solid fill and read as a wound.
    const pfrac = Math.max(0, Math.min(1, projected / maxhp));
    ctx.fillStyle = ahead;
    ctx.fillRect(x + 1 + filled, y + 1, Math.max(0, Math.round(inner * pfrac) - filled), innerH);
    // Rounded up, so a dose with anything left to take shows at least a
    // pixel of it however long the bar, and never wider than the fill.
    const venom = Math.min(filled, Math.ceil((inner * Math.max(0, poison)) / maxhp));
    ctx.fillStyle = grey ? greyed(POISON_GREEN) : POISON_GREEN;
    ctx.fillRect(x + 1 + filled - venom, y + 1, venom, innerH);
    ctx.globalAlpha = faded;
  }

  /**
   * One tile of a dragon's fire, drawn the way Tibia draws a fire field:
   * the whole square alight rather than a puff in the middle of it, since
   * what a player has to read is which tiles the wave covered. The tile is
   * laid down in bands — a scorched red floor, an orange body, tongues
   * with pale hot hearts — and the tongues step between a handful of
   * shapes on a frame clock rather than sliding, which is what makes it
   * read as a flame rather than as a wobbling blob.
   *
   * Every tile of one wave is drawn by this, with its own world position
   * as the seed, so neighbours flicker out of step with each other while
   * the floor underneath them joins up into one sheet.
   */
  private fireTile(ctx: CanvasRenderingContext2D, X: number, Y: number, wx: number, wy: number, age: number, now: number) {
    // It holds at full strength for most of its life and then goes out
    // quickly: a wave that faded from the moment it landed would be at its
    // clearest in the instant nobody was looking yet.
    const fade = age < 0.7 ? 1 : 1 - (age - 0.7) / 0.3;
    const seed = (Math.abs(wx * 7 + wy * 13) % 4) + Math.floor(now / 120);
    ctx.save();
    // Bright, but not a curtain: the wave is drawn over whatever is
    // standing in it, and somebody burning should still be somebody you
    // can see burning.
    ctx.globalAlpha = fade * 0.82;
    // The floor of it, tile-square so a row of them is a sheet of fire.
    ctx.fillStyle = "rgba(150,28,6,0.6)";
    ctx.fillRect(X, Y, TILE, TILE);
    const body = ctx.createLinearGradient(0, Y + TILE, 0, Y);
    body.addColorStop(0, "rgba(255,106,20,0.8)");
    body.addColorStop(0.55, "rgba(236,66,10,0.6)");
    body.addColorStop(1, "rgba(150,28,6,0.2)");
    ctx.fillStyle = body;
    ctx.fillRect(X, Y, TILE, TILE);
    // Tongues: base across the tile, heights stepping frame by frame.
    const tongue = (bx: number, half: number, h: number, hot: boolean) => {
      const foot = Y + TILE - 1;
      ctx.beginPath();
      ctx.moveTo(X + bx - half, foot);
      ctx.quadraticCurveTo(X + bx - half, foot - h * 0.55, X + bx, foot - h);
      ctx.quadraticCurveTo(X + bx + half, foot - h * 0.55, X + bx + half, foot);
      ctx.closePath();
      const g = ctx.createLinearGradient(0, foot, 0, foot - h);
      if (hot) {
        g.addColorStop(0, "#fff6d0");
        g.addColorStop(0.5, "#ffd24a");
        g.addColorStop(1, "rgba(255,140,40,0.15)");
      } else {
        g.addColorStop(0, "#ffb43c");
        g.addColorStop(0.6, "#ff7a1e");
        g.addColorStop(1, "rgba(214,60,12,0.1)");
      }
      ctx.fillStyle = g;
      ctx.fill();
    };
    const step = [0, 1, 2, 1];
    for (const [bx, half, base, hot] of [
      [6, 6, 20, false],
      [16, 9, 30, false],
      [26, 6, 19, false],
      [11, 4, 14, true],
      [21, 4, 15, true],
    ] as const) {
      tongue(bx, half, base + step[(seed + bx) % step.length] * 4, hot);
    }
    // Embers lifting off the top, which is what says the fire is moving
    // rather than painted on.
    ctx.fillStyle = "rgba(255,214,120,0.9)";
    for (let i = 0; i < 3; i++) {
      const f = (seed + i * 5) % 12;
      ctx.fillRect(X + 5 + ((i * 11 + f) % (TILE - 8)), Y + 4 + ((f * 3) % 14), 2, 2);
    }
    ctx.restore();
  }

  /**
   * One tile of a rift: the ground split open and lit from underneath. The
   * burst is sent a tile at a time the way a dragon's breath is, and it
   * brings the same problem with it — twenty separate puffs read as twenty
   * things happening rather than as one — so this is built to join up at the
   * edges instead. Two things do that. The whole square is washed dark, so
   * the covered ground is one unbroken shadow before a crack is even looked
   * at; and every crack runs from the middle of one of the tile's edges to
   * the middle of the tile, with whether an edge is split at all decided
   * from that edge's own position in the world rather than from either tile
   * beside it. Both tiles sharing an edge therefore ask the same question
   * and get the same answer, and the cracks run on across the whole area as
   * one web rather than stopping at every tile boundary.
   *
   * The demon's own tile is never sent, so the web has a hole in the middle
   * of it with the demon standing in the hole, which is the right picture:
   * the ground has opened around the thing, not under it.
   */
  private riftTile(ctx: CanvasRenderingContext2D, X: number, Y: number, wx: number, wy: number, age: number, now: number) {
    // It tears open, holds while a player works out where the edge of it
    // is, and shuts. Nothing about it eases in — ground does not open
    // gradually — so the opening is a twelfth of its life and the rest is
    // the part that is there to be read.
    const open = age < 0.12 ? age / 0.12 : age < 0.68 ? 1 : Math.max(0, 1 - (age - 0.68) / 0.32);
    if (open <= 0) return;
    ctx.save();
    ctx.fillStyle = `rgba(16,4,8,${0.52 * open})`;
    ctx.fillRect(X, Y, TILE, TILE);
    // An edge is named by the corner it starts at and whether it runs across
    // or down, which is the same pair of numbers asked from either side of
    // it. Roughly a third of them stay whole, which is what keeps the web
    // from closing into a net.
    const split = (ex: number, ey: number, across: number) =>
      ((Math.imul(ex, 73856093) ^ Math.imul(ey, 19349663) ^ Math.imul(across + 1, 83492791)) >>> 5) % 7 > 1;
    const seed = Math.abs(wx * 7 + wy * 13) % 4;
    // Where this tile's cracks meet, which is near the middle but not on it:
    // a node pinned to every tile centre puts the whole burst on a lattice
    // and the eye finds the grid instead of the ground.
    const nx = X + TILE / 2 + (seed - 1.5) * 2.6, ny = Y + TILE / 2 + ((seed * 5) % 4 - 1.5) * 2.6;
    const legs: number[][] = [];
    for (const [ex, ey, ok] of [
      [X + TILE / 2, Y, split(wx, wy, 1)],
      [X + TILE / 2, Y + TILE, split(wx, wy + 1, 1)],
      [X, Y + TILE / 2, split(wx, wy, 0)],
      [X + TILE, Y + TILE / 2, split(wx + 1, wy, 0)],
    ] as const) {
      if (!ok) continue;
      // A kink on the way in, square to the run of the crack. Both ends
      // stay put — the edge and the node — so it can wander as much as it
      // likes in between without ever failing to meet its neighbour.
      const dx = nx - ex, dy = ny - ey, L = Math.hypot(dx, dy) || 1;
      const k = legs.length % 2 ? 2.6 : -2.6;
      legs.push([ex, ey, (ex + nx) / 2 - (dy / L) * k, (ey + ny) / 2 + (dx / L) * k, nx, ny]);
    }
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    // Black first for the gap itself, then the fire a long way down it, then
    // the hot line up the middle: a crack is a hole with light in it, and
    // painting the light without the hole gives a glowing scribble lying on
    // top of the floor. Each one is thin where it reaches the tile edge and
    // widest where the cracks meet, so it narrows away to nothing instead of
    // stopping dead — and the two halves of a crack still meet, because both
    // tiles draw the thin end of it at the same point.
    for (const [w, col] of [
      [6, `rgba(6,2,4,${0.95 * open})`],
      [3.4, `rgba(188,48,10,${0.85 * open})`],
      [1.3, `rgba(255,198,86,${open})`],
    ] as const) {
      for (const [near, a, b] of [[0.42, 0, 1], [1, 1, 2]] as const) {
        ctx.lineWidth = w * near * (0.4 + 0.6 * open);
        ctx.strokeStyle = col;
        ctx.beginPath();
        for (const g of legs) {
          ctx.moveTo(g[a * 2], g[a * 2 + 1]);
          ctx.lineTo(g[b * 2], g[b * 2 + 1]);
        }
        ctx.stroke();
      }
    }
    if (legs.length > 1) {
      // Where they meet the hole is widest, and what is under the floor
      // shows through it rather than merely lighting the edges.
      ctx.fillStyle = `rgba(255,150,46,${0.55 * open})`;
      ctx.beginPath();
      ctx.ellipse(nx, ny, 5 * open, 3.4 * open, seed, 0, Math.PI * 2);
      ctx.fill();
      // Embers coming up out of it on the fire's own frame clock, so a rift
      // is plainly alight and not a crack painted on the floor.
      ctx.fillStyle = `rgba(255,214,120,${0.9 * open})`;
      const f = Math.floor(now / 120) + seed;
      for (let i = 0; i < 2; i++) {
        ctx.fillRect(nx - 4 + ((i * 7 + f * 3) % 9), ny - 3 - (((f + i * 5) * 3) % 11), 2, 2);
      }
    }
    ctx.restore();
  }

  /**
   * What a dead player sees: the world going on without them, and the view
   * held where they fell — the body is gone but the skull is on that tile
   * — so the last thing a death shows is where it happened and what is
   * lying there.
   *
   * The words and the way out are the #death panel over the top of this,
   * not painted here. They used to be painted here, and while the temple
   * took you on a timer that was enough: there was nothing to press, only
   * something to read. There is something to press now, and a button
   * painted on a canvas is a button that cannot be tabbed to, cannot be
   * pressed with a key and has to have its own hit-testing written for it.
   * What is left in here is the dimming, because that belongs to the view
   * rather than to the panel — see #death for the rest of the wash.
   */
  private deathBanner(g: Game) {
    if (!g.you?.dead) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.fillStyle = "rgba(20,0,0,0.45)";
    ctx.fillRect(0, 0, this.worldW(), this.worldH());
    ctx.restore();
  }

  /**
   * Which of these creatures go over which of their names (namefit.ts),
   * each as the figure the scene painted: a name that would be drawn over
   * another creature is laid behind it rather than left off (2026-10-07).
   * A body is its sprite where it is drawn, from the top of its ink down,
   * no wider than a tile, since most sprites are padded out past the
   * figure; a name is its text, with a skull's width beside it, at the
   * size it will be drawn. (X, Y) is where the name is reckoned from and
   * (bx, by) where the figure stands, which differ only on a cave's ramp
   * or ladder, where the cave's names do not follow the figure.
   */
  private creaturesOverNames(
    list: { c: ClientCreature; X: number; Y: number; bx: number; by: number; spr: HTMLCanvasElement; ny: number }[],
  ): Map<number, Figure[]> {
    const over = new Map<number, Figure[]>();
    if (list.length < 2) return over;
    const ctx = this.ctx;
    ctx.font = labelFont(10);
    const h = labelPx(10);
    const ids = creaturesOverNames(
      list.map(({ c, X, bx, by, spr, ny }) => {
        const w = ctx.measureText(c.name).width + (c.skull ? 14 : 0);
        const bw = Math.min(spr.width, TILE);
        const top = by + TILE - spr.height + inkTop(spr);
        return {
          id: c.id,
          name: { x: X + TILE / 2 - w / 2, y: ny - h, w, h: h + 2 },
          body: { x: bx + (TILE - bw) / 2, y: top, w: bw, h: by + TILE - top },
        };
      }),
    );
    const byId = new Map(list.map((e) => [e.c.id, e]));
    for (const [id, others] of ids) {
      over.set(
        id,
        others.map((o) => {
          const { bx, by, spr } = byId.get(o)!;
          return { spr, L: bx - (spr.width - TILE) / 2, T: by + TILE - spr.height };
        }),
      );
    }
    return over;
  }

  /**
   * A creature's name over its head, centred on its tile at X, at the
   * baseline ny, with its skull beside it when it wears one: straight onto
   * the scene, or, when `over` has figures in it, behind them (namefit.ts's
   * nameBehind), so they stand over the part of the name they cover and
   * the rest of it shows. At the context's alpha either way.
   *
   * A name behind somebody is held until layNamesBehind, which lays every
   * one held at once: see namesBehind.
   */
  private nameplate(c: ClientCreature, X: number, ny: number, color: string, skull: boolean, over: Figure[] | undefined) {
    const label = (into: CanvasRenderingContext2D) => {
      if (skull) {
        into.font = labelFont(10);
        drawSkullMark(into, X + TILE / 2 - into.measureText(c.name).width / 2 - 7, ny - 3, 10);
      }
      drawLabel(into, c.name, X + TILE / 2, ny, 10, color, "center");
    };
    if (!over?.length) {
      label(this.ctx);
      return;
    }
    this.ctx.font = labelFont(10);
    const tw = this.ctx.measureText(c.name).width, h = labelPx(10);
    // Room for the outline round the glyphs, their descenders and the
    // skull, which sits a little lower than the text and left of it.
    const left = X + TILE / 2 - tw / 2 - (skull ? 14 : 0) - 4;
    const box = { x: left, y: ny - h - 4, w: X + TILE / 2 + tw / 2 + 4 - left, h: h + 9 };
    this.behind.push({ box, label, over, alpha: this.ctx.globalAlpha });
  }

  /** The names nameplate has held to go behind somebody, until they are laid. */
  private behind: BehindName[] = [];
  /** Lay the names held to go behind somebody, in the order they were held. */
  private layNamesBehind() {
    namesBehind(this.ctx, (w, hh) => this.nameLayerOf(w, hh), this.behind);
    this.behind = [];
  }

  /** The layer a name is laid on to go behind somebody, kept and grown as needed. */
  private nameLayer: CanvasRenderingContext2D | null = null;
  private nameLayerOf(w: number, h: number): CanvasRenderingContext2D {
    let lc = this.nameLayer;
    if (!lc || lc.canvas.width < w || lc.canvas.height < h) {
      const c = document.createElement("canvas");
      c.width = Math.max(w, lc ? lc.canvas.width : 0);
      c.height = Math.max(h, lc ? lc.canvas.height : 0);
      lc = c.getContext("2d")!;
      this.nameLayer = lc;
    }
    return lc;
  }

  private text(
    s: string,
    x: number,
    y: number,
    size: number,
    color: string,
    align: CanvasTextAlign = "left",
    family = LABEL_FONT,
  ) {
    drawLabel(this.ctx, s, x, y, size, color, align, family);
  }

  /**
   * Lay a spoken line out to fit the room a bubble is allowed: five tiles
   * across and two high, directly over the speaker. The text is wrapped to
   * the width at the largest size it will take, and shrunk a point at a
   * time until the wrapped block fits the height too. So a word said in
   * passing is large and legible, a speech runs small, and neither one
   * sprawls across the map over everybody else.
   *
   * scale shrinks the whole arrangement — the box and both ends of the size
   * range together — for speech that is being reported from off the edge of
   * the screen rather than shown over the head that said it. Halving the
   * box as well as the type is what keeps it half size rather than merely
   * half as tall: the same words in a box half as wide would wrap to twice
   * as many lines and take up as much room as they did before.
   */
  private fitSpeech(text: string, scale = 1): { lines: string[]; size: number; lh: number } {
    const maxW = SPEECH_TILES_W * TILE * scale;
    const maxH = SPEECH_TILES_H * TILE * scale;
    // The floor is a floor and not a scaling: half of the smallest size
    // words are ever set in is not small type, it is a smudge. So a halved
    // bubble stops shrinking where the eye does, and a speech long enough
    // to reach that floor overruns its box exactly as one over a head does.
    const lo = Math.max(SPEECH_EDGE_MIN, Math.round(SPEECH_MIN * scale));
    const hi = Math.max(lo + 1, Math.round(SPEECH_MAX * scale));
    let lines: string[] = [];
    let size = lo;
    for (size = hi; size > lo; size--) {
      // Measured at the size it will be drawn at, snapped or not.
      const px = labelPx(size);
      this.ctx.font = labelFont(size);
      lines = wrapToWidth(this.ctx, text, maxW);
      if (lines.length * (px + 2) <= maxH) return { lines, size: px, lh: px + 2 };
    }
    // Nothing this long fits; the smallest size is the best on offer, and
    // it overruns the box upwards rather than being cut off mid-word.
    this.ctx.font = labelFont(lo);
    const px = labelPx(lo);
    return { lines: wrapToWidth(this.ctx, text, maxW), size: px, lh: px + 2 };
  }

  /**
   * Convert a mouse event on the canvas to a tile coordinate: the tile
   * whose picture is under the pointer, whatever height it is drawn at.
   *
   * Until the oblique view (2026-09-27, "implement the oblique view with
   * south and east faces visible") that was one division, because every
   * tile was drawn where it lay. Now a tile a storey up is drawn half a
   * tile up and to the left of that, so the point under the pointer may be
   * the top of a crag, the face of the wall in front of the ground there,
   * or the ground itself, and which it is depends on what stands where.
   * pickAt follows the line of sight down from above the highest thing
   * drawn and answers with the first thing it meets: raised surfaces front
   * to back, and the ground last. `h` is the height of what was met, for
   * the hover outline to be drawn on it; everything else reads x and y,
   * and what it does with them is exactly what it did before — a walk
   * still names a tile of the walker's own level and the server still
   * decides the rest.
   *
   * The zoomed-out map is drawn flat, top down, and is read back the old
   * way. A look at another floor is not, since 2026-10-13 ("when viewing
   * different levels, they should be rendered the same way as regular
   * levels, the oblique applies everywhere"): it is the close view seen
   * from that floor, and is picked from that floor.
   */
  tileFromEvent(ev: { clientX: number; clientY: number }): Picked {
    const r = this.canvas.getBoundingClientRect();
    // The viewport is letterboxed inside the canvas; convert CSS pixels to world pixels.
    const cssScale = this.scale / this.dpr;
    const px = (ev.clientX - r.left - this.offX / this.dpr) / cssScale;
    const py = (ev.clientY - r.top - this.offY / this.dpr) / cssScale;
    const originX = this.camX - this.viewW() / 2 + 0.5;
    const originY = this.camY - this.viewH() / 2 + 0.5;
    const wx = originX + px / TILE, wy = originY + py / TILE;
    const me = this.game.me();
    const meZ = me?.z ?? 0;
    const eyeZ = this.game.viewZ ?? meZ;
    if (this.tilePx() < FLAT_BELOW) return { x: Math.floor(wx), y: Math.floor(wy), h: eyeZ };
    const { levels, shown } = terrainOf(this.game, me, eyeZ);
    const hit = pickThrough(wx, wy, eyeZ, levels, shown, this.doorsSeen, this.game);
    return { x: hit.x, y: hit.y, h: hit.h, through: hit.through, roof: hit.roof };
  }

  /**
   * Where the tile at x, y on level z is drawn, in client pixels as of the
   * last frame: tileFromEvent the other way round, for a menu to be put
   * beside somebody standing there (2026-10-08: "use a context menu that
   * pops up over them"). A tile a storey up is drawn up and to the left in
   * the close view, as draw's toScreen puts it, a storey up from the floor
   * the view is seen from; the far zoom draws every tile where it lies.
   */
  tileClientRect(x: number, y: number, z?: number): { left: number; top: number; right: number; bottom: number } {
    const r = this.canvas.getBoundingClientRect();
    const cssScale = this.scale / this.dpr;
    const meZ = this.game.me()?.z ?? 0;
    const eyeZ = this.game.viewZ ?? meZ;
    const up = this.tilePx() < FLAT_BELOW ? 0 : ((z ?? meZ) - eyeZ) * STOREY;
    const sx = (x - (this.camX - this.viewW() / 2 + 0.5)) * TILE - up;
    const sy = (y - (this.camY - this.viewH() / 2 + 0.5)) * TILE - up;
    const left = r.left + this.offX / this.dpr + sx * cssScale;
    const top = r.top + this.offY / this.dpr + sy * cssScale;
    return { left, top, right: left + TILE * cssScale, bottom: top + TILE * cssScale };
  }


  draw(now: number) {
    const g = this.game;
    const ctx = this.ctx;
    this.resize();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    if (!g.ready) return;
    // Everything below is in world pixels; clip so labels near the edge stop at the
    // viewport rather than spilling into the letterbox.
    ctx.save();
    ctx.setTransform(this.scale, 0, 0, this.scale, this.offX, this.offY);
    ctx.beginPath();
    ctx.rect(0, 0, this.worldW(), this.worldH());
    ctx.clip();

    const me = g.me();
    // Where the player actually is, which is not always where the camera
    // is looking any more: a drag moves the view off them (Game.pan) and
    // their own light has to stay on their feet rather than travelling
    // with the window. See the lightsOn call below, which is the one
    // place that wanted the camera and meant the player.
    const mePos = me ? creaturePos(me, now) : null;
    // Which floor the player is standing on. Declared up here beside the
    // player rather than down with the close view's roofs, where it used
    // to live, because the lights are built before the flat branch now
    // and they are a fact about the floor the player is on.
    const meZ = me?.z ?? 0;
    if (mePos) {
      this.camX = mePos.x;
      this.camY = mePos.y;
      if (g.pan) {
        this.camX += g.pan.x;
        this.camY += g.pan.y;
      }
    }
    // Pixel origin of the top-left of the viewport in world pixels.
    const originPx = (this.camX - this.viewW() / 2 + 0.5) * TILE;
    const originPy = (this.camY - this.viewH() / 2 + 0.5) * TILE;
    // The floor the scene is seen from. It is the player's own, or the one
    // they are looking at with the floor buttons: since 2026-10-13 ("when
    // viewing different levels, they should be rendered the same way as
    // regular levels, the oblique applies everywhere") a look at another
    // floor is the close view seen from that floor, as if the player stood
    // on it where they are, rather than a flat picture of it. The far zoom
    // is flat for every floor and is drawn from the player's own, as it
    // always was. `peeking` is whether the floor shown is not your own.
    const peekZ = g.viewZ;
    const shownZ = peekZ ?? meZ;
    const peeking = shownZ !== meZ;
    const eyeZ = this.tilePx() < FLAT_BELOW ? meZ : shownZ;
    // A point z storeys up is drawn STOREY pixels up and left for every
    // storey above the floor it is seen from: see ToScreen, and the oblique
    // view below.
    const toScreen: ToScreen = (tx, ty, z = eyeZ) => ({ sx: tx * TILE - originPx - (z - eyeZ) * STOREY, sy: ty * TILE - originPy - (z - eyeZ) * STOREY });

    // The tiles worth looking at: the window, and a margin round it. The
    // margin is wider on the south and east than it used to be, and a
    // little wider on the north and west, because a crag two storeys up
    // whose foot is off the bottom of the window is drawn a whole tile up
    // into it, and the ground seen from the summit is drawn down and right
    // into it from off the top.
    const vw = this.viewW(), vh = this.viewH();
    const x0 = Math.floor(this.camX - vw / 2 - 2), x1 = Math.ceil(this.camX + vw / 2 + 3);
    const y0 = Math.floor(this.camY - vh / 2 - 3), y1 = Math.ceil(this.camY + vh / 2 + 3);
    const frame = Math.floor(now / 600);
    // What lies on the ground, with the depot boxes the map has as well as
    // the items in view: see groundShown.
    const items = g.groundShown();
    // How far up each thing in a pile is drawn: by whatever under it raises
    // what lies on it, as a box does in Tibia, which is the museum's
    // display stands (2026-10-20: "put them on top of the display stands,
    // which raise the items up a little bit, like boxes do in tibia if you
    // stand on them"). A pile comes bottom first. See proto.ItemDef.Raise.
    const lifts = new Map<(typeof items)[number], number>();
    {
      const under = new Map<string, number>();
      for (const it of items) {
        const k = `${it.z ?? 0},${it.x},${it.y}`;
        const h = under.get(k) ?? 0;
        if (h) lifts.set(it, h);
        const r = g.itemDefs.get(it.id)?.raise;
        if (r) under.set(k, h + r);
      }
    }

    // Creatures, sorted by interpolated y so southern ones draw on top.
    // Nothing with no hitpoints left is drawn. A player killed lies where
    // they fell for a few seconds before the temple takes them, and what
    // should be on that tile in those seconds is the skull they dropped,
    // not a figure standing about in it. They stay in the list because the
    // view is still theirs and still on that spot.
    const all = [...g.creatures.values()].filter((c) => c.hp > 0).map((c) => ({ c, p: creaturePos(c, now) }));
    // An admin gone /invisible after anybody on their row, so that one
    // sharing a tile with somebody (see ClientCreature.invisible) is laid
    // over them, see-through, rather than hidden under them.
    all.sort((a, b) => a.p.y - b.p.y || Number(!!a.c.invisible) - Number(!!b.c.invisible) || a.c.id - b.c.id);
    // Every light on this floor, once a frame: yours, from where the
    // camera sits, and every other player's from where they are drawn.
    // The cave, the surface and the minimap all darken by this one list.
    // See darkAt.
    //
    // Above ground it is the viewer alone, carrying no light, only their
    // alertness: the sky lights the surface (2026-10-13: "Remove the
    // surface 10"; from 2026-09-22 a player carried a light of viewR up
    // there). See lightOf, and the daylight wash further down.
    //
    // In a hollow open across levels — the orcs' cavern, the yard and the
    // walk round the top of its wall — it is every light in the hollow, on
    // both levels, and each lights both (2026-10-02: "Light like from the
    // spell should apply to other levels when underground"): see
    // lightReaches. `space` is that hollow, the one terrainOf draws.
    const space = me && meZ < 0 ? g.openSpaceAt(me.x, me.y, meZ) : undefined;
    // Your alertness is measured from your tile rather than from where you
    // are drawn mid-step: it is the server's fact about where you stand,
    // and you stand on the tile you are walking to the moment the step
    // starts (2026-10-13: "when moving the alertness brightness bonus
    // doesn't immediately appear for the tile being moved to").
    //
    // With them, the lights of those in view you cannot see (2026-10-20:
    // "if someone isn't visible, their light should still be"): a lamp
    // too far off to make out who carries it lights the cave round them
    // though nobody is drawn in it. See bearerless.
    const shining = [...all, ...bearerless(g.unseenLights)];
    const lights = lightsOn(g.you, meZ, mePos ?? { x: this.camX, y: this.camY }, shining, space, me ? { x: me.x, y: me.y } : undefined, g.night);
    // How dark any point of this floor is under all of them at once, and
    // whether a tile is inside a light at all — two questions, not one,
    // and litAt says why they had to come apart.
    //
    // Both are built here rather than in the scene that wants them
    // because both scenes want them now. The peek pass below was always
    // shared; since the surface got a wash the dark is too.
    //
    // WHAT THEY ARE WORTH IN THE TWO SCENES IS DIFFERENT, and it is worth
    // naming. Underground the light is a lamp's six or none, and your
    // alertness two or five, against a ring of ten, so
    // lit() is a real filter — most of what the server sends is not drawn
    // at all. Above ground the sky lights every level, so lit() is true of
    // everything and dark() nought: up here the wash is the whole of the
    // dark, and there is nothing for a filter to remove.
    //
    // Within your own alertness, on your own floor, a thing is seen
    // whether a light reaches it or not: there is no light of the eyes'
    // own any more, and what is that close is noticed (2026-10-13:
    // "There's no implicit light in 2 tiles. It's whatever your alertness
    // is adds 25%"). It does not light the floor; brightAt gives it its
    // own step. And every level at or above the ground is lit by the sky,
    // nobody carrying a light up there (2026-10-13: "Remove the surface
    // 10").
    const near = (tx: number, ty: number, tz: number) => tz === meZ && alertAt(tx, ty, lights);
    //
    // Asked of no level, each is asked of the floor the scene is seen from,
    // which on a look at another floor is that floor: a floor you are not
    // standing on, a quarter down, and lit only by the sky or by a light
    // that reaches it, as the flat look was (see brightAt).
    //
    // By night the sky lights no level, and the surface asks the lights
    // as the cave does (2026-10-21: "Add a day night cycle above ground").
    const sunlit = (tz: number) => tz >= 0 && !g.night;
    // An Unlight (2026-10-27: "Ungoliant creates 5 squares of unlight that
    // supercedes all other light. Inside it's pure darkness"): black under
    // the sky, a lamp or your own eyes alike, and drawn so for wraith-sight
    // too ("The darkness also appears in the wraith world"). Who stands in
    // it is the server's to say, and whoever it sent is drawn in it, not
    // washed out by it: those are seen by touch or wraith-sight, or by
    // their attack (see unlight.go).
    //
    // Its ground is not hidden (2026-10-27: "ungoliant's darkness doesn't
    // need to remove the visibility of terrain. it just blocks out any
    // light in both seen and unseen world and so makes light vision
    // impossible"): it is drawn as ground no light reaches, the sky's
    // included, and your own dark sight still lifts it as it does any
    // unlit ground. unlitLights is the frame's lights with their light
    // taken out and the viewer's sight left in.
    const unlit = (tx: number, ty: number, tz = eyeZ) => g.unlitAt(Math.round(tx), Math.round(ty), tz);
    // A protection zone counts as lit, by night, under the ground and in
    // an Unlight alike, though it is drawn no brighter than its own lights
    // make it (2026-11-02: "don't actually light the pz, but treat all
    // tiles in pz as lit even if they aren't"): so what stands in one is
    // drawn, as lit says, while dark, bright and tileShade wash it as
    // ever. Unless an admin has the zones off. The server's zoneLit.
    const pzLit = (tx: number, ty: number, tz = eyeZ) => !g.you?.zonesoff && g.protectedAt(Math.round(tx), Math.round(ty), tz);
    const unlitLights = g.unlight.length === 0 ? lights : lights.map((l) => ({ ...l, light: 0 }));
    const dark = (tx: number, ty: number, tz = eyeZ) => (unlit(tx, ty, tz) ? (near(tx, ty, tz) ? 0 : 1) : sunlit(tz) || near(tx, ty, tz) ? 0 : darkAt(tx, ty, lights, tz, space));
    const lit = (tx: number, ty: number, tz = eyeZ) => pzLit(tx, ty, tz) || unlit(tx, ty, tz) || sunlit(tz) || near(tx, ty, tz) || litAt(tx, ty, lights, tz, space);
    // And how bright the floor is drawn, which is a third question since
    // 2026-10-13: see brightAt.
    // Asked of a level, it is that level's, against the one looked at: see
    // brightAt.
    // A ladder's foot is drawn in full light, the light coming down its
    // shaft from the level above, so the tile that goes up stands out on
    // the floor it is on (2026-10-20: "on the lower level, show light on
    // the tile to emphasis that it's the up tile").
    const bright = (tx: number, ty: number, tz = eyeZ) => (unlit(tx, ty, tz) ? brightAt(tx, ty, unlitLights, tz, space, meZ, shownZ, 0) : g.tileOn(tz, tx, ty) === Tile.Ladder ? 1 : brightAt(tx, ty, lights, tz, space, meZ, shownZ, 1 - g.skyDark));
    // The dark drawn over a thing at (x, y), which may be part way between
    // tiles: the floor's own dark there, each tile's middle its own and
    // blended between them as the floor's picture is (layBlended), out of
    // how much of each tile a light or the viewer's dark sight covers
    // (lightCover). So a thing walking, flying or carried across the edge
    // of a light fades as it crosses rather than going dark a tile at once,
    // and a light walking past a thing that stands still brightens it as
    // it comes (2026-10-22: "apply this generally to any dim light
    // boundaries that could move?"). What is drawn at all is still lit's.
    const tileShade = (tx: number, ty: number, tz: number) =>
      sunlit(tz) || unlit(tx, ty, tz) ? 0 : 1 - Math.max(litCoverAt(tx, ty, lights, tz, space), tz === meZ ? alertCoverAt(tx, ty, lights) : 0);
    const shade = (x: number, y: number, tz = eyeZ) => (lights.length === 0 ? 0 : between((tx, ty) => tileShade(tx, ty, tz), x, y));

    // Too small to draw: the old map, in the window. It answers the peek
    // as well, since a floor you are looking at from above is drawn the
    // same way as the one under your feet at this size.
    //
    // The lights above are built before this branch rather than after it
    // because the flat scene wants the dark too, and until 2026-09-22 it
    // was the one scene in the game that had none. That was defensible
    // while the surface had none either: what the flat pass leaves out —
    // names, health bars, damage numbers — it leaves out because at four
    // pixels a tile a name is wider than the building it stands in, and
    // the dark went out of the window with them. A wash is not a name. It
    // is the same flat rectangles the ground itself is drawn as, legible
    // at any size, and leaving it out meant the one view where the whole
    // country fits on screen was also the one view that showed all of it
    // lit. Zooming out was seeing further, which is the opposite of what
    // a sight range is.
    //
    // Building the list costs a pass over the creatures, not over the
    // tiles, so the cheap path stays cheap.
    const px = this.tilePx();
    if (px < FLAT_BELOW) {
      this.drawFlat(g, toScreen, shownZ, x0, x1, y0, y1, bright);
      if (shownZ >= 0) this.drawPlaceNames(g, toScreen, meZ);
      this.drawQuestMarks(g, toScreen);
      this.drawTracks(g, toScreen, meZ, now);
      ctx.restore();
      return;
    }
    // Where you are standing, on a look at another floor: a ring on the
    // tile of that floor over or under your feet, in the middle of the
    // window, so the country on screen can be read against yourself.
    const markYou = () => {
      if (!peeking || !me) return;
      const { sx, sy } = toScreen(me.x, me.y);
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      ctx.strokeRect(Math.round(sx) + 1, Math.round(sy) + 1, TILE - 2, TILE - 2);
    };
    // The debug rings are round creatures on your own floor, so they are
    // put where that floor is drawn.
    const onYourFloor = (tx: number, ty: number) => toScreen(tx, ty, meZ);

    // The level above is drawn over everything on the ground, so whoever
    // is up there is seen from below and the other way round. A viewer on
    // the ground has some roofs lifted so they can see inside: the one
    // over their own head, and any building they can see into from where
    // they stand — its doorway, or beside any open floor under its roof.
    //
    // Which levels are drawn and what stands on each of them is terrainOf's
    // to say, since the click has to agree with the drawing about both.
    const { levels, shown: inMap, lifted, sig: terrainSig } = terrainOf(g, me, eyeZ, now);
    // Past the world's edge, above ground, the edge goes on: every tile
    // off the map is drawn as the nearest one on it (2026-10-17: "The
    // edges of the map should show more of whatever the edge tile is
    // beyond what is visible"), so the sea runs on as sea and a range on
    // as rock, rather than stopping at a line of black. Only drawn: no
    // one walks there, nothing lies there, and a click is asked of the
    // map's own tiles (see tileFromEvent). Past the east edge it is desert
    // on the ground and nothing above it: see groundPast.
    const shown: Shown =
      eyeZ < 0
        ? inMap
        : (z, x, y) => {
            const t = inMap(z, edgeX(g, x), edgeY(g, y));
            if (x < g.mapW || t === undefined) return t;
            return z === 0 ? Tile.Sand : undefined;
          };
    // Where a figure is drawn standing, for what hangs over one (see
    // drawOverlays): its level, or the incline of a ramp it is walking.
    const standing = (x: number, y: number, z: number) => standAt(shown, x, y, z, g);
    // Through roofAt (in lifted) rather than an index worked out here: this
    // is asked of tiles right across the window, and a window zoomed out is
    // wider than the world, so the sum wraps into the next row along. See
    // tileIndex in game.ts, and the rooftop that was floating in the sea.
    const upHidden = (x: number, y: number, z?: number) => lifted(z ?? 0, x, y);
    // A floor's picture in the close view, which is its tile's own but for
    // a ladder's two ends (2026-09-28: "render the ladder oblique"; see
    // ladderAt). The foot is the floor it stands on, the ladder being stood
    // up on it with everything else that has height; the head, with the
    // foot in view, is the hatch round an opening the ladder shows through,
    // and without it — looked down into, from a level whose level below is
    // not drawn — the shaft with the ladder going down it, as is a hole
    // with a ladder under it.
    const floorSprite = (t: number, x: number, y: number, z: number): HTMLCanvasElement => {
      if (t === Tile.Ladder) {
        const under = underfoot(g, z, x, y);
        return tileSprite(under, variantAt(g, under, x, y), frame);
      }
      if (t === Tile.LadderTop) return shown(z - 1, x, y) === Tile.Ladder ? hatchSprite() : ladderShaftSprite(false);
      if (t === Tile.Hole && g.tileOn(z - 1, x, y) === Tile.Ladder) return ladderShaftSprite(true);
      // The head of a stair whose foot is not drawn — the castle's, in the
      // temple floor, over the undercroft — is the stairwell, with the
      // flight going down it (2026-09-28: "render stairs oblique, the one
      // in the castle for example"; see stairwellPicture). With its foot in
      // view it draws nothing: the flight is its foot's (see inclineAt).
      if (t === Tile.StairTop && shown(z - 1, x, y) !== Tile.Stair) return stairwellPicture();
      // A pit prop's floor is the cave floor it stands on, the prop being
      // stood up on it (2026-09-28: "render the pit prop oblique"; see
      // propFacets); its flat picture, timbers and all, is for the flat
      // views.
      if (t === Tile.PitProp) return tileSprite(Tile.Cave, variantAt(g, Tile.Cave, x, y), frame);
      return tileSprite(t as Tile, variantAt(g, t as Tile, x, y), frame);
    };

    // THE OBLIQUE VIEW. Asked for on 2026-09-27: "implement the oblique view
    // with south and east faces visible". Height is drawn: everything a
    // storey above the viewer's floor is drawn STOREY pixels up and to the
    // left of where it lies, and the blocks that stand up out of their level
    // (see isRaised: the walls, the rock, the palisade, the cave's walls)
    // show their south and east faces in the gap that leaves. The grid, the
    // sprites and every rule of the world are what they were; oblique.ts has
    // the whole argument.
    //
    // WHAT IS FLAT AND WHAT IS NOT. The floor of the lowest level drawn —
    // the ground, or the cave you are in — and whatever lies flat on it are
    // painted first, straight down, the way the whole world used to be: none
    // of it can ever be in front of anything, because nothing is lower. The
    // dark goes over that at once. Everything else is a job, painted back to
    // front (byDepth): each block, each floor of a level above the lowest,
    // each tree, whatever lies on a raised floor, and every creature. Each
    // lays its own share of the dark as it goes on, since a wash laid over
    // the finished frame would darken the front of a wall by the ground
    // behind it.
    //
    // FROM BELOW, A LEVEL ABOVE IS FADED, as it always was: the world you
    // are walking in reads through what hangs over it, and up there
    // yourself, or above it, it is solid.
    const base = levels[0];
    const levelAlpha = (z: number) => (eyeZ >= z ? 1 : 0.8);
    const jobs: Job[] = [];
    const later = (key: number, z: number, sub: number, draw: () => void) => {
      jobs.push({ key, z, sub, draw });
    };
    // Doors that stand in a wall rather than lying on the floor (2026-09-27:
    // "fix the door so it fits the oblique style. we might need a east/west
    // and north/south variants of doors"). The item passes set them aside
    // here and raise stands them up with the walls: see doorFacing.
    const standingDoors: { id: number; x: number; y: number; z: number; side: "s" | "e" }[] = [];
    const standingChests: { x: number; y: number; z: number; kind: ChestKind }[] = [];
    const standingTrees: { spr: HTMLCanvasElement; x: number; y: number; z: number }[] = [];
    // Site 6 (2026-10-16): the falls of Henneth Annûn, stood up as a
    // curtain against the cliff; see WATERFALL_ITEM.
    const standingFalls: { spr: HTMLCanvasElement; x: number; y: number; z: number }[] = [];
    const standsUp = (it: { id: number; x: number; y: number; color?: string }, z: number): boolean => {
      // A chest stands up too, as a box on its tile (2026-09-28: "render
      // chests oblique"): see chestFacets. raise draws it with the rest.
      // And a depot box (2026-10-01: "render the depot chests oblique"),
      // since 2026-10-10 a square-edged chest of its own ("make depot
      // boxes a distinct square-edged chest shape").
      if (isStandingChest(it.id)) {
        standingChests.push({ x: it.x, y: it.y, z, kind: chestKindOf(it.id) });
        return true;
      }
      // Site 8 (2026-10-16): the Entwives' orchard trees stand up as trees.
      if (STANDING_TREE_ITEMS.has(it.id)) {
        standingTrees.push({ spr: groundSprite(it.id, it.color, g.defOf(it)?.make), x: it.x, y: it.y, z });
        return true;
      }
      // Site 6 (2026-10-16): the falls of Henneth Annûn stand up as a curtain.
      if (it.id === WATERFALL_ITEM) {
        standingFalls.push({ spr: groundSprite(it.id, it.color, g.defOf(it)?.make), x: it.x, y: it.y, z });
        return true;
      }
      if (!g.itemDefs.get(it.id)?.door) return false;
      const side = doorFacing(shown, z, it.x, it.y);
      if (side === null) return false;
      standingDoors.push({ id: it.id, x: it.x, y: it.y, z, side });
      return true;
    };
    // Where the doors are, before anything is drawn: a thin wall runs its
    // arm into a door standing in its line, and rock takes a door standing
    // in it for more of itself (see wallJoins and raisedPiece), so the wall
    // is whole over the doorway. Undefined when there are none in the
    // window, which is nearly always, so the walls ask nothing.
    const doorKeys = new Set<string>();
    // And where the chests are, and which kind each is, for whoever stands
    // behind one: see tallFront. Each asks chestCoversNorth by its kind:
    // the rounded chest's, or since 2026-10-10 ("make depot boxes a
    // distinct square-edged chest shape") the square depot box's.
    const chestKeys = new Map<string, ChestKind>();
    for (const it of items) {
      if (it.x < x0 - 1 || it.x > x1 + 1 || it.y < y0 - 1 || it.y > y1 + 1) continue;
      if (g.itemDefs.get(it.id)?.door) doorKeys.add(`${it.z ?? 0},${it.x},${it.y}`);
      if (isStandingChest(it.id)) chestKeys.set(`${it.z ?? 0},${it.x},${it.y}`, chestKindOf(it.id));
    }
    const doorAt: DoorAt | undefined = doorKeys.size ? (z, x, y) => doorKeys.has(`${z},${x},${y}`) : undefined;
    const doorSig = doorKeys.size ? [...doorKeys].sort().join(";") : "";
    // What is worked out of the terrain, kept while the map, what of it is
    // shown and the doors in view are what they were: see TileMemo. The
    // depths of the hanging floors are kept by the window as well, since
    // a floor's search for the rest of itself stops at the window's edge.
    const tiles = this.tileMemo.begin(`${g.terrainEra}|${terrainSig}|${doorSig}`);
    const hangs = this.hangMemo.begin(`${g.terrainEra}|${terrainSig}|${doorSig}|${x0},${x1},${y0},${y1}`);
    this.doorsSeen = doorAt;
    // A floor above the lowest level with no block under it and nothing
    // to lean on (see proppedBy): a roof over a room, a walk over a hall.
    // It is drawn as a slab, SLAB storeys thick at its open edges, and all
    // of one piece goes on after everything that stands under it — every
    // tile of it takes the depth of its nearest tile to the camera, or a
    // head under it pokes up through the tiles of it behind that head.
    const SLAB = 0.25;
    const propped = (z: number, x: number, y: number) => (z > base && !isRaised(shown(z - 1, x, y)) ? proppedBy(shown, z, x, y) : undefined);
    // Nor is the hatch at the head of a ladder a slab: the ladder stands up
    // under it (2026-09-28: see ladderAt).
    const overhang = (z: number, x: number, y: number) =>
      tiles.of(MEMO_OVERHANG, x, y, z, () => z > base && !isRaised(shown(z - 1, x, y)) && propped(z, x, y) === undefined && ladderAt(shown, z, x, y)?.foot !== z - 1);
    // The head of a ramp or a slope is not a floor to stand things on but
    // the top edge of the incline its foot draws (2026-09-28: see
    // inclineAt), so it is neither a slab nor a floor to be promoted.
    const floorAt = (z: number, x: number, y: number) =>
      tiles.of(MEMO_FLOOR, x, y, z, () => {
        const t = shown(z, x, y);
        return t !== undefined && !isRaised(t) && inclineAt(shown, z, x, y)?.foot !== z - 1;
      });
    // A roof a storey over a slab is all of one piece with the slab under
    // it, the tiles of it over the walls included, so it goes on after the
    // slab it covers: the Prancing Pony's thatch over the floor of its room
    // upstairs (2026-10-08), which the room's floor, all of it taking the
    // depth of its nearest tile, otherwise painted over at the thatch's
    // edges. Only a roof: a mountain's shelves sit on its rock, and each
    // tile of them keeps its own depth.
    const slabbed = (z: number, x: number, y: number) =>
      tiles.of(MEMO_SLABBED, x, y, z, () => overhang(z, x, y) || (z - 1 > base && isRoof(shown(z, x, y)) && isRaised(shown(z - 1, x, y))));
    const hangKeys = hangs.table<number>(0);
    const hangKey = (z: number, x: number, y: number): number => {
      const k = tileKey(x, y, z);
      const known = hangKeys.get(k);
      if (known !== undefined) return known;
      const seen: [number, number][] = [];
      const todo: [number, number][] = [[x, y]];
      const mark = new Set([k]);
      let deepest = -Infinity;
      while (todo.length) {
        const [a, b] = todo.pop()!;
        seen.push([a, b]);
        deepest = Math.max(deepest, a + b);
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const nx = a + dx, ny = b + dy, nk = tileKey(nx, ny, z);
          // Bounded to a margin round the window: a piece bigger than
          // that is drawn a piece at a time, which only matters for a
          // head standing under its far end.
          if (nx < x0 - 4 || nx > x1 + 4 || ny < y0 - 4 || ny > y1 + 4) continue;
          if (mark.has(nk) || !floorAt(z, nx, ny) || !slabbed(z, nx, ny)) continue;
          mark.add(nk);
          todo.push([nx, ny]);
        }
      }
      for (const [a, b] of seen) hangKeys.set(tileKey(a, b, z), deepest);
      return deepest;
    };
    // The depth anything standing on (x, y) at level z starts from: the
    // tile's own, or its slab's.
    const standKey = (x: number, y: number, z: number) => (floorAt(z, x, y) && overhang(z, x, y) ? hangKey(z, x, y) + 0.9 : x + y);
    // How high the tallest thing drawn at (x, y) stands, in storeys.
    const colTop = (x: number, y: number) => {
      let top = -Infinity;
      for (const z of levels) {
        const t = shown(z, x, y);
        if (t !== undefined) top = Math.max(top, isRaised(t) ? z + 1 : z);
      }
      return top;
    };
    // Whether something of a level above z stands just south or just east
    // of (x, y), where it covers a figure standing there. A wall or a crag
    // of the figure's own level does not count: nothing of a level covers
    // what is on that level (2026-09-27: "dungeon/cave/mountain/wall tiles
    // on the same level shouldn't be occluding tiles on the same level"),
    // so the figure goes on after it.
    const tallFront = (x: number, y: number, z: number) => {
      for (const [dx, dy] of [[1, 0], [0, 1]]) {
        if (colTop(x + dx, y + dy) > z + 1.01 || shown(z + 1, x + dx, y + dy) !== undefined || shown(z, x + dx, y + dy) === Tile.Tree || shown(z, x + dx, y + dy) === Tile.Hedge || shown(z, x + dx, y + dy) === Tile.PartyTree) return true;
        // Or something standing up on the tile just south that reaches
        // over this one on the glass (see coversNorth). A ladder did while
        // it stood at the north edge of its tile; with its foot in the
        // middle (2026-09-28: "since you can't stand on a ladder tile,
        // center the foot of ladder in the tile so it's clear why we can't
        // fit next to it") it does not.
        if (dy === 1 && coversNorth(shown(z, x, y + 1))) return true;
        // Or a chest standing on the tile just south, whose lid reaches
        // over this one (2026-09-28: "render chests oblique"): asked of
        // its own kind, the rounded chest or the square depot box.
        const southChest = dy === 1 ? chestKeys.get(`${z},${x},${y + 1}`) : undefined;
        if (southChest !== undefined && chestCoversNorth(southChest)) return true;
        // Or just east, reaching over this one: a flight of stairs leans
        // up and to the left over the tile west of it (2026-09-28: "render
        // stairs oblique, the one in the castle for example"), which counts
        // where its head is not drawn to say so, under the ground.
        if (dx === 1 && coversWest(shown(z, x + 1, y))) return true;
      }
      return false;
    };
    // What lies flat on a raised floor is drawn with that floor, before
    // the dark goes over it: blood, keyed by tile and level.
    const onFloor = new Map<string, (() => void)[]>();
    // Level z's flat things are drawn now if z is the lowest level, and
    // with their floor if it is not.
    const flat = (z: number, x: number, y: number, draw: () => void) => {
      if (z === base) return draw();
      const k = `${x},${y},${z}`;
      const list = onFloor.get(k);
      if (list) list.push(draw);
      else onFloor.set(k, [draw]);
    };

    // Blood, under everything else on its level. Pools are drawn per tile
    // rather than per item: colours on one tile mix in proportion to how
    // much of each was spilled, so a rat bled dry beside a troll reads as
    // one muddied stain. Spatter goes over the pools and is drawn per stain
    // rather than mixed — flecks from two creatures land beside each other,
    // they do not run together. Both belong to the level they were spilt
    // on: a rooftop fight stains the rooftop, a cave fight the cave floor.
    const drawStains = (z: number) => {
      const pools = new Map<string, { r: number; g: number; b: number; n: number; x: number; y: number }>();
      for (const it of items) {
        if ((it.z ?? 0) !== z) continue;
        if (upHidden(it.x, it.y, it.z)) continue;
        const rgb = BLOOD_RGB[it.id];
        if (!rgb) continue;
        if (it.x < x0 || it.x > x1 || it.y < y0 || it.y > y1) continue;
        const key = `${it.x},${it.y}`;
        const acc = pools.get(key) ?? { r: 0, g: 0, b: 0, n: 0, x: it.x, y: it.y };
        acc.r += rgb[0] * it.count;
        acc.g += rgb[1] * it.count;
        acc.b += rgb[2] * it.count;
        acc.n += it.count;
        pools.set(key, acc);
      }
      for (const p of pools.values()) {
        const { sx, sy } = toScreen(p.x, p.y, z);
        const mixed: [number, number, number] = [
          Math.round(p.r / p.n),
          Math.round(p.g / p.n),
          Math.round(p.b / p.n),
        ];
        flat(z, p.x, p.y, () => ctx.drawImage(bloodSprite(mixed, p.n), Math.round(sx), Math.round(sy)));
      }
      for (const it of items) {
        if ((it.z ?? 0) !== z) continue;
        if (upHidden(it.x, it.y, it.z)) continue;
        const rgb = SPATTER_RGB[it.id];
        if (!rgb) continue;
        if (it.x < x0 || it.x > x1 || it.y < y0 || it.y > y1) continue;
        const { sx, sy } = toScreen(it.x, it.y, z);
        flat(z, it.x, it.y, () => ctx.drawImage(spatterSprite(rgb, it.count), Math.round(sx), Math.round(sy)));
      }
    };

    // Whoever is standing where a passage lets out is seen from its mouth a
    // storey away: at the lip of a hole, the player on the landing below is
    // down in the opening looking up. They are drawn in the mouth's tile and
    // clipped to it, so only as much of them as the opening frames shows,
    // and then the mouth itself is laid back over them half-transparent —
    // the hole is above them, and they are in it. A glimpse only: levels do
    // not interact, so nothing seen this way can be reached or hit.
    const drawPeeks = (z: number) => {
      for (const { c } of all) {
        if (!c.peek) continue;
        const t = g.tileOn(z, c.peek.x, c.peek.y);
        if (t === undefined) continue;
        if (c.peek.x < x0 || c.peek.x > x1 || c.peek.y < y0 || c.peek.y > y1) continue;
        // A glimpse is still a sight of somebody, so underground it obeys
        // the light like every other figure: the mouth of the passage has
        // to be lit for whoever is standing under it to show in it. Their
        // own lamp is no help here — they are a storey down and their
        // light lights that storey — so an unlit hole is an empty hole,
        // which is also what keeps the dim from quietly showing a face in
        // one. Above ground there is no light and no dark and every hole
        // is looked into as it always was.
        if (!sunlit(meZ) && !lit(c.peek.x, c.peek.y)) continue;
        const { sx, sy } = toScreen(c.peek.x, c.peek.y, z);
        const X = Math.round(sx), Y = Math.round(sy);
        const still = c.riding
          ? riderSprite(c.outfit, c.lastDir, 0, c, c.color, c.level, c.locked, c.admin, c.race, c.hair, c.steed)
          : creatureSprite(c.outfit, c.lastDir, 0, c, c.color, c.level, c.locked, c.admin, c.race, c.hair);
        const faded = seeThrough(still, c.fade);
        const spr = c.invisible ? ghostly(faded) : faded;
        ctx.save();
        ctx.beginPath();
        ctx.rect(X, Y, TILE, TILE);
        ctx.clip();
        ctx.globalAlpha = 0.85;
        // Head at the top of the opening rather than feet at the bottom of
        // it: what the mouth frames is the top of someone standing a storey
        // down, so the legs are the part the rim cuts off.
        ctx.drawImage(spr, X - (spr.width - TILE) / 2, Y);
        ctx.restore();
        ctx.globalAlpha = 0.6;
        // The stairwell as the ground pass drew it (2026-09-28: "render
        // stairs oblique, the one in the castle for example").
        const mouth = t === Tile.StairTop ? floorSprite(t, c.peek.x, c.peek.y, z) : tileSprite(t as Tile, (c.peek.x * 7 + c.peek.y * 13) & 3, frame, underfoot(g, z, c.peek.x, c.peek.y));
        ctx.drawImage(mouth, X, Y);
        ctx.globalAlpha = 1;
      }
    };

    // The tile under the pointer, outlined on whatever surface of it the
    // pointer is on: see tileFromEvent. On the lowest floor it is drawn
    // with the ground, and anywhere higher with the block or floor it
    // outlines, so what stands in front of that still stands in front.
    const drawHover = () => {
      const hov = g.hoverTile;
      if (!hov) return;
      const h = hov.h ?? eyeZ;
      // A ramp or a slope is outlined on its incline, with it: see pickAt,
      // which gives the foot's level for anywhere on the incline.
      const ramp = inclineAt(shown, h, hov.x, hov.y, g);
      if (ramp && ramp.foot === h) {
        later(hov.x + hov.y, h, 0.3, () => {
          const { sx, sy } = toScreen(hov.x, hov.y, h);
          const X = Math.round(sx) + 0.5, Y = Math.round(sy) + 0.5;
          // A stair's round its steps (2026-09-28: "render stairs oblique,
          // the one in the castle for example").
          const top = ramp.stair ? stairFacets().top : inclineFacets(ramp.dir).top;
          ctx.beginPath();
          top.forEach(([px, py], i) => (i ? ctx.lineTo(X + px, Y + py) : ctx.moveTo(X + px, Y + py)));
          ctx.closePath();
          ctx.strokeStyle = "rgba(255,255,255,0.35)";
          ctx.lineWidth = 1;
          ctx.stroke();
        });
        return;
      }
      const outline = () => {
        const { sx, sy } = toScreen(hov.x, hov.y, h);
        ctx.strokeStyle = "rgba(255,255,255,0.35)";
        ctx.lineWidth = 1;
        ctx.strokeRect(Math.round(sx) + 0.5, Math.round(sy) + 0.5, TILE - 1, TILE - 1);
      };
      // Over everything, last of all, or with the ground: see outlineOnTop.
      if (outlineOnTop(shown, base, hov.x, hov.y, h, !!hov.through)) later(Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, 0, outline);
      else outline();
    };

    // Whatever lies on a raised floor, standing up on it and painted with
    // everything else in depth order, darkened by the dark where it lies.
    // The lowest floor's things are drawn flat with the ground, as before.
    const standItem = (x: number, y: number, z: number, wash: (x: number, y: number, z: number) => number, put: () => Placed) => {
      later(standKey(x, y, z) + 0.5, z, 1, () => {
        ctx.globalAlpha = levelAlpha(z);
        const { spr, X, Y } = put();
        const a = wash(x, y, z);
        if (a > 0) {
          ctx.globalAlpha = a * levelAlpha(z);
          ctx.drawImage(shadowOf(spr), X, Y);
        }
        ctx.globalAlpha = 1;
      });
    };

    // A tile's picture, for what the oblique view paints over the ground.
    // Only a ladder's picture depends on the floor round it (see
    // tileSprite), and working that floor out is eight lookups and a map
    // for every block in the window every frame, so it is asked only of a
    // ladder.
    const spriteOf = (t: number, x: number, y: number, z: number) =>
      tileSprite(t as Tile, variantAt(g, t as Tile, x, y), frame, t === Tile.Ladder ? underfoot(g, z, x, y) : undefined);

    // The floor under a wall or a rock, where it shows (see
    // groundRegions): each part of the square is the same part of the
    // picture of the floor beside it on that side, so it reads as that
    // floor running on up to the wall. `a` is the dark over it, for a
    // wall above the lowest level, which lays its floor as it is drawn.
    const layGround = (z: number, x: number, y: number, GX: number, GY: number, a = 0) => {
      for (const r of tiles.of(MEMO_REGIONS, x, y, z, () => groundRegions(shown, z, x, y, doorAt))) {
        let fx = x, fy = y;
        let t: number = z < 0 ? Tile.Cave : Tile.Dirt;
        if (r.from) {
          fx += r.from[0];
          fy += r.from[1];
          t = shown(z, fx, fy)!;
          if (t === Tile.Tree || t === Tile.Hedge || t === Tile.PartyTree) t = Tile.Grass;
          else if (t === Tile.Ship) t = Tile.Water;
        } else if (z > base) {
          // Up on a battlement with no floor of its own level round it:
          // what shows is the top of the rock it stands on.
          continue;
        }
        const spr = spriteOf(t, fx, fy, z);
        // Rock behind a wall is in the shadow of the two, as dark as the
        // face of the rock above it.
        const shade = isRaised(t) ? 1 - (1 - a) * (1 - FACE_SHADE.e) : a;
        this.drawTileImage(shade > 0 ? dimmed(spr, shade) : spr, GX, GY, r);
      }
    };

    // How much black lies over a tile for what is painted over the floor's
    // own wash, with a scene's `known` and ceiling: the same rule the
    // floor's wash is laid by (washAt), asked of the same dark.
    const washOf = (known: (x: number, y: number) => boolean) => (x: number, y: number, z: number) => washAt(bright(x, y, z), known(x, y));

    // Everything with height, back to front, then the see-through
    // outlines. `people` is who is drawn and `wash` how much dark lies on
    // a tile, which are the two things the cave and the surface decide
    // differently.
    const raise = (people: typeof all, wash: (x: number, y: number, z: number) => number) => {
      // The dark of a tile and the eight round it, for blending the dark
      // over what stands up off the floor across the edge of a light as the
      // floor's own is (2026-10-21: "the soft edges to light doesn't work
      // above ground level?"): see softDimmed. `soft` is a picture drawn at
      // X, Y for the tile x, y on level z, with that dark laid over it.
      const washRound = (x: number, y: number, z: number) => {
        const r: number[] = [];
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) r.push(wash(x + dx, y + dy, z));
        return r;
      };
      const soft = (spr: HTMLCanvasElement, x: number, y: number, z: number, X: number, Y: number) => {
        const { sx, sy } = toScreen(x, y, z);
        return softDimmed(spr, washRound(x, y, z), Math.round(sx) - X, Math.round(sy) - Y);
      };
      const targetId = g.you?.target ?? 0;
      const foeId = g.you?.foe ?? 0;
      // A floor of the same height as a creature, just south or east of
      // it, is painted before it when something taller stands beside that
      // floor: the creature goes on before the tall thing, and the floor
      // beside it would otherwise go on after and cover its feet.
      const promote = new Set<string>();
      // Figures hidden behind a wall or a crag are drawn again, as an
      // outline, once the scene is done: every player, and whatever you
      // are fighting. Each is sampled at a few points down its middle,
      // and every block or raised floor painted after it strikes out the
      // points it covers.
      //
      // A roof outlines whoever it hides, players or not (2026-10-07:
      // "creatures should be showing outlines when under a roof like this,
      // not a partially visible sprite"): every figure is sampled, and
      // what a roof strikes out is counted apart in roofHit, so a monster
      // or one of the world's people behind the eaves of a building is
      // outlined over the roof as a player behind a wall is. `always` is
      // the old few, outlined behind anything at all.
      //
      // A great ring's wearer is outlined in plain sight too, hidden or
      // not (2026-10-27: "always show an outline, like when a player is
      // behind something like a roof, but the outline appears even when
      // they're in direct view"): `ringed`.
      const ghosts: { spr: HTMLCanvasElement; L: number; T: number; color: string; pts: [number, number][]; hit: number; roofHit: number; always: boolean; ringed: boolean }[] = [];
      // And every figure standing under a roof drawn over it, outlined
      // whole over the roof whoever it is and not painted at all: see
      // underRoof.
      const roofed: { spr: HTMLCanvasElement; L: number; T: number; color: string }[] = [];
      // What was just painted over the box L..R, T..B, as a test of a
      // point: it strikes out the points of every figure it covers.
      const hide = (L: number, T: number, R: number, B: number, over: (px: number, py: number) => boolean, roof = false) => {
        for (const gh of ghosts) {
          if (gh.L > R || gh.L + gh.spr.width < L || gh.T > B || gh.T + gh.spr.height < T) continue;
          gh.pts = gh.pts.filter(([px, py]) => {
            const o = over(px, py);
            if (o) gh.hit++;
            if (o && roof) gh.roofHit++;
            return !o;
          });
        }
      };
      // A raised floor with its faces k storeys deep.
      // `roof` says it is a roof, which outlines whoever it hides.
      const cover = (X: number, Y: number, s: number, e: number, roof = false) =>
        hide(X, Y, X + TILE + STOREY, Y + TILE + STOREY, (px, py) => (px >= X && px < X + TILE && py >= Y && py < Y + TILE) || (s > 0 && inFace(px, py, X, Y, "s", s)) || (e > 0 && inFace(px, py, X, Y, "e", e)), roof);
      // A wall, a rock or a door's lintel, by the very shapes it painted:
      // since the thin walls (2026-09-27) that is only ever its own square
      // and its joined neighbours', so it rarely hides anybody at all.
      const coverPiece = (GX: number, GY: number, piece: Piece) =>
        hide(GX - PIECE_PAD, GY - PIECE_PAD, GX + TILE + PIECE_PAD, GY + TILE + PIECE_PAD, (px, py) => piece.facets.some((f) => inPoly(px - GX, py - GY, f.pts)));

      // A ramp or a slope, as its incline (see inclineOf): the surface, and
      // under it the faces of the wedge in the stuff of the ledge it is cut
      // into — the rock uphill of it. It goes on at its foot's depth, after
      // the ledge behind it that the surface leans out over and before
      // anything further south or east, and hides whoever is behind it by
      // the shapes it painted, like a rock.
      //
      // A stair is drawn so too, as a flight of steps (2026-09-28: "render
      // stairs oblique, the one in the castle for example"): see
      // stairFacets. The side of the flight is the ledge's, like a ramp's
      // wedge, and the steps are dressed stone.
      const incline = (x: number, y: number, z: number, t: number, dir: number) => {
        const [ux, uy] = [[0, -1], [1, 0], [0, 1], [-1, 0]][dir];
        const up = shown(z, x + ux, y + uy);
        const ledge = up !== undefined && isRaised(up) ? up : Tile.Rock;
        const stair = t === Tile.Stair;
        const ledgeSpr = spriteOf(ledge, x + ux, y + uy, z);
        const pic = stair ? stairPicture(ledgeSpr) : inclineOf(inclineSprite(t as Tile), ledgeSpr, dir);
        const { sx, sy } = toScreen(x, y, z);
        const GX = Math.round(sx), GY = Math.round(sy);
        // What it paints, as the outline of its surface and the convex
        // shapes that hide whoever is behind it: a flight's outline is
        // stepped, and its facets are the shapes.
        const inc = stair ? stairFacets() : inclineFacets(dir);
        const polys = stair ? stairFacets().facets.map((f) => f.pts) : [inc.top, ...inclineFacets(dir).faces.map((f) => f.pts)];
        ctx.globalAlpha = levelAlpha(z);
        ctx.drawImage(soft(pic, x, y, z, GX - PIECE_PAD, GY - PIECE_PAD), GX - PIECE_PAD, GY - PIECE_PAD);
        if (g.protectedAt(x, y, z)) {
          ctx.beginPath();
          inc.top.forEach(([px, py], i) => (i ? ctx.lineTo(GX + px, GY + py) : ctx.moveTo(GX + px, GY + py)));
          ctx.fillStyle = PZ_TINT;
          ctx.fill();
        }
        ctx.globalAlpha = 1;
        hide(GX - PIECE_PAD, GY - PIECE_PAD, GX + TILE, GY + TILE, (px, py) => polys.some((pts) => inPoly(px - GX, py - GY, pts)));
      };

      // A ladder, stood up on the face ladderFace gives it, a storey tall,
      // darkened by the dark where it stands and hiding whoever is behind it
      // by its outline.
      const standLadder = (x: number, y: number, z: number) => {
        const { sx, sy } = toScreen(x, y, z);
        const GX = Math.round(sx), GY = Math.round(sy);
        const face = ladderFace();
        const spr = ladderSprite(LADDER_W);
        // The light coming down the shaft from the hatch above: a soft
        // column from the hatch, a storey up, to the floor, and a pool of it
        // on the floor, so the tile that goes up reads as the way up
        // (2026-10-20: "on the lower level, show light on the tile to
        // emphasis that it's the up tile").
        ctx.save();
              ctx.globalAlpha = levelAlpha(z);
        const beam = ctx.createLinearGradient(0, GY - STOREY, 0, GY + TILE);
        beam.addColorStop(0, "rgba(255,236,190,0)");
        beam.addColorStop(1, "rgba(255,236,190,0.16)");
        ctx.fillStyle = beam;
        ctx.beginPath();
        ctx.moveTo(GX - STOREY, GY - STOREY);
        ctx.lineTo(GX + TILE - STOREY, GY - STOREY);
        ctx.lineTo(GX + TILE, GY);
        ctx.lineTo(GX + TILE, GY + TILE);
        ctx.lineTo(GX, GY + TILE);
        ctx.lineTo(GX - STOREY, GY + TILE - STOREY);
        ctx.closePath();
        ctx.fill();
        const pool = ctx.createRadialGradient(GX + TILE / 2, GY + TILE / 2, 0, GX + TILE / 2, GY + TILE / 2, TILE * 0.7);
        pool.addColorStop(0, "rgba(255,236,190,0.35)");
        pool.addColorStop(1, "rgba(255,236,190,0)");
        ctx.fillStyle = pool;
        ctx.fillRect(GX - TILE / 4, GY - TILE / 4, TILE * 1.5, TILE * 1.5);
        ctx.restore();
        ctx.save();
        // Its shadow first, on whatever is behind it: the light comes from
        // the south-west, so a couple of pixels to the east.
        ctx.globalAlpha = 0.35 * levelAlpha(z);
        ctx.transform(...faceTransform("s", GX + face.at[0] + 2, GY + face.at[1]));
        ctx.drawImage(shadowOf(spr), 0, 0);
        ctx.restore();
        ctx.globalAlpha = levelAlpha(z);
        ctx.save();
        ctx.transform(...faceTransform("s", GX + face.at[0], GY + face.at[1]));
        ctx.drawImage(dimmed(spr, wash(x, y, z)), 0, 0);
        ctx.restore();
        ctx.globalAlpha = 1;
        hide(GX - STOREY, GY - STOREY, GX + TILE, GY + TILE, (px, py) => inPoly(px - GX, py - GY, face.pts));
      };

      // A pit prop, stood up at the north side of its tile as propFacets
      // gives it (2026-09-28: "render the pit prop oblique"): its timbers'
      // faces, darkened by the dark where it stands, hiding whoever is
      // behind it by the shapes it painted.
      const propFaces = propFacets().map((f) => f.pts);
      const standProp = (x: number, y: number, z: number) => {
        const { sx, sy } = toScreen(x, y, z);
        const GX = Math.round(sx), GY = Math.round(sy);
        ctx.globalAlpha = levelAlpha(z);
        ctx.drawImage(soft(propPicture(), x, y, z, GX - PIECE_PAD, GY - PIECE_PAD), GX - PIECE_PAD, GY - PIECE_PAD);
        ctx.globalAlpha = 1;
        hide(GX - STOREY, GY - STOREY, GX + TILE, GY + TILE, (px, py) => propFaces.some((pts) => inPoly(px - GX, py - GY, pts)));
      };

      // A chest, stood up on its tile as chestFacets gives it (2026-09-28:
      // "render chests oblique"): its front, its east end, its lid and its
      // ironwork, darkened by the dark where it stands, hiding whoever is
      // behind it by the shapes it painted: its own kind's, a wooden
      // chest with a rounded lid (2026-10-01) or, for a depot box since
      // 2026-10-10 ("make depot boxes a distinct square-edged chest
      // shape"), a square-edged box with a flat lid.
      const chestFaces: Record<ChestKind, Pt[][]> = {
        chest: chestFacets("chest").map((f) => f.pts),
        depot: chestFacets("depot").map((f) => f.pts),
      };
      const standChest = (x: number, y: number, z: number, kind: ChestKind) => {
        const { sx, sy } = toScreen(x, y, z);
        const GX = Math.round(sx), GY = Math.round(sy);
        ctx.globalAlpha = levelAlpha(z);
        ctx.drawImage(soft(chestPicture(kind), x, y, z, GX - PIECE_PAD, GY - PIECE_PAD), GX - PIECE_PAD, GY - PIECE_PAD);
        ctx.globalAlpha = 1;
        hide(GX - STOREY, GY - STOREY, GX + TILE, GY + TILE, (px, py) => chestFaces[kind].some((pts) => inPoly(px - GX, py - GY, pts)));
      };

      // The players painted so far this frame, to be painted again over a
      // creature that goes on after them: see paint below.
      const drawnPlayers: { z: number; box: { L: number; T: number; R: number; B: number }; paint: (over: { spr: HTMLCanvasElement; L: number; T: number }) => void }[] = [];
      for (const { c, p } of people) {
        const cz = c.z ?? 0;
        // The more south-easterly of the two tiles a walker is between,
        // which is the one whose depth it has to be painted at to go on
        // over both of them.
        const tx = Math.ceil(p.x), ty = Math.ceil(p.y);
        const tall = tallFront(tx, ty, cz);
        if (tall && cz > base) {
          for (const [dx, dy] of [[1, 0], [0, 1]]) {
            if (floorAt(cz, tx + dx, ty + dy) && !overhang(cz, tx + dx, ty + dy)) promote.add(`${tx + dx},${ty + dy},${cz}`);
          }
        }
        // On its level, or on the incline of a ramp it is walking up or
        // down, or on a ladder it is climbing (2026-09-28): see standAt. On
        // a ladder it goes on just after the ladder and before anything
        // south or east of it, which stands in front of the climber.
        const pose = standAt(shown, p.x, p.y, cz, g);
        // Behind whatever is in front of either end of a step, for the
        // whole of the step: see walkerKey.
        let key = pose.ladder
          ? pose.x + Math.ceil(pose.y) + 0.5
          : walkerKey(p, { x: c.fromX, y: c.fromY }, { x: c.x, y: c.y }, (x, y) => tallFront(x, y, cz), (x, y) => standKey(x, y, cz));
        // Coming up a stair or a ramp onto a floor upstairs, it goes on
        // after that floor, as it will once it stands on it: the head of
        // the flight is no floor of the slab's, so the climber took the
        // head's own depth, the slab went on after and covered all but its
        // feet, and it was outlined over the slab the whole way up
        // (2026-10-20: "when climbing stairs, i see an outline around the
        // character until they arrive at the top").
        if (!pose.ladder && p.moving && (inclineAt(shown, cz, c.fromX, c.fromY, g) || inclineAt(shown, cz, c.x, c.y, g))) key = Math.max(key, standKey(c.fromX, c.fromY, cz), standKey(c.x, c.y, cz));
        // And up a ladder onto a floor upstairs the same, on the ladder
        // and stepping off its hatch: the climber took the ladder's depth
        // or the hatch's, the slab it climbs onto (and the one the ladder
        // stands on) went on after, and it was outlined over them all the
        // way up (2026-10-22: "walking up a ladder shouldn't show the
        // outline on the player", the one in the Mathom-house's museum up
        // to its roof).
        if (pose.ladder || (p.moving && ladderAt(shown, cz, c.fromX, c.fromY))) key = Math.max(key, standKey(c.fromX, c.fromY, cz), standKey(c.x, c.y, cz));
        // The colour it is outlined in when something hides it: the colour
        // its target or foe square would be, or else the colour its name
        // is written in, as a player's always was.
        const ghostColor = c.id === targetId ? (c.kind === "player" && !c.hostile ? "#3f3" : "#f22") : c.id === foeId ? "#fd0" : nameColour(c);
        // Under a roof drawn over it, nothing of it is painted in with
        // the rest: it is outlined over the roof once the scene is done
        // (2026-10-07: "creatures should be showing outlines when under a
        // roof like this, not a partially visible sprite"). See underRoof.
        if (underRoof(shown, cz, p, { x: c.fromX, y: c.fromY }, { x: c.x, y: c.y })) {
          const { sx, sy } = toScreen(pose.x, pose.y, pose.h);
          const X = Math.round(sx), Y = Math.round(sy);
          const spr = creatureSpriteNow(c, p.moving, now);
          roofed.push({ spr, L: X - (spr.width - TILE) / 2, T: Y + TILE - spr.height, color: ghostColor });
          continue;
        }
        later(key, cz, 2, () => {
          const { sx, sy } = toScreen(pose.x, pose.y, pose.h);
          const X = Math.round(sx), Y = Math.round(sy);
          ctx.globalAlpha = levelAlpha(cz);
          // One outline says everything. Red is the target you chose (and
          // chase); green the player you follow (one who is not hostile —
          // when your stance allows them, a player is a fight and
          // outlined red like any monster); yellow is the creature your swings
          // actually land on when it is not the one you chose — the reflex
          // foe picked automatically. Your chosen target keeps its own colour
          // even while being hit, since it is the choice worth seeing; the
          // Statuses line says whether it is being chased. One of the
          // world's people is never the target, followed or fought, so has
          // no colour of its own (2026-10-07: "don't allow npcs to be
          // followed").
          const targetColor = c.kind === "player" && !c.hostile ? "#3f3" : "#f22";
          if (c.id === targetId) {
            ctx.strokeStyle = targetColor;
            ctx.lineWidth = 2;
            ctx.strokeRect(X + 1, Y + 1, TILE - 2, TILE - 2);
          } else if (c.id === foeId) {
            ctx.strokeStyle = "#fd0";
            ctx.lineWidth = 2;
            ctx.strokeRect(X + 1, Y + 1, TILE - 2, TILE - 2);
          }
          const spr = creatureSpriteNow(c, p.moving, now);
          const L = X - (spr.width - TILE) / 2, T = Y + TILE - spr.height;
          // The dark over the tile it stands on, laid over the figure alone.
          // Blended between the tiles it is between, as the floor is.
          const a = between((tx, ty) => (unlit(tx, ty, cz) ? 0 : wash(tx, ty, cz)), p.x, p.y);
          // Painted again over a monster, it is masked by that monster's
          // own figure, so where the monster is see-through the first
          // painting shows alone and a faded player is not doubled.
          const paint = (over?: { spr: HTMLCanvasElement; L: number; T: number }) => {
            ctx.save();
            ctx.globalAlpha = levelAlpha(cz);
            if (!over) {
              ctx.drawImage(spr, X - (spr.width - TILE) / 2, Y + TILE - spr.height);
              if (a > 0) {
                ctx.globalAlpha = a * levelAlpha(cz);
                ctx.drawImage(shadowOf(spr), L, T);
              }
            } else {
              const m = document.createElement("canvas");
              m.width = spr.width;
              m.height = spr.height;
              const mc = m.getContext("2d")!;
              mc.drawImage(spr, 0, 0);
              if (a > 0) {
                mc.globalAlpha = a;
                mc.drawImage(shadowOf(spr), 0, 0);
                mc.globalAlpha = 1;
              }
              mc.globalCompositeOperation = "destination-in";
              mc.drawImage(over.spr, over.L - L, over.T - T);
              ctx.drawImage(m, L, T);
            }
            ctx.restore();
          };
          paint();
          ctx.globalAlpha = 1;
          // A player is drawn over every other creature whose figure
          // overlaps theirs (2026-10-28: "prefer to draw the player over
          // the monster in this kind of set up", of a player with a forest
          // troll on the tile east of them, the troll's shield over the
          // player's body; then "special case drawing the player after
          // other creatures"). A big figure is wider and taller than its
          // tile and one further east or south goes on later, so it
          // covered whoever stood beside it; the player, drawn already, is
          // painted again over it where the two overlap. Not past
          // something tall in front of the player, a wall or a crag, which
          // stands between them.
          const box = { L, T, R: L + spr.width, B: T + spr.height };
          if (c.kind === "player") {
            if (!tall) drawnPlayers.push({ z: cz, box, paint });
          } else {
            for (const o of drawnPlayers) {
              if (o.z !== cz) continue;
              if (Math.max(box.L, o.box.L) < Math.min(box.R, o.box.R) && Math.max(box.T, o.box.T) < Math.min(box.B, o.box.B)) o.paint({ spr, L, T });
            }
            ctx.globalAlpha = 1;
          }
          // A creature two tiles tall or more — a huorn, the mûmak, Old Man
          // Willow — hides whoever stands just north of it as a crag would,
          // so it strikes out the points it covers like one, and a player
          // or the target behind its crown is outlined over it. Old Man
          // Willow made it matter (2026-10-12): he is fought from the north,
          // his water to the south, and his crown covered the player whole.
          if (c.kind !== "player" && spr.height >= 2 * TILE) hide(L, T, L + spr.width, T + spr.height, (px, py) => inkAt(spr, px - L, py - T));
          {
            const pts: [number, number][] = [];
            const top = T + inkTop(spr), bottom = Y + TILE - 2;
            for (let i = 0; i < 6; i++) {
              for (const dx of [10, 16, 22]) pts.push([X + dx, top + ((bottom - top) * i) / 5]);
            }
            ghosts.push({ spr, L, T, color: ghostColor, pts, hit: 0, roofHit: 0, always: c.kind === "player" || c.id === targetId || c.id === foeId, ringed: !!c.greatring });
          }
        });
      }

      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          for (const z of levels) {
            const t = shown(z, x, y);
            if (t === undefined) continue;
            const alpha = levelAlpha(z);
            if (t === Tile.Tree) {
              // A tree stands up on its tile like a figure, larger than
              // the tile, over the grass it grows in; see treeCut.
              later(standKey(x, y, z) + 0.5, z, 1, () => {
                const { sx, sy } = toScreen(x, y, z);
                // The foot of the trunk a quarter of a tile up from the
                // bottom of its tile, centred across it.
                const X = Math.round(sx + TILE / 2 - TREE_PX / 2), Y = Math.round(sy + TILE - 8 - 30 * TREE_SCALE);
                const cut = soft(treeCut(spriteOf(Tile.Tree, x, y, z)), x, y, z, X, Y);
                ctx.globalAlpha = alpha;
                ctx.drawImage(cut, X, Y);
                ctx.globalAlpha = 1;
              });
            }
            if (t === Tile.PartyTree) {
              // The Party Tree stands up on its tile as a tree does, only
              // far bigger: its crown spreads over the tiles round and
              // north of it, and whoever wakes at its foot stands under
              // it (2026-10-01: "Move the respawn under the tree. Make the
              // tree look special"); see partytree.ts. Its foot is where a
              // tree's is, a quarter of a tile up from the bottom of its
              // tile, centred across it.
              later(standKey(x, y, z) + 0.5, z, 1, () => {
                const { sx, sy } = toScreen(x, y, z);
                ctx.globalAlpha = alpha;
                const X = Math.round(sx + TILE / 2 - PARTY_TREE_FOOT.x), Y = Math.round(sy + TILE - 8 - PARTY_TREE_FOOT.y);
                ctx.drawImage(soft(partyTreePicture(), x, y, z, X, Y), X, Y);
                ctx.globalAlpha = 1;
              });
            }
            if (t === Tile.Ship) {
              // A ship lies at the end of the jetty at the Grey Havens and
              // at the Harlond, stood up side on over the water with its
              // waterline across the middle of its tile (2026-10-06: "Add a
              // boat from the Grey Havens to somewhere near Minas Tirith per
              // the lore"); see ship.ts.
              later(standKey(x, y, z) + 0.5, z, 1, () => {
                const { sx, sy } = toScreen(x, y, z);
                ctx.globalAlpha = alpha;
                const X = Math.round(sx + TILE / 2 - SHIP_FOOT.x), Y = Math.round(sy + TILE / 2 + 4 - SHIP_FOOT.y);
                ctx.drawImage(soft(shipPicture(), x, y, z, X, Y), X, Y);
                ctx.globalAlpha = 1;
              });
            }
            if (t === Tile.Hedge) {
              // A shrub stands up on its tile as a bush over the grass it
              // grows in, as a tree does (2026-10-01: "render shrubs
              // oblique"); see shrub.ts.
              later(standKey(x, y, z) + 0.5, z, 1, () => {
                const { sx, sy } = toScreen(x, y, z);
                ctx.globalAlpha = alpha;
                const X = Math.round(sx) - PIECE_PAD, Y = Math.round(sy) - PIECE_PAD;
                ctx.drawImage(soft(shrubPicture(variantAt(g, t as Tile, x, y)), x, y, z, X, Y), X, Y);
                ctx.globalAlpha = 1;
              });
            }
            if (isRaised(t)) {
              // A wall, a rock or the palisade, from the floor of its level
              // to the floor of the next, painted inside its own square
              // (2026-09-27: "implement the thin castle walls, alter the
              // position of the base of the wall so minimal or no overlap
              // is required most of the time ... rework mountain/cave
              // walls for a similar effect"): see raisedPiece. So it covers
              // no floor of its own level and needs no cutting off one,
              // which the full blocks before it did.
              //
              // A rock with rock all round has a top and no faces, and its
              // top is covered exactly by the floors of the level above
              // over its four quarters, when there are four: then it is
              // left out. The rock under a hilltop is most of the blocks in
              // a mountain range, so leaving out what cannot be seen is
              // most of what a range costs to draw.
              const piece = tiles.of(MEMO_PIECE, x, y, z, () => raisedPiece(shown, z, x, y, doorAt, g))!;
              if (piece.key === 7 && levelAlpha(z + 1) === 1) {
                let covered = true;
                for (let i = 0; i < 4 && covered; i++) {
                  const up = shown(z + 1, x + (i & 1), y + (i >> 1));
                  covered = up !== undefined && !isRaised(up);
                }
                if (covered) continue;
              }
              // A wall above the lowest level lays its own floor, from the
              // floors beside it up there; the lowest level's was laid with
              // the ground. It is laid a row and a half ahead of the walls
              // rather than with its own: the face of the wall behind it
              // runs on into its square where the two join, and a floor
              // laid with the wall cut that face off at every tile, so a
              // wall upstairs read as a row of separate blocks (2026-10-19:
              // "the walls of the mathom house at the level above ground
              // are disjointed").
              if (z > base && t === Tile.Wall && !wallMass(shown, z, x, y)) {
                later(x + y - 1.5, z, 0, () => {
                  const { sx, sy } = toScreen(x, y, z);
                  ctx.globalAlpha = alpha;
                  layGround(z, x, y, Math.round(sx), Math.round(sy), wash(x, y, z));
                  ctx.globalAlpha = 1;
                });
              }
              later(x + y, z, 0, () => {
                const spr = spriteOf(blockTile(t), x, y, z);
                const { sx, sy } = toScreen(x, y, z);
                const GX = Math.round(sx), GY = Math.round(sy);
                ctx.globalAlpha = alpha;
                // Always the padded picture, a rock all round included,
                // rather than its bare sprite: where two tiles' tops meet
                // inside both pictures, a zoom of one and a half cannot put
                // the seam between them on a half pixel that neither fills.
                ctx.drawImage(soft(pieceOf(spr, piece), x, y, z, GX - PIECE_PAD, GY - PIECE_PAD), GX - PIECE_PAD, GY - PIECE_PAD);
                // A hobbit hole's door, in the face of the hill: see
                // hobbitDoorPiece.
                if (t === Tile.HobbitDoor) {
                  const door = hobbitDoorPiece(piece, variantAt(g, t as Tile, x, y));
                  if (door) ctx.drawImage(soft(door, x, y, z, GX - PIECE_PAD, GY - PIECE_PAD), GX - PIECE_PAD, GY - PIECE_PAD);
                }
                ctx.globalAlpha = 1;
                coverPiece(GX, GY, piece);
              });
              if (t === Tile.FrozenWaste && shown(z + 1, x, y) === undefined) {
                // The frozen waste's spires stand up off the top of its ice
                // (2026-10-08: "spiky looking ice mountains"), their foot in
                // the middle of the top, which is the tile's square a storey
                // up; see icespire.ts. After every block of its row, so the
                // ice behind it is under the spikes and the ice in front
                // over their feet.
                later(x + y + 0.5, z, 0, () => {
                  const { sx, sy } = toScreen(x, y, z + 1);
                  ctx.globalAlpha = alpha;
                  ctx.drawImage(dimmed(iceSpirePicture(variantAt(g, t as Tile, x, y)), wash(x, y, z)), Math.round(sx + TILE / 2 - SPIRE_FOOT.x), Math.round(sy + TILE * 0.6 - SPIRE_FOOT.y));
                  ctx.globalAlpha = 1;
                });
              }
              continue;
            }
            // A ramp or a slope is one incline from its foot up to its head
            // (2026-09-28: "fix ramps looking like they're inside a weird
            // box of a similar type of the neighbouring terrain. also render
            // them oblique"), drawn with its foot, on whatever level that
            // is; the head is the incline's top edge and draws nothing of
            // its own — no floor, and no column of rock under it.
            const ramp = tiles.of(MEMO_RAMP, x, y, z, () => inclineAt(shown, z, x, y, g));
            if (ramp) {
              if (ramp.foot === z) later(x + y, z, 0, () => incline(x, y, z, t, ramp.dir));
              continue;
            }
            // A ladder stands up from its floor to the hatch above it
            // (2026-09-28: "render the ladder oblique"), from the middle of
            // its tile (2026-09-28: "since you can't stand on a ladder
            // tile, center the foot of ladder in the tile so it's clear why
            // we can't fit next to it"): see ladderAt. Its floor is laid as
            // a floor.
            //
            // A ladder whose foot is on a floor upstairs — the one in the
            // Mathom-house's museum up to its roof (2026-10-19: "there's no
            // ladder up to the roof of the mathom house") — goes on after
            // that floor, which is a slab and is drawn all at once at the
            // depth of its nearest tile (see hangKey), or the floor covers
            // it. And after what lies on that floor, which goes on at the
            // slab's depth too (see standItem): since the ladder stands
            // flush with the north edge of its tile (2026-10-19) it rises
            // over the tile behind it, and was drawn under the museum's
            // rope there (2026-10-20: "ladder should be drawn over the
            // things behind it").
            if (t === Tile.Ladder) later(z > base && slabbed(z, x, y) ? hangKey(z, x, y) + 1.4 : x + y, z, z > base && slabbed(z, x, y) ? 1.5 : 0.5, () => standLadder(x, y, z));
            // A pit prop stands up too (2026-09-28: "render the pit prop
            // oblique"), at the north side of its tile, over the cave floor
            // it is laid on: see propFacets. It goes on after the floor of
            // its tile and before whoever stands on it, who is in front of
            // it; whoever stands north of it is behind it (coversNorth).
            if (t === Tile.PitProp) later(x + y, z, 0.5, () => standProp(x, y, z));
            // The lowest floor was painted flat, with the ground.
            if (z === base) continue;
            // A floor of a level above the lowest: on a block, which
            // draws the faces under it; propped against a ledge, with a
            // column of the ledge's rock under it; or a slab.
            const hang = overhang(z, x, y);
            const prop = hang ? undefined : propped(z, x, y);
            // The hatch at the head of a ladder goes on after the ladder
            // under it, level for level: with the ladder's foot in the
            // middle of its tile (2026-09-28: "since you can't stand on a
            // ladder tile, center the foot of ladder in the tile so it's
            // clear why we can't fit next to it") the foot is behind the
            // hatch's south and east rims on the glass, which are nearer.
            const head = ladderAt(shown, z, x, y)?.foot === z - 1;
            const key = slabbed(z, x, y) ? hangKey(z, x, y) + 0.9 : head ? x + y + 0.5 : promote.size && promote.has(`${x},${y},${z}`) ? x + y - 0.6 : x + y;
            later(key, z, 0, () => {
              const floor = (t === Tile.Tree || t === Tile.Hedge || t === Tile.PartyTree ? Tile.Grass : t === Tile.Ship ? Tile.Water : t) as Tile;
              const spr = floorSprite(floor, x, y, z);
              const { sx, sy } = toScreen(x, y, z);
              const X = Math.round(sx), Y = Math.round(sy);
              // How far down this floor's own faces hang, in storeys, and
              // which of them show.
              let k = 0, s = false, e = false;
              ctx.globalAlpha = alpha;
              if (prop !== undefined) {
                k = 1;
                s = !solidAt(shown, base, z - 1, x, y + 1);
                e = !solidAt(shown, base, z - 1, x + 1, y);
                const faces = faceSprites(spriteOf(prop, x, y, z - 1));
                if (s) ctx.drawImage(dimmed(faces.s, wash(x, y, z)), X, Y + TILE);
                if (e) ctx.drawImage(dimmed(faces.e, wash(x, y, z)), X + TILE, Y);
              } else if (hang) {
                // A slab's open edges, where nothing of its own level
                // carries on beyond it: its own texture, shaded.
                k = SLAB;
                s = shown(z, x, y + 1) === undefined;
                e = shown(z, x + 1, y) === undefined;
                for (const [side, open] of [["s", s], ["e", e]] as const) {
                  if (!open) continue;
                  facePath(ctx, X, Y, side, SLAB);
                  ctx.fillStyle = patternOf(ctx, spr);
                  ctx.fill();
                  ctx.fillStyle = `rgba(0,0,0,${FACE_SHADE[side] + 0.1})`;
                  ctx.fill();
                }
              }
              this.drawTileImage(spr, X, Y);
              if (g.protectedAt(x, y, z)) {
                ctx.fillStyle = PZ_TINT;
                this.fillTile(X, Y);
              }
              if (onFloor.size) for (const draw of onFloor.get(`${x},${y},${z}`) ?? []) draw();
              const round = washRound(x, y, z);
              const a = round[4];
              if (!evenWash(round)) {
                // At the edge of a light the floor's dark is blended as the
                // ground's is (see washPatch), and each face takes the edge
                // of the floor it hangs from, drawn down it along its slant.
                const patch = washPatch(round);
                ctx.globalAlpha = alpha;
                this.drawTileImage(patch, X, Y);
                for (const [side, open] of [["s", s], ["e", e]] as const) {
                  if (!open || prop !== undefined) continue;
                  ctx.save();
                  facePath(ctx, X, Y, side, k);
                  ctx.clip();
                  ctx.imageSmoothingEnabled = false;
                  if (side === "s") {
                    ctx.transform(1, 0, 1, 1, X, Y + TILE);
                    ctx.drawImage(patch, 0, TILE - 1, TILE, 1, -1, 0, TILE + 2, k * STOREY);
                  } else {
                    ctx.transform(1, 1, 0, 1, X + TILE, Y);
                    ctx.drawImage(patch, TILE - 1, 0, 1, TILE, 0, -1, k * STOREY, TILE + 2);
                  }
                  ctx.restore();
                }
              } else if (a > 0) {
                ctx.globalAlpha = a * alpha;
                ctx.fillStyle = "#000";
                this.fillTile(X, Y);
                for (const [side, open] of [["s", s], ["e", e]] as const) {
                  if (!open || prop !== undefined) continue;
                  facePath(ctx, X, Y, side, k);
                  ctx.fill();
                }
              }
              ctx.globalAlpha = 1;
              cover(X, Y, s ? k : 0, e ? k : 0, isRoof(t));
            });
          }
        }
      }

      // The doors, each standing in its wall: the wall's top carried over
      // the doorway as a lintel, and the door on the wall's own south or
      // east face under it — the thin wall's, a quarter of a tile in, or
      // the rock's, half a tile in (see doorPlane) — from the wall on one
      // side of the doorway to the wall on the other, whose arms run up to
      // it. Before whoever stands in the doorway, who is in front of the
      // frame.
      for (const d of standingDoors) {
        const sd = standingDoor(shown, d.z, d.x, d.y, doorAt);
        if (!sd) continue;
        const whole = doorFacets(sd.side, sd.thin);
        // Everything but the door's own face, which is the first facet:
        // the lintel, and the half tile of wall face beyond the door.
        const lintel: Piece = { key: whole.key + 10, facets: whole.facets.slice(1), edges: whole.edges };
        later(d.x + d.y, d.z, 1, () => {
          const { sx, sy } = toScreen(d.x, d.y, d.z);
          const GX = Math.round(sx), GY = Math.round(sy);
          const a = wash(d.x, d.y, d.z);
          ctx.globalAlpha = levelAlpha(d.z);
          // The lintel is made of the wall it spans.
          ctx.drawImage(soft(pieceOf(spriteOf(sd.beside, d.x, d.y, d.z), lintel), d.x, d.y, d.z, GX - PIECE_PAD, GY - PIECE_PAD), GX - PIECE_PAD, GY - PIECE_PAD);
          const [px, py] = doorPlane(sd.side, sd.thin);
          ctx.save();
          ctx.transform(...faceTransform(sd.side, GX + px, GY + py));
          ctx.drawImage(dimmed(doorFace(d.id), a), 0, 0);
          ctx.restore();
          ctx.globalAlpha = 1;
          coverPiece(GX, GY, whole);
        });
      }
      standingDoors.length = 0;
      // The chests, each at its tile's depth after the floor it stands on
      // and before whoever stands south, east or west of it, who is in
      // front of it; whoever stands north of it is behind it (tallFront).
      for (const ch of standingChests) later(standKey(ch.x, ch.y, ch.z), ch.z, 0.5, () => standChest(ch.x, ch.y, ch.z, ch.kind));
      standingChests.length = 0;
      // Site 8 (2026-10-16): the Entwives' orchard trees, items stood up
      // the way a tree tile is, at a tree's depth; see STANDING_TREE_ITEMS.
      for (const tr of standingTrees) {
        later(standKey(tr.x, tr.y, tr.z) + 0.5, tr.z, 1, () => {
          const { sx, sy } = toScreen(tr.x, tr.y, tr.z);
          const X = Math.round(sx + TILE / 2 - TREE_PX / 2), Y = Math.round(sy + TILE - 8 - 30 * TREE_SCALE);
          ctx.globalAlpha = levelAlpha(tr.z);
          ctx.drawImage(soft(treeCut(tr.spr), tr.x, tr.y, tr.z, X, Y), X, Y);
          ctx.globalAlpha = 1;
        });
      }
      standingTrees.length = 0;
      // Site 6 (2026-10-16): the falls of Henneth Annûn, a curtain a
      // storey taller than its tile, so it pours down the face of the
      // rock behind it to the pool, with light running down it.
      for (const f of standingFalls) {
        later(standKey(f.x, f.y, f.z) + 0.5, f.z, 1, () => {
          const { sx, sy } = toScreen(f.x, f.y, f.z);
          const X = Math.round(sx), Y = Math.round(sy - STOREY), H = TILE + STOREY;
          ctx.globalAlpha = levelAlpha(f.z);
          ctx.drawImage(dimmed(f.spr, wash(f.x, f.y, f.z)), X, Y, TILE, H);
          const t = performance.now() / 12;
          ctx.fillStyle = "rgba(255, 255, 255, 0.55)";
          for (let i = 0; i < 5; i++) {
            const lane = (i * 7 + f.x * 3) % (TILE - 3);
            const at = (t + i * 23 + f.x * 11) % H;
            ctx.fillRect(X + lane, Y + at, 2, Math.min(7, H - at));
          }
          ctx.globalAlpha = 1;
        });
      }
      standingFalls.length = 0;
      jobs.sort(byDepth);
      for (const j of jobs) j.draw();
      ctx.globalAlpha = 1;
      // A sixth of a figure hidden is enough to outline it: that is its
      // feet behind a wall, or its head behind a crag, and less than that
      // is a figure plainly still in sight.
      for (const gh of ghosts) {
        if (gh.ringed || (gh.always ? gh.hit : gh.roofHit) >= 3) ctx.drawImage(ghostOf(unfaded(gh.spr), gh.color), gh.L - 1, gh.T - 1);
      }
      for (const r of roofed) ctx.drawImage(ghostOf(unfaded(r.spr), r.color), r.L - 1, r.T - 1);
    };

    // Underground the world is the cave and nothing else: the surface is a
    // storey of rock away. Paint the dark, then this floor's tiles, then
    // whatever lies and stands down here, and skip the surface passes.
    if (eyeZ < 0) {
      // How dark any point of the floor is under every light on it: yours
      // and each other player's, each measured from where its bearer is
      // drawn standing — the interpolated position rather than the tile,
      // so a light walks with whoever carries it instead of jumping a
      // tile at a time. See darknessAt for what a light's number means
      // and why the dark is drawn here rather than kept off the wire, and
      // darkAt for how several are combined. Everything below that asks
      // how dark a tile is asks `dark`, built with the lights above.
      //
      // Whether this floor has a tile here for the player at all. What
      // they have never seen is not drawn, so the dim must not be laid
      // over it: an unknown tile keeps the full black it always had, and
      // the cave ends where the player's knowledge of it does.
      const known = (tx: number, ty: number) => g.tileOn(eyeZ, tx, ty) !== undefined;
      // ONE FLOOR, OR EVERY LEVEL OF A HOLLOW. Standing in a hollow open
      // across levels — the orcs' cavern, its yard and the walk round the
      // top of its wall — the scene is every level of it, the way the
      // surface's is every level under the sky (2026-10-02: "Light like
      // from the spell should apply to other levels when underground. For
      // example I should be able to see the battlements and central
      // compound of the orc fort from the other locations and vice
      // versa"). terrainOf says which levels and what of each, and
      // everywhere else under the ground that is the viewer's own floor
      // alone, as it always was. The lowest is laid flat here, as the
      // ground is on the surface; what is above it is stood up in depth
      // order by raise, faded when it is over the viewer's head, as the
      // surface's roofs are. The dark is the cave's on every level of it:
      // lit where a light in the hollow reaches, from either level
      // (lightReaches), dim where it was seen once, black where never.
      const knownHere = (tx: number, ty: number) => known(tx, ty) || levels.some((z) => shown(z, tx, ty) !== undefined);
      ctx.fillStyle = "#0c0a08";
      ctx.fillRect(0, 0, this.worldW(), this.worldH());
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          const t = shown(base, x, y);
          if (t === undefined) continue;
          const { sx, sy } = toScreen(x, y, base);
          // A wall is painted standing up, in the far corner of its
          // square, and the floor either side of it runs up to it.
          if (isRaised(t)) {
            layGround(base, x, y, Math.round(sx), Math.round(sy));
            continue;
          }
          this.drawTileImage(floorSprite(t, x, y, base), Math.round(sx), Math.round(sy));
          // Protected ground under the ground, on its own level: Galion's
          // corner of the Elvenking's cellars (2026-10-07), washed as the
          // zones on the ground are.
          if (g.protectedAt(x, y, base)) {
            ctx.fillStyle = PZ_TINT;
            this.fillTile(Math.round(sx), Math.round(sy));
          }
        }
      }
      drawHover();
      // What lies on the floor is part of the floor, and is left under the
      // wash with it. Blood, a spatter and a dropped club out past the
      // light are drawn and then dimmed to a seventh of themselves along
      // with the earth they are lying on, which is a smudge on the ground
      // rather than an object — and that is the right answer for a thing
      // that does not move. The rule the dark is keeping is about what can
      // come at you: a monster hidden out there is hidden because knowing
      // where it stands is knowing what it is about to do, and a club has
      // never been about to do anything. Taking the ground's things out of
      // the dim as well would have meant a second wash or a second pass
      // over the same tiles to put them back in the black, for a club.
      //
      // A level of a hollow above the lowest lays what lies on it with its
      // floor, as a roof does (see flat and standItem), and only where the
      // level is in the scene at all.
      for (const z of levels) drawStains(z);
      const caveWash = washOf(knownHere);
      for (const it of bodiesFirst(items)) {
        const iz = it.z ?? 0;
        if (!levels.includes(iz) || shown(iz, it.x, it.y) === undefined) continue;
        if (BLOOD_RGB[it.id] || SPATTER_RGB[it.id]) continue; // drawn above
        if (it.x < x0 || it.x > x1 || it.y < y0 || it.y > y1) continue;
        if (standsUp(it, iz)) continue;
        const put = () => {
          const { sx } = toScreen(it.x, it.y, iz);
          const sy = toScreen(it.x, it.y, iz).sy - (lifts.get(it) ?? 0);
          // Centred by its own size: a ground sprite may be bigger than a
          // tile (the giant sword is) and then overhangs equally all round.
          const spr = isBody(it.id) ? bodySprite(it.id, it.kind, it.color, it.race, it.hair) : groundSprite(it.id, it.color, g.defOf(it)?.make);
          ctx.drawImage(spr, Math.round(sx) - (spr.width - TILE) / 2, Math.round(sy) - (spr.height - TILE) / 2);
          if (it.count > 1) {
            this.text(String(it.count), Math.round(sx) + 2, Math.round(sy) + TILE - 3, 9, "#fff", "left", COUNT_FONT);
          }
          return { spr, X: Math.round(sx) - (spr.width - TILE) / 2, Y: Math.round(sy) - (spr.height - TILE) / 2 };
        };
        if (iz === base) put();
        else standItem(it.x, it.y, iz, caveWash, put);
      }
      // The mouths of passages on your own floor, which a look at another
      // floor is not.
      if (!peeking) drawPeeks(meZ);
      // Only what the light actually reaches is drawn at all, and the
      // question is asked of the light rather than of the wash (asked for
      // on 2026-09-21: "Don't show monsters outside the light radius.
      // Extend the "dim" area effect to all unlit tiles underground").
      // The two halves of that are one change: now that a drawn tile is
      // never fully black, "its tile is not fully black" would let every
      // monster in view through, so what used to be a test on the alpha
      // is a test on the radius. See litAt.
      //
      // A creature outside it is not painted and then covered — it is
      // left out, so nothing of it can show: a demon is three tiles tall
      // and its head would otherwise stand up out of the dark into a tile
      // that is lighter than the one it is standing on. What this costs
      // is the old fade-in, where something in the falloff band was drawn
      // faint for a tile before it was drawn plainly; that was the mood
      // of a cave and it was also the thing that was asked to go, since a
      // shape you can half see is a shape you have seen. A monster now
      // arrives at the edge of the light, whole.
      //
      // Yourself always, whatever the light is doing. The one figure a
      // player must be able to find on the screen is their own, and the
      // camera is on it; today your own alertness takes in your own tile
      // so this never differs, and it is written down so that the day something
      // takes a light away it does not take you with it.
      //
      // The same rule for a player as for a monster, deliberately. It is
      // a fact about the floor and not about who is standing on it, and
      // it costs a player nothing in practice: every player carries a
      // light, every light in view is in this list, and a lamp lights the
      // tile its bearer is standing on — so somebody out in the black is
      // lit by their own light and is drawn, exactly as they were. What
      // the rule does catch is a player with no light at all, and a
      // figure standing in the dark with no lamp should be as unseen as
      // anything else standing there.
      const below = all.filter((e) => e.c.z === eyeZ && (e.c.id === g.you?.id || lit(e.p.x, e.p.y)));
      // And in a hollow, whoever stands on its other level, by the same
      // rule asked of their own level: an orc on the walk is seen from the
      // yard when a light in the hollow reaches where it stands, from
      // either level (2026-10-02: "Light like from the spell should apply
      // to other levels when underground"). Drawn and not named, as a
      // creature on a roof is from the street: a plate is for what is on
      // your own level.
      const across = all.filter((e) => e.c.z !== eyeZ && levels.includes(e.c.z) && shown(e.c.z, Math.round(e.p.x), Math.round(e.p.y)) !== undefined && lit(e.p.x, e.p.y, e.c.z));
      // The dark goes on over the floor and everything lying on it, and
      // under everything that follows: the walls, the figures, the names,
      // the rings, the effects. Each of those decides for itself what the
      // dark does to it, which is the point of drawing it here rather than
      // last — and the walls and the figures, painted in depth order since
      // the oblique view, lay the same wash over themselves as they go on
      // (washAt), so a lit wall stands at the edge of a dim cave.
      //
      // The floor it goes over is the lowest level drawn, which is the
      // viewer's own everywhere but on the walk over a hollow's yard
      // (2026-10-02), and the dark over it, which tiles of it are known
      // and where it is on the glass are all that level's.
      const floorBright = (x: number, y: number) => bright(x, y, base);
      const floorKnown = (x: number, y: number) => shown(base, x, y) !== undefined;
      this.drawDarkness(floorBright, floorKnown, (x, y) => toScreen(x, y, base), x0, x1, y0, y1);
      raise([...below, ...across], caveWash);
      // The colour of a coloured light, over the floor, the walls and
      // whoever stands in it alike, since it is the light they are all
      // seen by (2026-10-13: "Can we add a red or blue tint to certain
      // types of light?"). See tintAt.
      this.drawTints(lights, knownHere, (x, y) => toScreen(x, y, base), x0, x1, y0, y1, base, space);
      // Names and bars only for what is seen, and faded by the same
      // factor as the figure they belong to. A name floating in the black
      // over a creature that is not drawn is a tell — it says exactly
      // where the thing you cannot see is standing — so the list this
      // walks is the list of the drawn, and the unseen have no plate.
      // They are drawn over the dark rather than under it, and dimmed by
      // alpha instead, because a plate sits in the tile above its
      // creature and that tile may be lighter or darker than the one the
      // creature is standing on: a figure to the south fading into the
      // dark would keep a bright name, and one to the north would lose
      // its name a tile before it lost its body.
      //
      // That fade is nearly always a factor of one now, since what this
      // walks is what the light reaches and the dark over a lit tile is
      // nought by definition. It is kept because it is the correct
      // question rather than a leftover one: the plate belongs to the
      // tile its creature is standing on, that tile's darkness is what a
      // plate should be worth, and the day the light stops being a hard
      // edge this goes on being right with nothing to remember.
      //
      // A name that would be drawn over another creature goes behind it
      // (2026-10-07: "render the creature's name *behind* the obscured
      // creature instead"): see namefit.ts. Those names are laid first,
      // under every other plate, so whoever stands over one keeps their
      // own name and bar on top of it.
      const plates = below.map(({ c, p }) => {
        const { sx, sy } = toScreen(p.x, p.y);
        const X = Math.round(sx), Y = Math.round(sy), spr = creatureSpriteNow(c, p.moving, now);
        const nameColor = nameColour(c);
        const ny = nameplateY(Y, spr, c.maxhp);
        // Where raise painted the figure, which is where it covers a name.
        const pose = standAt(shown, p.x, p.y, eyeZ, g);
        const at = toScreen(pose.x, pose.y, pose.h);
        return { c, X, Y, bx: Math.round(at.sx), by: Math.round(at.sy), spr, ny, nameColor, d: shade(p.x, p.y) };
      });
      playersLast(plates);
      const overName = this.creaturesOverNames(plates);
      for (const { c, X, ny, nameColor, d } of plates.filter((e) => overName.has(e.c.id))) {
        ctx.globalAlpha = nameAlpha(c, d);
        this.nameplate(c, X, ny, nameColor, !!c.skull, overName.get(c.id));
        ctx.globalAlpha = 1;
      }
      this.layNamesBehind();
      for (const { c, X, ny, nameColor, d } of plates) {
        // A monster's red name is held up off the dark: see MONSTER_NAME_FLOOR.
        ctx.globalAlpha = nameAlpha(c, d);
        if (!overName.has(c.id)) this.nameplate(c, X, ny, nameColor, !!c.skull, undefined);
        ctx.globalAlpha = 1 - d;
        this.healthBar(ctx, X, ny + BAR_DROP, c.hp, c.maxhp, g.you && c.id === g.you.id ? g.you.projected : c.hp, !!c.invisible, g.you && c.id === g.you.id ? (g.you.poisonleft ?? 0) : 0);
        ctx.globalAlpha = 1;
      }
      markYou();
      // The rings are drawn over the dark and take no notice of it. They
      // are a debug overlay, and the whole point of a debug overlay is
      // seeing what the game knows: a ring round a monster you cannot see
      // is the overlay doing its job, not the darkness failing at its.
      this.drawAggroRings(g, onYourFloor, meZ);
      this.drawOverlays(g, now, toScreen, standing, x0, x1, y0, y1, shade);
      this.drawTracks(g, toScreen, meZ, now);
      this.deathBanner(g);
      ctx.restore();
      return;
    }

    // Ground. Tiles inside the protection zone take a cool wash so the safe
    // ground — and the line it stops at — is readable at a glance. It is the
    // only wash there is: see the note above on the one that went.
    //
    // A tree's tile is laid as grass: the tree itself is stood up on it
    // with everything else that has height (see raise).
    //
    // Past the map's edge the edge goes on: a tile off the map is laid as
    // the nearest one on it, corners as the corner (2026-10-17: "The edges
    // of the map should show more of whatever the edge tile is beyond what
    // is visible"). Until 2026-09-28 the loop ran over the whole window and
    // tileAt's water filled the rest, so the world was hemmed by a sea that
    // was not there; from then until 2026-10-17 it was left black past the
    // edge. The east and south edges are open country, and now they run on
    // as open country, and past the east edge as desert (see groundPast).
    // tileAt still answers water off the map, which is what keeps anyone
    // from walking off it.
    const groundTile = (x: number, y: number, X: number, Y: number) => {
      const off = x !== edgeX(g, x) || y !== edgeY(g, y);
      const tile = groundPast(g, x, y) as Tile;
      // Ground never seen is not drawn: it is left the black the frame
      // was cleared to (2026-09-28: "don't send players the entire map.
      // make them have to explore and record it as they go").
      if ((tile as number) === UNSEEN) return;
      // A wall or a rock is painted standing up, in the far corner of
      // its square, and the ground beside it runs up to it wherever it
      // shows: see raise and groundRegions.
      if (isRaised(tile)) {
        layGround(0, x, y, X, Y);
        return;
      }
      // The Party Tree stands in the grass, as every tree does.
      const t = tile === Tile.Tree || tile === Tile.Hedge || tile === Tile.PartyTree ? Tile.Grass : tile === Tile.Ship ? Tile.Water : tile;
      this.drawTileImage(floorSprite(t, x, y, 0), X, Y);
      if (!off && g.protectedAt(x, y)) {
        this.ctx.fillStyle = PZ_TINT;
        this.fillTile(X, Y);
      }
    };
    if (!this.layGroundKept(x0, x1, y0, y1, toScreen(0, 0, 0), frame & 1, `${g.terrainEra}|${doorSig}`, groundTile)) {
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          const { sx, sy } = toScreen(x, y, 0);
          groundTile(x, y, Math.round(sx), Math.round(sy));
        }
      }
    }

    // Hover highlight.
    drawHover();

    // The surface's share of the dark, for what has height: every tile is
    // known up here, and the ceiling is the surface's.
    const surfaceWash = washOf(() => true);

    // Items, bottom of stack first: flat on the ground, or standing on a
    // raised floor with everything else that has height.
    const drawItems = (z: number) => {
      // Bodies under everything else: see bodiesFirst.
      for (const it of bodiesFirst(items)) {
        if ((it.z ?? 0) !== z) continue;
        if (lifted(z, it.x, it.y)) continue;
        if (BLOOD_RGB[it.id] || SPATTER_RGB[it.id]) continue; // drawn above
        if (it.x < x0 || it.x > x1 || it.y < y0 || it.y > y1) continue;
        if (standsUp(it, z)) continue;
        const put = () => {
          const { sx } = toScreen(it.x, it.y, z);
          const sy = toScreen(it.x, it.y, z).sy - (lifts.get(it) ?? 0);
          // Centred by its own size: a ground sprite may be bigger than a
          // tile (the giant sword is) and then overhangs equally all round.
          const spr = isBody(it.id) ? bodySprite(it.id, it.kind, it.color, it.race, it.hair) : groundSprite(it.id, it.color, g.defOf(it)?.make);
          ctx.drawImage(spr, Math.round(sx) - (spr.width - TILE) / 2, Math.round(sy) - (spr.height - TILE) / 2);
          if (it.count > 1) {
            this.text(String(it.count), Math.round(sx) + 2, Math.round(sy) + TILE - 3, 9, "#fff", "left", COUNT_FONT);
          }
          return { spr, X: Math.round(sx) - (spr.width - TILE) / 2, Y: Math.round(sy) - (spr.height - TILE) / 2 };
        };
        if (z === base) put();
        // Under a floor of the level above, it lies with its own floor, so
        // the floor over it covers it: the beds in the room over the
        // Prancing Pony, under its thatch (2026-10-08), which stood up over
        // the thatch with everything else that has height.
        else if (shown(z + 1, it.x, it.y) !== undefined) flat(z, it.x, it.y, put);
        else standItem(it.x, it.y, z, surfaceWash, put);
      }
    };
    drawStains(0);
    // Fish go under everything that lies on the ground and over the water
    // itself: they are in the lake, not on it. Their positions slide
    // between tiles, since a fish that hopped would read as a frog.
    const drawFish = () => {
      for (const f of g.fish.values()) {
        if (f.x < x0 - 1 || f.x > x1 + 1 || f.y < y0 - 1 || f.y > y1 + 1) continue;
        const t = Math.min(1, (now - f.at) / FISH_SWIM_MS);
        const fx = f.fromX + (f.x - f.fromX) * t, fy = f.fromY + (f.y - f.fromY) * t;
        const { sx, sy } = toScreen(fx, fy, 0);
        ctx.globalAlpha = 0.85;
        ctx.drawImage(fishSprite(DIR_INDEX[f.dir] ?? 1, Math.floor(now / 220) % 3), Math.round(sx), Math.round(sy));
        ctx.globalAlpha = 1;
      }
    };
    drawFish();
    drawItems(0);
    drawPeeks(0);

    // THE DAYLIGHT WASH, which until 2026-09-22 did not exist: above ground
    // lightsOn returned an empty list, nothing asked how dark anything was,
    // and the surface was drawn lit to the edge of the window however wide
    // the window happened to be. Asked for that day: "show the map dimmed
    // outside of sight/light range at any level. players have a sight range
    // of 10 in any direction at ground level and above."
    //
    // It is the cave's wash, by the same brightAt since 2026-10-13, and
    // the surface is always lit ("everywhere above ground is always lit"):
    // the country you know is whole, near and far alike (2026-10-14:
    // "Above ground, everything is lit 100%"). Every tile used to count as known up here, where the cave asks
    // whether the player has ever seen this one, because the client was
    // handed the whole ground at login. It is not any more (2026-09-28:
    // "don't send players the entire map. make them have to explore and
    // record it as they go"), so the surface asks the cave's question: the
    // wash over ground seen before says "you cannot see what is on this
    // from where you stand", and ground never seen is not drawn at all and
    // stays the black of the unknown.
    //
    // Laid here, over the ground and what lies on it and under everything
    // with height — the same place in the order the cave lays its own, and
    // for the same reason: everything that follows decides for itself what
    // the dark does to it. What has height lays the same wash over itself
    // as it is painted (see raise), since the oblique view put it in front
    // of ground a wash laid last would have darkened it by.
    //
    // It is the ground's own brightness, asked of the ground, whichever level
    // the scene is seen from: from a roof or a look up, the ground is a level
    // round the one that reads, and drawn a quarter down from it (2026-10-16:
    // "the currently active level should be brighter than the surrounding
    // visible levels").
    const groundBright = (x: number, y: number) => bright(x, y, base);
    this.drawDarkness(groundBright, (tx, ty) => g.tileOn(0, tx, ty) !== undefined, (x, y) => toScreen(x, y, 0), x0, x1, y0, y1);
    // The colour of a coloured light on the ground round it, though the
    // sky is what lights it (2026-10-17: "Above ground, coloured light
    // sources should still be discernable"). See tintsOn.
    // By night they light the ground round them as in a cave, and are
    // tinted as a cave's are.
    if (g.night) this.drawTints(lights, (tx, ty) => g.tileOn(0, tx, ty) !== undefined, (x, y) => toScreen(x, y, 0), x0, x1, y0, y1, 0, undefined);
    else this.drawTints(tintsOn(g.you, mePos ?? { x: this.camX, y: this.camY }, shining), (tx, ty) => g.tileOn(0, tx, ty) !== undefined, (x, y) => toScreen(x, y, 0), x0, x1, y0, y1, undefined, undefined, true);

    // The upper levels: rooftops, hilltops and stair heads, and whatever
    // lies and stands up there, painted in with everything else that has
    // height. A roof is hidden entirely while the player is indoors under
    // it.
    //
    // Every level above the ground, each a storey over the one below it.
    // There was only ever the one until 2026-09-26, when the summit over
    // the dragons' peaks went up on the level above theirs ("Add a dragon
    // lord at the very top"). Only the first had roofs to lift until
    // 2026-10-08, when the Prancing Pony went up with a room over its
    // ground floor and its thatch over that: from its ground floor the
    // room upstairs and the thatch both lift, and from the room the
    // thatch (see terrainOf).
    for (const z of levels) {
      if (z === base) continue;
      drawStains(z);
      drawItems(z);
    }
    raise(all.filter((e) => levels.includes(e.c.z ?? 0) && !upHidden(e.c.x, e.c.y, e.c.z)), surfaceWash);

    // Names and health bars on a second pass so they sit above every sprite,
    // and only for what stands on your own level. A creature on a roof or a
    // hilltop seen from the ground, or on the ground seen from up there, is
    // drawn but not named or barred (2026-09-24: "creatures on a different
    // level to the player should not show health bar or name"): a plate is
    // a reading of something you could reach, and those are a storey away.
    const named = all.filter((e) => (e.c.z ?? 0) === eyeZ && !upHidden(e.c.x, e.c.y, e.c.z));
    const plates = named.map(({ c, p }) => {
      // Over the figure's head wherever it is drawn, a ramp included.
      const pose = standAt(shown, p.x, p.y, eyeZ, g);
      const { sx, sy } = toScreen(pose.x, pose.y, pose.h);
      const X = Math.round(sx), Y = Math.round(sy), spr = creatureSpriteNow(c, p.moving, now);
      // A player's name floats in their own colour, the same one their
      // tunic and damage numbers wear; the world's people are orange and
      // monsters red (2026-10-07: "switch monsters to use the brighter red
      // as suggested"), and a monster's red is held up off the dark: see
      // MONSTER_NAME_FLOOR.
      const nameColor = nameColour(c);
      const ny = nameplateY(Y, spr, c.maxhp);
      // Faded by the dark over the tile its creature stands on, the way
      // the cave's plates are, and for the reason given there: it is the
      // correct question rather than a live one. With the sight range and
      // the view ring being one number, everything the server sends is
      // inside the sight and this is a factor of one every time — but a
      // plate belongs to its creature's tile, that tile's darkness is what
      // it is worth, and the day those two numbers part this goes on being
      // right with nothing to remember.
      return { c, X, Y, bx: X, by: Y, spr, ny, nameColor, d: shade(p.x, p.y) };
    });
    // A name that would be drawn over another creature goes behind it, so
    // that creature's figure stands over the part of the name it covers
    // and the rest of the name shows (2026-10-07: "render the creature's
    // name *behind* the obscured creature instead"; it was left off since
    // 2026-10-01): see namefit.ts. Those names are laid first, under every
    // other plate, so whoever stands over one keeps their own name and bar
    // on top of it.
    //
    // A skull goes beside the name rather than over it: above is roughly
    // where the nameplate of whoever stands one tile north is — roughly,
    // since every label now rides at its own creature's height — and a
    // mark that collides with someone else's name reads as being about
    // them. Beside the name it cannot, whatever the neighbour is doing.
    // Players' plates go on after the rest, so a player's name and bar
    // stay readable over a monster's beside them: see playersLast.
    playersLast(plates);
    const overName = this.creaturesOverNames(plates);
    for (const { c, X, ny, nameColor, d } of plates.filter((e) => overName.has(e.c.id))) {
      ctx.globalAlpha = nameAlpha(c, d);
      this.nameplate(c, X, ny, nameColor, !!c.skull, overName.get(c.id));
      ctx.globalAlpha = 1;
    }
    this.layNamesBehind();
    for (const { c, X, ny, nameColor, d } of plates) {
      ctx.globalAlpha = nameAlpha(c, d);
      if (!overName.has(c.id)) this.nameplate(c, X, ny, nameColor, !!c.skull, undefined);
      ctx.globalAlpha = 1 - d;
      this.healthBar(ctx, X, ny + BAR_DROP, c.hp, c.maxhp, g.you && c.id === g.you.id ? g.you.projected : c.hp, !!c.invisible, g.you && c.id === g.you.id ? (g.you.poisonleft ?? 0) : 0);
      ctx.globalAlpha = 1;
    }

    // The floor seen from, not nought. This scene draws everything from the ground up —
    // the surface and whatever roofs and hilltops stand on it — so a
    // viewer standing on a roof or on top of the dragons' hill is at
    // z=1 here, and a hardcoded nought threw away every ring on the
    // level they were actually on: the creatures up there, because their
    // z did not match, and their own, because the player's ring is drawn
    // under the same test. The rings simply vanished the moment you
    // climbed anything, which is a fault you can only find by climbing.
    markYou();
    this.drawAggroRings(g, onYourFloor, meZ);
    if (px <= PLACE_NAMES_AT) this.drawPlaceNames(g, toScreen, 0);
    this.drawQuestMarks(g, toScreen);
    this.drawOverlays(g, now, toScreen, standing, x0, x1, y0, y1);
    this.drawTracks(g, toScreen, meZ, now);
    this.deathBanner(g);
    ctx.restore();
  }

  /**
   * The names of the land, written over the ground once the window is zoomed
   * out far enough to be a map (2026-10-05: "Show the names of locations
   * when zooming out on the map"): each area's name on the tile of it the
   * map viewer writes it on, in a dark box the same size whatever the zoom.
   * Only for the areas this character has seen some ground of, or the tile
   * itself, so the names are a map of where you have been rather than of
   * everywhere there is, as the ground is; and only above the ground, since the land's names are the
   * land's. Biggest first, and one that would sit on a name already
   * written is left off until a closer zoom pulls the two apart.
   */
  private drawPlaceNames(g: Game, toScreen: ToScreen, z: number) {
    if (!g.places.length) return;
    const ctx = this.ctx;
    const h = this.markPx(18), pad = this.markPx(5), edge = this.markPx(1.5);
    ctx.save();
    ctx.font = `bold ${this.markPx(12)}px ui-sans-serif, system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const placed: { x: number; y: number; w: number; h: number }[] = [];
    for (const p of g.places) {
      if (!g.knownPlaces.has(p.name) && g.unexplored(p.x, p.y)) continue;
      const { sx, sy } = toScreen(p.x + 0.5, p.y + 0.5, z);
      const w = ctx.measureText(p.name).width + 2 * pad;
      const box = { x: sx - w / 2, y: sy - h / 2, w, h };
      if (placed.some((o) => box.x < o.x + o.w && o.x < box.x + box.w && box.y < o.y + o.h && o.y < box.y + box.h)) continue;
      placed.push(box);
      ctx.fillStyle = "rgba(10,12,16,0.72)";
      ctx.fillRect(box.x, box.y, box.w, box.h);
      ctx.fillStyle = "rgba(230,200,120,0.9)";
      ctx.fillRect(box.x, box.y + box.h - edge, box.w, edge);
      ctx.fillStyle = "#f2f2f2";
      ctx.fillText(p.name, sx, sy);
    }
    ctx.restore();
  }

  /**
   * The places quests have sent you to (2026-10-08: "when npcs give quests,
   * add the locations they mention to the map"): a pin standing on the
   * tile, its point on the tile's middle, with the place's name in a box
   * beside its head. Gold while the quest that needs the place is still to
   * do; grey, with a tick in the head and the name greyed, once it is done.
   *
   * At every zoom and on every level shown, since the mark is where to go
   * rather than what is there: a chest two floors down is marked on the
   * ground over it. Unlike the land's names it is drawn whether or not the
   * ground under it has been seen, because being told of a place you have
   * never been is the whole of what a mark is. Pin and box are the same
   * size on the glass whatever the zoom (markPx), and drawn after the
   * land's names so a mark is never under one; the open ones go on top of
   * the done ones.
   */
  /**
   * The way to everybody the server is showing the player (2026-10-08:
   * "change the find person spell to show the location players for 1
   * minute, with a direction indicator at the edge of the screen. use the
   * same indicator for the wielder of the one, when one of the other great
   * rings is being worn"): see Track. Off the window, an arrowhead at its
   * edge on the line from the middle of the view towards them, with their
   * name, find person's rough distance and the floors between; on it, a
   * ring round their tile with the same label, so the arrow does not just
   * vanish when they come into view. A find person's is where they were
   * when it was said or last seen, not where they are (2026-10-20): see
   * tracksFor. Gold for a find person; for a great
   * ring felt through the One, orange for the Seven and a cold violet for
   * the Nine; Gollum, for an admin, in his own pale green, and the Nine
   * themselves, for an admin, in red; everybody shown from the Seat of
   * Seeing on Amon Hen in a clear sky blue.
   *
   * It is drawn over the dark and takes no notice of it:
   * knowing where somebody is is what the spell or the ring bought.
   */
  private drawTracks(g: Game, toScreen: ToScreen, meZ: number, now: number) {
    if (!g.tracks.length) return;
    const ctx = this.ctx;
    const W = this.worldW(), H = this.worldH();
    const cx = W / 2, cy = H / 2;
    const inset = this.markPx(48);
    const head = this.markPx(14);
    const pad = this.markPx(4), h = this.markPx(16);
    const pulse = 0.5 + 0.5 * Math.sin(now / 250);
    ctx.save();
    ctx.font = `bold ${this.markPx(11)}px ui-sans-serif, system-ui, sans-serif`;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    for (const t of g.tracks) {
      const colour = t.why === "seven" ? "#ffa040" : t.why === "nine" ? "#c4b2ff" : t.why === "gollum" ? "#b8e08a" : t.why === "nazgul" ? "#ff5c5c" : t.why === "seat" ? "#9fd8ff" : "#ffd75e";
      const dz = (t.z ?? 0) - meZ;
      let label = t.name;
      if (t.far) label += ` \u00b7 ${t.far}`;
      if (dz) label += ` \u00b7 ${Math.abs(dz)} ${dz > 0 ? "up" : "down"}`;
      // Their tile on this floor's grid, with no storey's lean: the arrow
      // is about which way to walk, and a floor up is said in words.
      const { sx, sy } = toScreen(t.x, t.y, meZ);
      const tx = sx + TILE / 2, ty = sy + TILE / 2;
      const dx = tx - cx, dy = ty - cy;
      const w = ctx.measureText(label).width + 2 * pad;
      const halfW = Math.max(W / 2 - inset, 1), halfH = Math.max(H / 2 - inset, 1);
      const fit = Math.min(dx ? halfW / Math.abs(dx) : Infinity, dy ? halfH / Math.abs(dy) : Infinity);
      let lx: number, ly: number;
      if (fit >= 1) {
        // In view: a ring round them, breathing so it is seen.
        ctx.globalAlpha = 0.6 + 0.4 * pulse;
        ctx.strokeStyle = colour;
        ctx.lineWidth = this.markPx(2.5);
        ctx.beginPath();
        ctx.arc(tx, ty, Math.max(TILE * 0.7, this.markPx(10)), 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;
        // The label under their feet, clear of their own name and of
        // anything floating over the head of whoever is looking.
        lx = tx;
        ly = ty + Math.max(TILE * 0.7, this.markPx(10)) + h / 2 + pad;
      } else {
        // Off it: an arrowhead on the edge, pointing at them.
        const ax = cx + dx * fit, ay = cy + dy * fit;
        const a = Math.atan2(dy, dx);
        ctx.save();
        ctx.translate(ax, ay);
        ctx.rotate(a);
        ctx.beginPath();
        ctx.moveTo(head, 0);
        ctx.lineTo(-head * 0.7, head * 0.75);
        ctx.lineTo(-head * 0.35, 0);
        ctx.lineTo(-head * 0.7, -head * 0.75);
        ctx.closePath();
        ctx.fillStyle = colour;
        ctx.fill();
        ctx.lineWidth = this.markPx(1.5);
        ctx.strokeStyle = "rgba(10,12,16,0.9)";
        ctx.stroke();
        ctx.restore();
        // The label just inside the arrow, towards the middle, far enough
        // back along it that the box clears the arrowhead whichever edge
        // it is on.
        const back = head + pad + Math.abs(Math.cos(a)) * (w / 2) + Math.abs(Math.sin(a)) * (h / 2);
        lx = ax - Math.cos(a) * back;
        ly = ay - Math.sin(a) * back;
      }
      // Kept on the glass, whichever edge the arrow is on.
      lx = Math.min(Math.max(lx, w / 2 + pad), W - w / 2 - pad);
      ly = Math.min(Math.max(ly, h / 2 + pad), H - h / 2 - pad);
      ctx.fillStyle = "rgba(10,12,16,0.78)";
      ctx.fillRect(lx - w / 2, ly - h / 2, w, h);
      ctx.fillStyle = colour;
      ctx.fillText(label, lx, ly);
    }
    ctx.restore();
  }

  private drawQuestMarks(g: Game, toScreen: ToScreen) {
    if (!g.marks.length) return;
    const ctx = this.ctx;
    const r = this.markPx(6), stem = this.markPx(9), line = this.markPx(1.5);
    const h = this.markPx(18), pad = this.markPx(5), gap = this.markPx(4);
    ctx.save();
    ctx.font = `bold ${this.markPx(12)}px ui-sans-serif, system-ui, sans-serif`;
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    const order = [...g.marks].sort((a, b) => Number(!!b.done) - Number(!!a.done));
    for (const m of order) {
      const { sx, sy } = toScreen(m.x + 0.5, m.y + 0.5);
      const hy = sy - stem - r;
      const fill = m.done ? "#8a9099" : "#ffc93c";
      ctx.globalAlpha = m.done ? 0.8 : 1;
      // The pin: a round head and a point down to the tile.
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(sx - r * 0.75, hy + r * 0.6);
      ctx.arc(sx, hy, r, Math.PI * 0.8, Math.PI * 0.2);
      ctx.closePath();
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.lineWidth = line;
      ctx.strokeStyle = "rgba(10,12,16,0.9)";
      ctx.stroke();
      ctx.beginPath();
      if (m.done) {
        ctx.moveTo(sx - r * 0.45, hy);
        ctx.lineTo(sx - r * 0.1, hy + r * 0.35);
        ctx.lineTo(sx + r * 0.5, hy - r * 0.35);
        ctx.stroke();
      } else {
        ctx.arc(sx, hy, r * 0.35, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(10,12,16,0.9)";
        ctx.fill();
      }
      // The name, in a box beside the head.
      const w = ctx.measureText(m.name).width + 2 * pad;
      const bx = sx + r + gap, by = hy - h / 2;
      ctx.fillStyle = "rgba(10,12,16,0.78)";
      ctx.fillRect(bx, by, w, h);
      ctx.fillStyle = fill;
      ctx.fillRect(bx, by, line, h);
      ctx.fillStyle = m.done ? "#b8bcc2" : "#fff1c8";
      ctx.fillText(m.name, bx + pad, hy);
    }
    ctx.restore();
  }

  /**
   * The darkness pass: black laid over every tile of the floor at the rest
   * of its brightness, 1 − brightAt, and nothing at all over a tile drawn
   * whole. Per tile rather than as one radial gradient, because a light's
   * reach is a Chebyshev square and a gradient is round — see darknessAt —
   * and because every tile then darkens by exactly the number a creature
   * standing on it is washed by.
   *
   * OVER A TILE THE PLAYER KNOWS the wash is what brightAt leaves: a half
   * out of every light, a quarter in one, nothing within your alertness
   * (2026-10-13; see BRIGHT_BASE). Over a tile they have never seen it is
   * the whole of it, black, since the floor was painted for the tiles the
   * player knows and the viewport was filled black behind them: a tile the
   * player has not seen must look like nothing, not like a dim nothing,
   * since the shape of what is missing is a map too.
   *
   * Everything that fades a THING by the dark — a nameplate, a damage
   * number, a puff of dust — asks darkAt instead, which is nought in a
   * light and one out of it: a floor at half itself is a room, and a
   * damage number at half itself is still a number hanging over the thing
   * that hit you.
   *
   * It is laid as one picture, a pixel a tile, blended across the edges
   * between tiles (layBlended), so the edge of a light shades off into the
   * dim over the half tile either side of it instead of stopping at a line.
   */
  private drawDarkness(
    bright: (tx: number, ty: number) => number,
    // Whether this floor has a tile here at all for the player: the dim
    // is for ground they know, and the rest stays black.
    known: (tx: number, ty: number) => boolean,
    // The named type rather than the shape written out: a brace in a
    // signature is a brace, and the tests that read this file find a
    // method's body by counting them from the first one. Spelled out
    // here, this method's body began and ended inside its own parameter
    // list as far as they were concerned, and a test watching it was
    // quietly watching nothing.
    toScreen: ToScreen,
    x0: number,
    x1: number,
    y0: number,
    y1: number,
  ) {
    const w = x1 - x0 + 1,
      h = y1 - y0 + 1;
    if (w <= 0 || h <= 0) return;
    // A pixel a tile, black, as opaque as the tile is washed, laid as one
    // blended picture: see layBlended.
    const pic = this.ctx.createImageData(w, h);
    // The black tiles in runs along each row, a rectangle a run.
    const black: { sx: number; sy: number; n: number }[] = [];
    let run: { sx: number; sy: number; n: number } | null = null;
    let any = false;
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const a = washAt(bright(x, y), known(x, y));
        if (a <= 0) {
          run = null;
          continue;
        }
        any = true;
        pic.data[((y - y0) * w + (x - x0)) * 4 + 3] = Math.round(a * 255);
        if (a < 1) run = null;
        else if (run) run.n++;
        else black.push((run = { ...toScreen(x, y), n: 1 }));
      }
      run = null;
    }
    // Nothing washed at all, as under the sun, lays a clear picture over
    // the whole window, which is a whole window's blend for nothing.
    if (any) this.layBlended(pic, toScreen(x0, y0), "source-over");
    // A tile washed all the way, black, stays black to its edge: the
    // blend shades a known tile off into it, and never lets the half of it
    // next to a known one show what lies under it, since what is missing
    // must look like nothing. Through fillTile so the black squares abut
    // on the device's pixels and rule no grid in the dark.
    const ctx = this.ctx;
    ctx.fillStyle = "#000";
    for (const { sx, sy, n } of black) this.fillTile(sx, sy, n);
  }

  /**
   * Lay a picture a pixel a tile, its top-left pixel the tile at `at`, over
   * the scene stretched to a tile a pixel and SMOOTHED, so each tile is its
   * own colour at its middle and goes over into its neighbour's across the
   * edge between them. Asked for on 2026-10-18: "blend light at transition
   * points". The darkness and the tints were a square of flat colour a tile
   * until then, laid through fillTile, so the edge of a light was a hard
   * step from the whole to a half and a coloured light stopped dead at the
   * edge of its reach. The steps themselves are kept (see BRIGHT_BASE): a
   * tile's middle is exactly as bright as brightAt says, and only the half
   * tile either side of an edge is a blend, so the light still reaches as
   * far as it reaches, and a thing is still seen or not by litAt.
   *
   * One picture rather than a rectangle a tile also means there are no
   * edges inside it to rule a grid with at fractional zooms, which is what
   * fillTile was for here.
   *
   * The canvas is kept and sized to the picture exactly, since smoothing
   * reads past the edge of a source rectangle into whatever lies beyond it.
   */
  private blendCanvas: HTMLCanvasElement | null = null;
  private layBlended(pic: ImageData, at: { sx: number; sy: number }, op: GlobalCompositeOperation, alpha = 1) {
    let c = this.blendCanvas;
    if (!c) c = this.blendCanvas = document.createElement("canvas");
    if (c.width !== pic.width || c.height !== pic.height) {
      c.width = pic.width;
      c.height = pic.height;
    }
    c.getContext("2d")!.putImageData(pic, 0, 0);
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = op;
    ctx.globalAlpha = alpha;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "low";
    ctx.drawImage(c, at.sx, at.sy, pic.width * TILE, pic.height * TILE);
    ctx.restore();
  }

  /**
   * The tint pass: each coloured light's colour laid over the tiles its
   * tinted part reaches, multiplied in and a little of it screened back
   * over (see TINTS), so the dark under it stays dark and what is lit
   * takes the colour. Per tile, as the darkness is, since a light's reach
   * is a square, and blended across the edges between tiles as the
   * darkness is. Only over ground the player knows: the rest is black, and
   * stays so.
   */
  private drawTints(
    lights: readonly LightSource[],
    known: (tx: number, ty: number) => boolean,
    toScreen: ToScreen,
    x0: number,
    x1: number,
    y0: number,
    y1: number,
    z: number | undefined,
    space: OpenSpace | undefined,
    daylight = false,
  ) {
    if (!lights.some((l) => l.tint && (l.tintR ?? 0) > 0)) return;
    const w = x1 - x0 + 1,
      h = y1 - y0 + 1;
    if (w <= 0 || h <= 0) return;
    // Two pictures a pixel a tile, the light multiplied in and the glow
    // screened back over, each blended across the edges between tiles as
    // the darkness is (see layBlended), so a coloured light shades off
    // into the uncoloured round it, and two colours meet in a mix of both.
    // A tile no tint reaches is left clear.
    const mul = this.ctx.createImageData(w, h);
    const glow = this.ctx.createImageData(w, h);
    let any = false;
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        if (!known(x, y)) continue;
        const tint = tintAt(x, y, lights, z, space);
        if (!tint) continue;
        any = true;
        const i = ((y - y0) * w + (x - x0)) * 4;
        // In daylight the colour is a glow round the bearer rather than
        // the whole of what lights the ground: strongest on their own tile
        // and fading out to the edge of its reach (tintAt's near), so a
        // torch on the grass reads as a warm patch rather than a square of
        // dirt.
        const put = (pic: ImageData, rgb: RGB, alpha: number) => {
          pic.data.set(rgb, i);
          pic.data[i + 3] = Math.round(alpha * 255);
        };
        put(mul, tint.light, daylight ? DAYLIGHT_TINT * tint.near : TINT_STRENGTH * tint.cover);
        put(glow, tint.glow, daylight ? DAYLIGHT_GLOW * tint.near : TINT_GLOW * tint.cover);
      }
    }
    if (!any) return;
    const at = toScreen(x0, y0);
    this.layBlended(mul, at, "multiply");
    this.layBlended(glow, at, "screen");
  }

  /**
   * The aggro rings, drawn only with the debug switch on: the machine
   * showing its working for the one rule in the game a player otherwise
   * has to learn by being bitten.
   *
   * WHAT THE RULE IS. A monster notices somebody when the distance
   * between the two of them is no more than the monster's alertness plus
   * that player's trigger range — its keenness, and the noise they are
   * making. That is the ordinary condition for two rings, one round each
   * of them, to overlap, and drawing it as two rings is the whole reason
   * this exists. The server sends the monster's half (alert, and how much
   * of it is anger) and the client already knows its own clank, so the
   * sum is done here and only here; the server never sends a per-viewer
   * answer, so nothing drawn can drift from what the world does.
   *
   * MOST RINGS ARE ONE TILE ACROSS, and that is the picture, not a bug.
   * Ten of the fifteen kinds in the bestiary have no keenness of their
   * own at all — a rat's alertness is nought — so what is drawn round one
   * is a square of radius nought, which is the single tile it is standing
   * on. The reach that actually catches anybody is the player's: an
   * ordinary character makes three tiles of noise just by being a person
   * walking about, and that is the meeting line drawn outside the rat's
   * one tile. It is worth saying plainly because the drawing looks broken
   * the first time and is not: the monsters stopped having radii, and the
   * player got one.
   *
   * WHY THEY ARE SQUARES. Distance in this world is Chebyshev — the
   * number of steps, diagonals costing one — so the set of tiles within
   * four of something is a nine-by-nine square and not a circle. A round
   * ring would look nicer and would be a lie about every corner of it, and
   * a lie about which tiles are safe is the worst possible thing for a
   * drawing whose entire job is to say which tiles are safe.
   *
   * THE NEGATIVE CLANK, which is the hard part. A spidersilk cloak is −6
   * against a body's own 3, so an archer wearing one and little else has
   * a trigger range of −3, and there is no such ring. Three ways out were
   * considered and two rejected.
   *
   * Drawing nothing round a player with a negative clank and calling it a
   * day would be the easy one, and it is a quiet lie: the cloak is the
   * strongest single piece of gear in the game for staying unnoticed, and
   * the debug view that is supposed to explain noticing would be the one
   * place it does not appear. Clamping the radius at zero is the same lie
   * with arithmetic in front of it, and worse, because it makes the cloak
   * look like a piece that merely fails to add noise rather than one that
   * takes noise away.
   *
   * So: the sum is what matters, and the sum is drawn. Round every monster
   * there are two squares. The first is its own alertness, at radius
   * alert — a fact about the monster, the same for everybody, and what it
   * would hear of a person in nothing at all. The second is the MEETING
   * LINE, at radius alert + yourClank: the edge of the ground from which
   * this monster would actually notice YOU. A loud viewer's meeting line
   * sits outside the alertness ring, because their noise reaches out to
   * meet it early. A cloaked viewer's sits inside it. When the clank is
   * zero the two coincide and only one is drawn, which is also the right
   * picture — for a silent person the monster's reach is the whole of it,
   * and for most of the bestiary that is one tile.
   *
   * And when alert + yourClank comes out below zero — a rat's nought
   * against a cloaked archer's −3 — there is no meeting line to draw,
   * because there is no distance at all, not even the next tile, at which
   * that rat can hear that archer. The drawing says so by drawing no
   * meeting line and putting the alertness ring in the deaf colour: the
   * thing is as alert as it ever was, and none of it reaches you. That is
   * the honest picture of a negative radius, and it is the same rule the
   * server runs — rangesMeet floors nothing either.
   *
   * Round the viewer themselves there is one more square, at radius
   * abs(clank), red when the clank is positive and dark red otherwise. Same size for +6 and −6 and opposite colours,
   * because the magnitudes are the same thing pointing opposite ways: six
   * tiles added to every monster's reach in the world, or six taken off
   * it. It is drawn at nought as well, where it is one tile: the ground
   * you are standing on.
   * Nothing here changes what the server does; all of it is a description
   * of a number the server already computed.
   *
   * THE OTHER WAY ROUND. Since 2026-10-12 players notice by the same rule:
   * you see a monster when it is no further off than your alertness and
   * its clank together. So each monster carries a red square at its own
   * clank (the server's triggerRange, its light under the ground) and you
   * a blue one at your alertness (2026-10-16: "Show creature clank as a
   * red outline in debug, show alertness as blue"): blue is always
   * somebody's noticing, red always somebody's noise, and a blue touching
   * a red is the one noticing the other.
   *
   * WHOSE RINGS. Every monster in sight on your own level, whether or not
   * it can hear you, because the ring worth having is the one you can read
   * before anything has happened — one that appeared only once something
   * had already turned would arrive too late to be any use. Only your own
   * player ring, and nobody else's: another player's clank moves THEIR
   * meeting line and not yours, so five rings in a party would be four
   * pieces of information about somebody else's danger laid over the one
   * about your own. Other players do get their red square, though, since
   * 2026-10-20 ("in debug mode, show us the clank of players"): players
   * see each other by the same rule, so it is the red half of whether you
   * make them out, and the server sends it under the switch as it does a
   * monster's. No blue round them: theirs is the same as yours.
   *
   * A FIGHT is drawn in red: since 2026-10-16 a monster no longer grows
   * keener when attacked, and whoever attacks is louder for a few seconds
   * instead, and since 2026-10-21 whoever is attacked too, so the
   * fighters' red squares swell and the blue ones stay.
   */
  /**
   * The world at a size where nothing can be drawn: flat colour a tile,
   * a dot a creature, and no text at all. It is the old minimap, in the
   * window, which is what was asked for when the zoom was asked for
   * out that far (2026-09-20) — and the reason it is a separate pass
   * rather than the same one with smaller sprites is that a 32-pixel
   * sprite squeezed into four is both unreadable and no cheaper.
   *
   * Everything the close view draws over the ground — items, names,
   * health bars, damage numbers — is left out on purpose. At four pixels
   * a tile a name is wider than the building it stands in, and a number
   * over a creature covers the ten tiles round it. What is left is what a
   * map is for: where the ground goes, and who is on it.
   *
   * THE DARK IS NOT LEFT OUT, and it was until 2026-09-22 — it used to be
   * named in the list above with the names and the numbers. It does not
   * belong in that list. The things there are left out because they are
   * illegible at this size; a wash is the same flat rectangle the ground
   * is drawn as and is legible at any size at all. What leaving it out
   * actually did was make the one view that fits the whole country on
   * screen the one view that showed all of it lit, so zooming out was
   * seeing further. See the daylight wash in draw.
   */
  private drawFlat(
    g: Game,
    toScreen: ToScreen,
    z: number,
    x0: number,
    x1: number,
    y0: number,
    y1: number,
    // How bright a tile of a level is drawn: brightAt, asked of that level.
    bright: (tx: number, ty: number, tz: number) => number,
  ) {
    const ctx = this.ctx;
    // Clamped to the world rather than run over the whole window, for two
    // reasons that happen to be the same fix. Off the edge there is
    // nothing, and the black the window was cleared to says so (the close
    // view is clamped the same way since 2026-09-28; before that it asked
    // tileAt for tiles off the edge and drew the water it was given, which
    // at five hundred tiles drew the whole country as an island in a sea
    // that is not there). And a window this
    // wide would otherwise paint a quarter of a million rectangles a
    // frame, most of them outside a map 124 tiles across.
    const cx0 = Math.max(0, x0), cx1 = Math.min(g.mapW - 1, x1);
    const cy0 = Math.max(0, y0), cy1 = Math.min(g.mapH - 1, y1);
    // The ground is one picture, a pixel a tile, stretched over the window
    // at the zoom's size with no smoothing, so every tile is still a block
    // of its own flat colour. It was a rectangle a tile until 2026-09-28,
    // when the world was doubled ("double the size of the map, increasing
    // the level of detail"): the widest zoom then had four times the tiles
    // to lay, a whole window of them, and took a fifth of a second a frame
    // to lay them. The picture is painted once a floor (see flatFloor).
    //
    // The two corners are snapped to whole device pixels the way fillTile
    // snaps a tile's, so the picture's edge does not blend with the black
    // it is laid on, and inside it there are no edges to blend: stretching
    // a picture without smoothing gives each tile whole device pixels, a
    // pixel wider or narrower than its neighbour at a fractional zoom,
    // which is the same answer fillTile gives one tile at a time.
    if (cx0 <= cx1 && cy0 <= cy1) {
      const a = toScreen(cx0, cy0), b = toScreen(cx1 + 1, cy1 + 1);
      const s = this.scale;
      const X0 = Math.round(a.sx * s) / s, Y0 = Math.round(a.sy * s) / s;
      const X1 = Math.round(b.sx * s) / s, Y1 = Math.round(b.sy * s) / s;
      const smooth = ctx.imageSmoothingEnabled;
      ctx.imageSmoothingEnabled = false;
      // Over the ground and every level between, when the floor drawn is
      // above the ground — looked at, or stood on (2026-10-07: "when looking
      // up levels above ground, show lower levels dimmed down to the ground
      // level"; 2026-10-13: "-25% for areas visible on a different level").
      // Pictures already painted, so it is a draw a level. How dim they are
      // is the wash's below, asked of the level that shows at each tile, so
      // they are a quarter down from the floor drawn, which is UNDER_KEEP
      // stood on a roof and a half on a look up from the ground (2026-10-16:
      // "the currently active level should be brighter than the surrounding
      // visible levels"). The map view fades by UNDER_KEEP; see levelsUnder.
      // What stands on them is not dotted: the dots are for the floor drawn,
      // as a click is.
      //
      // Past the world's edge, above ground, each picture's edge is
      // stretched on out to the window's, as the close view lays the edge
      // tile on past it (2026-10-17: "The edges of the map should show more
      // of whatever the edge tile is beyond what is visible"), and the
      // floors' east edge as desert, as the close view lays it (see
      // groundPast); see runOn.
      const edges = z >= 0 ? { l: x0 < 0, r: x1 >= g.mapW, t: y0 < 0, b: y1 >= g.mapH } : null;
      const lay = (pic: HTMLCanvasElement, sx: number, sy: number, east?: string) => {
        ctx.drawImage(pic, sx, sy, cx1 - cx0 + 1, cy1 - cy0 + 1, X0, Y0, X1 - X0, Y1 - Y0);
        if (edges) this.runOn(pic, sx, sy, cx1 - cx0 + 1, cy1 - cy0 + 1, X0, Y0, X1, Y1, edges, east);
      };
      const desert = TILE_COLORS[Tile.Sand];
      const under = levelsUnder(z);
      for (const lz of under) lay(this.flatFloor(g, lz), cx0, cy0, desert);
      lay(this.flatFloor(g, z), cx0, cy0, desert);
      // And the dark over it, under the dots: see below. It is a picture
      // too, for the same reason and since the same day, laid over the
      // ground's the same way — the surface is washed everywhere past the
      // viewer's own sight, which at this zoom is nearly the whole window.
      //
      // Known on a level under counts as known, and is left as the fill
      // above left it: the black the wash lays over ground never seen
      // would otherwise be laid over the air round a roof as well, and
      // cover the country drawn under it just above.
      const onZ = (tx: number, ty: number) => g.tileOn(z, tx, ty) !== undefined;
      const underOnly = (tx: number, ty: number) => !onZ(tx, ty) && under.some((lz) => g.tileOn(lz, tx, ty) !== undefined);
      const known = (tx: number, ty: number) => onZ(tx, ty) || underOnly(tx, ty);
      // The level that shows at a tile: the floor drawn, or the highest
      // under it that has the tile, which is what was laid last there.
      const showing = (tx: number, ty: number) => {
        if (onZ(tx, ty)) return z;
        for (let i = under.length - 1; i >= 0; i--) if (g.tileOn(under[i], tx, ty) !== undefined) return under[i];
        return z;
      };
      const w = cx1 - cx0 + 1, h = cy1 - cy0 + 1;
      lay(this.flatWash((tx, ty) => bright(tx, ty, showing(tx, ty)), known, cx0, cy0, w, h), 0, 0);
      ctx.imageSmoothingEnabled = smooth;
    }
    // The dark over the ground, under the dots, by the same brightAt the
    // close view uses.
    //
    // `known` is asked of the ground rather than answered true, because
    // down here it is a real question: a cave tile the player has never
    // been to is not painted at all, and washing it would be drawing a
    // map of a cave nobody has walked. Above ground it is the same
    // question since 2026-09-28 ("don't send players the entire map. make
    // them have to explore and record it as they go"): the ground comes as
    // it is seen, and what has not been seen is not painted and not
    // washed, so the far zoom is a map of where you have been.
    //
    // It was laid a tile at a time through drawDarkness until the world was
    // doubled on 2026-09-28 ("double the size of the map, increasing the
    // level of detail"), which is the same wash tile for tile: flatWash
    // asks bright and known of every tile and washAt of the answer, exactly
    // as drawDarkness does, and writes the alpha into a picture instead of
    // laying a rectangle.
    // A dot a creature, at least two screen pixels across whatever the
    // zoom, since a creature drawn smaller than the eye can catch is a
    // creature that is not on the map at all. Yours is white, as it was.
    const me = g.me();
    const dot = Math.max(TILE, this.markPx(3));
    const blip = (x: number, y: number, color: string) => {
      const { sx, sy } = toScreen(x, y);
      ctx.fillStyle = color;
      ctx.fillRect(Math.round(sx + TILE / 2 - dot / 2), Math.round(sy + TILE / 2 - dot / 2), dot, dot);
    };
    for (const c of g.creatures.values()) {
      if ((c.z ?? 0) !== z || c.hp <= 0) continue;
      blip(c.x, c.y, c.id === g.myId ? "#fff" : c.kind === "player" ? "#22e8ff" : "#f33");
    }
    if (me && (me.z ?? 0) === z) blip(me.x, me.y, "#fff");
  }

  /**
   * A flat picture's edges run on past the world's: the w by h tiles from
   * (sx, sy) of `pic` are laid at X0..X1, Y0..Y1, and on each side the
   * world ends inside the window its edge row or column is stretched out
   * to the window's edge, and its corner pixel over the corner beyond.
   * With `east`, what runs on past the east edge is that colour wherever
   * the edge column has a tile: the desert of groundPast.
   */
  private runOn(pic: HTMLCanvasElement, sx: number, sy: number, w: number, h: number, X0: number, Y0: number, X1: number, Y1: number, edges: { l: boolean; r: boolean; t: boolean; b: boolean }, east?: string) {
    const ctx = this.ctx;
    const W = this.worldW(), H = this.worldH();
    const L = edges.l ? 0 : X0, R = edges.r ? W : X1, T = edges.t ? 0 : Y0, B = edges.b ? H : Y1;
    const ex = sx + w - 1, ey = sy + h - 1;
    // The east column, recoloured when the east is to be a colour of its
    // own: it is drawn into a column of its own, and the colour laid over
    // only what it covers, so ground never seen stays clear.
    let col = pic, cx = ex, cy = sy;
    if (east && edges.r) {
      let c = this.eastColumn;
      if (!c || c.height < h) {
        c = document.createElement("canvas");
        c.width = 1;
        c.height = Math.max(h, c ? c.height : 0);
        this.eastColumn = c;
      }
      const pc = c.getContext("2d")!;
      pc.clearRect(0, 0, 1, c.height);
      pc.drawImage(pic, ex, sy, 1, h, 0, 0, 1, h);
      pc.globalCompositeOperation = "source-in";
      pc.fillStyle = east;
      pc.fillRect(0, 0, 1, h);
      pc.globalCompositeOperation = "source-over";
      col = c;
      cx = 0;
      cy = 0;
    }
    if (edges.l) ctx.drawImage(pic, sx, sy, 1, h, L, Y0, X0 - L, Y1 - Y0);
    if (edges.r) ctx.drawImage(col, cx, cy, 1, h, X1, Y0, R - X1, Y1 - Y0);
    if (edges.t) ctx.drawImage(pic, sx, sy, w, 1, X0, T, X1 - X0, Y0 - T);
    if (edges.b) ctx.drawImage(pic, sx, ey, w, 1, X0, Y1, X1 - X0, B - Y1);
    if (edges.l && edges.t) ctx.drawImage(pic, sx, sy, 1, 1, L, T, X0 - L, Y0 - T);
    if (edges.r && edges.t) ctx.drawImage(col, cx, cy, 1, 1, X1, T, R - X1, Y0 - T);
    if (edges.l && edges.b) ctx.drawImage(pic, sx, ey, 1, 1, L, Y1, X0 - L, B - Y1);
    if (edges.r && edges.b) ctx.drawImage(col, cx, cy + h - 1, 1, 1, X1, Y1, R - X1, B - Y1);
  }
  private eastColumn: HTMLCanvasElement | null = null;

  /**
   * One floor in its flat colours, a pixel a tile, for drawFlat to stretch
   * over the window: TILE_COLORS for every tile the floor has, magenta for
   * a tile with no colour, and nothing at all where the floor has no tile,
   * so what shows through there is the black the frame was cleared to, as
   * it did when the tiles were laid one at a time.
   *
   * Painted the first time the floor is wanted and kept, since the ground
   * does not change under a world: a new welcome is a new world, and the
   * pictures go with the ground they were painted from. What changes is
   * how much of it has been seen (2026-09-28: "don't send players the
   * entire map. make them have to explore and record it as they go"), and
   * each tile seen since the welcome is put into the pictures already
   * painted as a pixel of its own, rather than a whole floor painted again
   * for every step.
   */
  private flatFloor(g: Game, z: number): HTMLCanvasElement {
    if (this.flatFloorsOf !== g.tiles) {
      this.flatFloors.clear();
      this.flatFloorsOf = g.tiles;
      g.takeRevealed();
    }
    const fresh = g.takeRevealed();
    if (fresh === null) this.flatFloors.clear();
    else {
      for (const r of fresh) {
        const pic = this.flatFloors.get(r.z)?.getContext("2d");
        if (!pic) continue;
        pic.fillStyle = TILE_COLORS[r.t] ?? "#f0f";
        pic.fillRect(r.x, r.y, 1, 1);
      }
    }
    const kept = this.flatFloors.get(z);
    if (kept) return kept;
    const c = document.createElement("canvas");
    c.width = Math.max(1, g.mapW);
    c.height = Math.max(1, g.mapH);
    const pic = keptContext(c);
    const img = pic.createImageData(c.width, c.height);
    const rgb = new Map<number, number[]>();
    for (let y = 0; y < g.mapH; y++) {
      for (let x = 0; x < g.mapW; x++) {
        const t = g.tileOn(z, x, y);
        if (t === undefined) continue;
        let col = rgb.get(t);
        if (!col) {
          col = hexRGB(TILE_COLORS[t] ?? "#f0f");
          rgb.set(t, col);
        }
        const i = (y * c.width + x) * 4;
        img.data[i] = col[0];
        img.data[i + 1] = col[1];
        img.data[i + 2] = col[2];
        img.data[i + 3] = 255;
      }
    }
    pic.putImageData(img, 0, 0);
    this.flatFloors.set(z, c);
    return c;
  }

  /**
   * The flat scene's dark, a pixel a tile, for w by h tiles from (x0, y0):
   * black, as opaque as washAt says for the tile's brightness, and clear
   * where it is drawn whole. The same questions drawDarkness asks
   * of each tile, answered into one picture rather than as a rectangle a
   * tile, since at the widest zoom the wash covers a whole window of them.
   * The canvas is kept and reused frame to frame, grown when it has to be.
   */
  private flatWashCanvas: HTMLCanvasElement | null = null;
  private flatWash(
    bright: (tx: number, ty: number) => number,
    known: (tx: number, ty: number) => boolean,
    x0: number,
    y0: number,
    w: number,
    h: number,
  ): HTMLCanvasElement {
    let c = this.flatWashCanvas;
    if (!c || c.width < w || c.height < h) {
      c = document.createElement("canvas");
      c.width = Math.max(w, c ? c.width : 0);
      c.height = Math.max(h, c ? c.height : 0);
      this.flatWashCanvas = c;
    }
    const pic = c.getContext("2d")!;
    const img = pic.createImageData(w, h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const a = washAt(bright(x0 + x, y0 + y), known(x0 + x, y0 + y));
        if (a <= 0) continue;
        img.data[(y * w + x) * 4 + 3] = Math.round(a * 255);
      }
    }
    pic.putImageData(img, 0, 0);
    return c;
  }

  private drawAggroRings(
    g: Game,
    toScreen: (tx: number, ty: number) => { sx: number; sy: number },
    meZ: number,
  ) {
    const you = g.you;
    if (!you || !you.debug) return;
    const ctx = this.ctx;
    // A Chebyshev radius r round a tile is the square of side 2r+1
    // centred on it, so that is what is stroked. The half-pixel keeps a
    // one-pixel line on a pixel instead of across two of them.
    //
    // Red is stroked just outside the edge of its tiles and blue just
    // inside, so a clank and an alertness of the same radius are two lines
    // side by side rather than one drawn over the other (2026-10-16: "Make
    // sure the clank and alertness outlines are both visible if they are
    // the same radius. Make them bolder too"). Either way the line hugs
    // the edge of the last tile it covers.
    const square = (cx: number, cy: number, r: number, color: string, dash: number[], width: number, outside = false) => {
      const { sx, sy } = toScreen(cx - r, cy - r);
      const side = (2 * r + 1) * TILE;
      const off = outside ? -width / 2 : width / 2;
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.setLineDash(dash);
      ctx.strokeRect(Math.round(sx) + off, Math.round(sy) + off, side - 2 * off, side - 2 * off);
      ctx.setLineDash([]);
    };
    const clank = you.clank;
    for (const c of g.creatures.values()) {
      if (c.hp <= 0 || (c.z ?? 0) !== meZ) continue;
      // Players have no alertness and never did; monsters do, and most of
      // them now have none of it, which is why the test here is what the
      // thing IS rather than what its number comes to. Alertness rides on
      // the wire with omitempty, so a rat's nought and a switch that is
      // off look exactly alike from here — the switch is gated once at the
      // top of this method and does not need a second, weaker guess made
      // about it per creature. Skipping on `alert <= 0`, which is what
      // this used to do, would now draw nothing at all round ten of the
      // fifteen kinds in the game: exactly the ones whose ring is most
      // worth seeing, because a ring one tile across is a surprising
      // thing to be told and a player will not believe it until they have
      // watched themselves walk past a rat.
      if (c.kind === "player") {
        // Another player's noise, red like a monster's, in the hushed
        // red when it has gone below nothing, at its size either way (as
        // yours is). No blue: a player's alertness is the same as yours.
        if (c.id !== you.id && c.clank !== undefined) {
          square(c.x, c.y, Math.abs(c.clank), c.clank < 0 ? AGGRO_CLANK_HUSHED : AGGRO_CLANK, [10, 6], 2, true);
        }
        continue;
      }
      // Its own noise first, red and under the blue: how far past your
      // alertness it is seen, the other half of the rule turned round.
      // Absent only for a state sent before the switch came on.
      if (c.clank !== undefined) square(c.x, c.y, c.clank, AGGRO_CLANK, [10, 6], 2, true);
      const alert = c.alert ?? 0;
      // The rule, spelled once so it can be read against rangesMeet in
      // world.go: a monster notices you when the distance is no more than
      // its alertness plus your trigger range.
      //
      // It is not a square round the monster, and it used to be, which
      // was wrong in a way that got much more wrong when most of the
      // bestiary dropped to no keenness at all. A square of radius
      // alert + clank centred on a rat is the old "the monster does all
      // the noticing, with a fudge factor for what you are wearing"
      // framing drawn out in dashes — and with the rat's own half at
      // nought, the whole of that square was the player's clank, sitting
      // round a rat, looking for all the world like the rat's own reach.
      // A monster's own clank is for players seeing it, not for this; your
      // half is drawn round you at the bottom of this method,
      // and the two rings touching IS the meeting. That is the entire
      // reason the rule is written as two ranges rather than a sum.
      //
      // What the sum is still needed for is the one thing two rings
      // cannot show: a reach that has gone below nothing.
      const meets = alert + clank;
      const deaf = meets < 0;
      // Its reach: the one ring every monster always has, at its own
      // alertness and nothing else. A fight no longer swells it (since
      // 2026-10-16 an attack makes the attacker louder instead, which is
      // the red ring round them growing), so there is no angry ring.
      //
      // Deaf when there is no square for "it cannot hear you however
      // close you stand" — the radius would be negative — so its own
      // ring is drawn in the deaf colour and that is the message.
      if (deaf) square(c.x, c.y, alert, AGGRO_DEAF, [2, 5], 2);
      else square(c.x, c.y, alert, AGGRO_ALERT, [6, 4], 2);
      // And nothing in words over its head. There was a "+N" for an angry
      // monster, on the argument that a swollen ring can be wider than the
      // screen and the number is what is left when the ring has walked
      // off it; it came off on 2026-09-19 (\"don't show the alertness
      // popup text over monsters"). The rings are the overlay, and a
      // number floating over a head is the one thing on screen that reads
      // as the game talking rather than the world drawn — it sat where
      // damage floats and speech sit, saying neither.
    }
    // Your own trigger range, round you, in whichever direction it
    // points — and it is drawn at nought too, which it did not used to
    // be. A clank of nought once meant a character who had simply not
    // put anything on yet, and a square of radius nought round them said
    // nothing worth the ink. It now means quiet kit chosen with care: the
    // quietest anybody gets without going below nothing. The one-tile
    // square is the honest picture of it — your half of the rule is the
    // ground you are standing on, and the whole of what any monster can
    // hear of you is its own keenness, which for most of them is the tile
    // THEY are standing on.
    // And your own alertness, blue like theirs, which is what a monster's
    // red ring has to reach for you to see it.
    const me = g.me();
    if (me && (me.z ?? 0) === meZ) {
      square(me.x, me.y, Math.abs(clank), clank < 0 ? AGGRO_CLANK_HUSHED : AGGRO_CLANK, [10, 6], 2, true);
      square(me.x, me.y, you.alert ?? 0, AGGRO_ALERT, [6, 4], 2);
    }
  }

  /**
   * The effect layers every scene shares, above whichever world was drawn
   * under them: arrows in flight, speech, floating numbers and sparks.
   *
   * `dark` is how dark a point of the floor is, from darknessAt; the
   * surface passes nothing and gets a function that says nought
   * everywhere, so nothing here changes by a pixel above ground. Under
   * the ground each layer decides what the dark does to it, and the
   * decision is by what the thing IS:
   *
   * SPEECH IS HEARD. A bubble is drawn wherever it was said, in the dark
   * or out of it, because words are a sound and the dark does nothing to
   * sound — the same reason a line said off screen is already pinned to
   * the edge of the viewport rather than dropped. A monster shouting
   * from the black is the best thing this mechanic has to offer, and a
   * player calling for help from a tile you cannot see is a thing you
   * should hear. Yes, it says where they are; so would their voice.
   *
   * NUMBERS AND SPARKS ARE SEEN, so they fade with the tile they are on
   * and are not drawn at all where it is black. A damage number is not a
   * sound: it is the game telling you a hitpoint moved, and one floating
   * up out of the dark would tell you exactly where the thing that hit
   * you is standing, which is the one piece of information the dark is
   * there to withhold. A miss puff, a block, blood — the same.
   *
   * FIRE IS ITS OWN LIGHT. A wave of fire, a demon's rift, the light of a
   * homecoming, a lava bolt and a demon's beam are all drawn at
   * full strength wherever they are, because they are light sources and
   * a light in the dark is seen. An arrow is wood and is seen only where
   * the light reaches its head, so one shot at you out of the black
   * appears part way along its flight — the archer stays where the dark
   * put them and the arrow does not point back at them until it is
   * close.
   *
   * What was considered and not done: darkening the lot, speech included,
   * by drawing the darkness pass after this one. It is a line shorter and
   * it makes the cave silent, which is the wrong cave.
   */
  private drawOverlays(
    g: Game,
    now: number,
    // Lifted to the level each thing happened on: see ToScreen.
    toScreen: ToScreen,
    // Where a figure at (x, y) on level z is drawn standing: see standAt.
    stand: (x: number, y: number, z: number) => { x: number; y: number; h: number },
    x0: number,
    x1: number,
    y0: number,
    y1: number,
    dark: (tx: number, ty: number) => number = () => 0,
  ) {
    const ctx = this.ctx;
    // Arrows, from wherever the archer is now to the tile they were aimed
    // at. The flight takes the same time whatever the distance, so it reads
    // as a shot rather than a throw. What is drawn is what was loosed — an
    // arrow, a crossbow bolt or a thrown rock (2026-09-27), see missiles.ts
    // — and the lava and the song below.
    for (const s of g.shots) {
      const archer = g.creatures.get(s.from);
      if (!archer) continue;
      const a = creaturePos(archer, now);
      const t = Math.min(1, (now - s.born) / shotMs(s.kind));
      // Seen where its head is now, unless it is fire and lights itself:
      // an arrow, a bolt or a stone is only seen where there is light.
      const lit = s.kind !== "lava" && s.kind !== "song" && s.kind !== "whip" && s.kind !== "lure" ? 1 - dark(a.x + (s.x - a.x) * t, a.y + (s.y - a.y) * t) : 1;
      if (lit <= 0) continue;
      ctx.globalAlpha = lit;
      const from = toScreen(a.x, a.y, archer.z), to = toScreen(s.x, s.y, s.z);
      const fx = from.sx + TILE / 2, fy = from.sy + TILE / 2 - 6;
      const tx = to.sx + TILE / 2, ty = to.sy + TILE / 2;
      const len = Math.hypot(tx - fx, ty - fy);
      if (len < 1) continue;
      const ux = (tx - fx) / len, uy = (ty - fy) / len;
      const head = len * t;
      if (s.kind === "song") {
        // A siren's song crossing the water. It is the one thing in
        // flight that nobody threw, so it is drawn as nothing solid at
        // all: three arcs square across the line of travel, riding out
        // from the singer's mouth to whoever it was aimed at, opening
        // and fading as they go. Round-fronted rather than pointed,
        // because a point is what an arrow and a bolt have and both of
        // those mean "something is being aimed at you and a wall would
        // have stopped it" — which is true of this as well, but what a
        // player has to read off a song is where it came from, and a
        // widening wave leads the eye backwards to its own centre.
        //
        // Pale, and pale on purpose: this arrives over blue water, where
        // the fire colours the other two use would read as something
        // burning. It is a voice.
        const bx = fx + ux * head, by = fy + uy * head;
        ctx.lineCap = "round";
        for (let i = 0; i < 3; i++) {
          // The three ride a little apart so the wave has depth, and the
          // one in front is the brightest: they arrive rather than pass.
          const back = i * 7;
          const cx = bx - ux * back, cy = by - uy * back;
          const spread = 0.55 + i * 0.12;
          const r = 5 + i * 2 + t * 4;
          const face = Math.atan2(uy, ux);
          ctx.strokeStyle = `rgba(226,248,255,${(1 - t * 0.45) * (0.85 - i * 0.22)})`;
          ctx.lineWidth = 2.4 - i * 0.6;
          ctx.beginPath();
          ctx.arc(cx, cy, r, face - spread, face + spread);
          ctx.stroke();
        }
        continue;
      }
      if (s.kind === "whip") {
        // A Balrog's whip (2026-10-12: "Give the balrog a whip and it
        // pulls its target to it"): a lash of fire thrown out from the
        // Balrog to where its target stood, drawn whole from the hand to
        // its tip as it goes, with a ripple running down it that dies away
        // towards the tip, and a crack of sparks where it lands. Its own
        // light, like the lava, since it is fire.
        //
        // Since 2026-10-20 it holds whoever it caught where they stand
        // for as long as the server said, `ms`: out over WHIP_MS, taut
        // and twitching while it holds, and pulling back to the Balrog
        // over the last WHIP_RETRACT_MS, as which the server drags them
        // in. `t` here is the throw; `reach` how much of the line is out.
        const since = now - s.born;
        const total = s.ms ?? WHIP_MS;
        const out = Math.min(1, since / WHIP_MS);
        const back = Math.max(0, Math.min(1, (since - (total - WHIP_RETRACT_MS)) / WHIP_RETRACT_MS));
        const reach = total > WHIP_MS + WHIP_RETRACT_MS ? out * (1 - back) : out;
        const len2 = Math.max(len * reach, 1);
        const px = -uy, py = ux;
        const pts: [number, number][] = [];
        for (let i = 0; i <= 12; i++) {
          const f = i / 12;
          const amp = 6 * (1 - f) * (1 - out * 0.6) + 2 * back * Math.sin(f * Math.PI);
          const wave = Math.sin(f * Math.PI * 3 - since * 0.037) * amp;
          pts.push([fx + ux * len2 * f + px * wave, fy + uy * len2 * f + py * wave]);
        }
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        for (const [lw, col] of [[4, "rgba(59,14,14,0.9)"], [2.2, "rgba(255,122,28,0.95)"], [0.9, "rgba(255,226,140,1)"]] as const) {
          ctx.lineWidth = lw;
          ctx.strokeStyle = col;
          ctx.beginPath();
          ctx.moveTo(pts[0][0], pts[0][1]);
          for (const [x, y] of pts.slice(1)) ctx.lineTo(x, y);
          ctx.stroke();
        }
        if (out > 0.6 && since < WHIP_MS * 1.6) {
          const k = Math.min(1, (out - 0.6) / 0.4 + Math.max(0, since - WHIP_MS) / (WHIP_MS * 0.6));
          const [x, y] = pts[pts.length - 1];
          ctx.strokeStyle = `rgba(255,210,94,${1 - k})`;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          for (let i = 0; i < 6; i++) {
            const a = (i / 6) * Math.PI * 2;
            ctx.moveTo(x + Math.cos(a) * 2, y + Math.sin(a) * 2);
            ctx.lineTo(x + Math.cos(a) * (3 + k * 7), y + Math.sin(a) * (3 + k * 7));
          }
          ctx.stroke();
        }
        ctx.globalAlpha = 1;
        continue;
      }
      if (s.kind === "lure") {
        // A corpse candle's lure (2026-10-15: "Put spirit undead in the
        // dead makes marshes"): a thin wavering thread of its pale light
        // reaching out from the flame to where its target stood, and a
        // few motes drifting back along it towards the flame, the way the
        // eye goes. Its own light, since it is one.
        const len2 = Math.max(head, 1);
        const px = -uy, py = ux;
        const pts: [number, number][] = [];
        for (let i = 0; i <= 10; i++) {
          const f = i / 10;
          const wave = Math.sin(f * Math.PI * 2 + t * 9) * 3 * f;
          pts.push([fx + ux * len2 * f + px * wave, fy + uy * len2 * f + py * wave]);
        }
        const fade = 1 - t * 0.6;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        for (const [lw, col] of [[4, `rgba(120,200,190,${0.25 * fade})`], [1.4, `rgba(214,250,236,${0.9 * fade})`]] as const) {
          ctx.lineWidth = lw;
          ctx.strokeStyle = col;
          ctx.beginPath();
          ctx.moveTo(pts[0][0], pts[0][1]);
          for (const [x, y] of pts.slice(1)) ctx.lineTo(x, y);
          ctx.stroke();
        }
        ctx.fillStyle = `rgba(232,255,246,${fade})`;
        for (let k = 0; k < 4; k++) {
          const f = 1 - ((t * 1.6 + k / 4) % 1);
          const [x, y] = pts[Math.round(f * 10)];
          ctx.fillRect(x - 1, y - 1, 2, 2);
        }
        ctx.globalAlpha = 1;
        continue;
      }
      if (s.kind === "web") {
        // Shelob's web in flight (2026-10-12): a grey-white knot of silk
        // with a strand trailing back to her.
        const bx = fx + ux * head, by = fy + uy * head;
        ctx.strokeStyle = "rgba(232,232,224,0.7)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(fx, fy);
        ctx.lineTo(bx, by);
        ctx.stroke();
        ctx.fillStyle = "#ecece4";
        ctx.beginPath();
        ctx.arc(bx, by, 3, 0, Math.PI * 2);
        ctx.fill();
        continue;
      }
      if (s.kind === "lava") {
        // A bolt of lava: a lump of molten rock with a white-hot heart
        // and a short trail of cooling ember behind it. Round rather than
        // a shaft, and the fire's own colours rather than wood and steel,
        // so nobody mistakes it for something a shield is put up against.
        const trail = Math.max(0, head - 8);
        ctx.strokeStyle = "rgba(214,60,12,0.7)";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(fx + ux * trail, fy + uy * trail);
        ctx.lineTo(fx + ux * head, fy + uy * head);
        ctx.stroke();
        ctx.fillStyle = "#ff7a1e";
        ctx.beginPath();
        ctx.arc(fx + ux * head, fy + uy * head, 3.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#fff6d0";
        ctx.beginPath();
        ctx.arc(fx + ux * head, fy + uy * head, 1.5, 0, Math.PI * 2);
        ctx.fill();
        continue;
      }
      // A demon's bolt used to be drawn here too, as a knot of fire
      // crossing from the demon to whoever it was aimed at. It is a beam
      // from 2026-09-21 and is drawn a tile at a time with the sparks
      // below, because what a player has to read off it is the line it
      // covered rather than where it came from.
      drawMissile(ctx, s.kind, fx + ux * head, fy + uy * head, ux, uy, t, head);
    }

    // The links of a chain lightning, each one drawn whole from the tile it
    // leapt out of to the tile it landed on (asked for on 2026-09-21: "Add a chain
    // lightning spell that deals damage that jumps between nearby
    // monsters").
    //
    // WHOLE, and that is the one decision here that matters. Everything
    // else in flight in this world is drawn as a head part way along its
    // travel, because for an arrow or a lump of lava the thing worth
    // knowing is where it has got to. A chain has nothing travelling in it:
    // the server settles the whole chain and deals the whole of it in one
    // instant, and what a player has to be able to read off the screen is
    // which three things were linked together — so all three segments are
    // drawn at once, at full length, for the whole of their life, and they
    // fade out together. Drawing a head crawling along each segment would
    // have been a picture of a rule the spell does not have.
    //
    // It is its own light, like the fire and the beam and the rift, so the
    // dark is not asked about it. A bolt of lightning in a black cave is
    // seen, and the room round it besides.
    //
    // The jag is worked out from the two ends rather than from a random
    // number, so a link does not writhe about from frame to frame: it is
    // the same crooked line for the whole six hundred milliseconds, which
    // is what lets the eye follow it from one monster to the next. A
    // shimmering one would look more like lightning and be harder to read,
    // and legibility is the whole reason this has a drawing of its own
    // rather than borrowing the beam's.
    for (const a of g.arcs) {
      if (a.shaft) {
        drawShaft(ctx, a, toScreen, Math.min(1, (now - a.born) / SHAFT_MS));
        continue;
      }
      const t = Math.min(1, (now - a.born) / ARC_MS);
      const from = toScreen(a.fx, a.fy, a.z), to = toScreen(a.x, a.y, a.z);
      const ax = from.sx + TILE / 2, ay = from.sy + TILE / 2 - 6;
      const bx = to.sx + TILE / 2, by = to.sy + TILE / 2 - 6;
      const len = Math.hypot(bx - ax, by - ay);
      if (len < 1) continue;
      // Square across the line, which is the direction the kinks go in.
      const px = -(by - ay) / len, py = (bx - ax) / len;
      const steps = 6;
      const bend = (i: number) => {
        if (i === 0 || i === steps) return 0; // both ends are pinned to their tile
        // A stable hash of the two tiles and which kink this is. Integer
        // arithmetic on purpose: the same chain drawn on two machines is
        // the same crooked line.
        const h = Math.sin((a.fx * 71 + a.fy * 131 + a.x * 197 + a.y * 313 + i * 419) * 12.9898) * 43758.5453;
        return ((h - Math.floor(h)) * 2 - 1) * Math.min(7, len / 6);
      };
      const path = () => {
        ctx.beginPath();
        for (let i = 0; i <= steps; i++) {
          const f = i / steps, o = bend(i);
          const x = ax + (bx - ax) * f + px * o, y = ay + (by - ay) * f + py * o;
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
      };
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      // A wide cold halo and a white-hot core inside it. Blue-white rather
      // than the fire's orange, because orange in this world means the one
      // thing armor cannot answer *and is made of fire* — a dragon's wave,
      // a rift, a geomancer's lava. This is also unanswerable and is not
      // fire: it is the demon's energy by a player's hand, and a colour of
      // its own is what says a demon will stand in it untouched.
      ctx.globalAlpha = 1 - t;
      ctx.strokeStyle = "rgba(120,190,255,0.55)";
      ctx.lineWidth = 5;
      path();
      ctx.globalAlpha = 1 - t * 0.7;
      ctx.strokeStyle = "#eaf6ff";
      ctx.lineWidth = 1.6;
      path();
    }
    ctx.globalAlpha = 1;

    // Speech, pinned to the tile it was said on. Chat is heard out to
    // SPEECH_PIN_RANGE (a yell YELL_PIN_RANGE; the server holds it to the
    // same reach, speechRange and yellRange), past the screen's edge, so
    // much of what is said is said off screen; rather than being dropped
    // for the log to carry alone, it is pinned to the edge of the viewport
    // in the direction it came from, at half size. Where a voice is coming
    // from is most of what you want from it — somebody calling for help, or
    // two people talking, is a place on the map as much as it is a line of
    // text — and the half size is what says the speaker is elsewhere.
    //
    // Noises go to the edge on the same terms, and the case for it got
    // stronger rather than weaker the day they were given a hearing
    // distance. A noise no longer carries the whole world the way words
    // do — each kind of creature has its own reach — but those reaches are
    // routinely wider than the screen: the view is nine tiles either side
    // and a dragon is heard at eighteen, a demon at twenty-four. So a roar
    // out of the dark is now the ordinary case rather than the odd one,
    // and dropping it here would leave the one channel that says something
    // has happened and refuses to say where. The orange keeps it apart
    // from speech, and the half size still says the throat is elsewhere.
    const hearer = g.me();
    for (const b of g.bubbles) {
      const onScreen = b.x >= x0 && b.x <= x1 && b.y >= y0 && b.y <= y1;
      // Speech and spell words from off screen are pinned only from within
      // SPEECH_PIN_RANGE. Noises have their own reach, kept on the server.
      // A yell carries twice as far: YELL_PIN_RANGE.
      if (!onScreen && b.kind === "say" && hearer && Math.max(Math.abs(b.x - hearer.x), Math.abs(b.y - hearer.y)) > (b.yell ? YELL_PIN_RANGE : SPEECH_PIN_RANGE)) continue;
      // Words are Tibia's speech yellow; a noise — a Munch., a Chomp., a
      // rat squeaking in the dark — is its creature-sound orange, so the
      // two never read alike. The orange is the console's own #ff8c00 and
      // not a value chosen for the canvas, because the console prints the
      // same line at the same moment: one sound arriving in two places
      // should read as one thing arriving, which is the argument the
      // answer's white already makes a few lines down. An
      // answer to something you asked is white, which is free: nothing
      // else that hangs over a head is drawn in it, and the console prints
      // the same line in the same white, so a sentence read in the log and
      // read over your head reads as one thing rather than two. White is
      // also the one of the three that does not look like speech, which is
      // the point — nobody said this, you were told it.
      // A critical line — the server going down — is red, the console's
      // own red for it, over the head of the player it was said to.
      // Spell words are a say in a colour of their own: see SPELL_COLOR.
      const color = b.spell ? SPELL_COLOR : b.kind === "noise" ? "#ff8c00" : b.kind === "answer" ? "#ffffff" : b.kind === "critical" ? "#ff3b30" : "#f0d060";
      // A line reported from the edge says who said it; one over a head
      // does not. The name is not decoration in either case, it is the
      // thing that makes the line usable: on screen the nameplate is drawn
      // just under the bubble and already says it, so repeating it
      // would print the same word twice a line apart, and off screen there
      // is no nameplate anywhere on the canvas, so without this the whole
      // report is a disembodied sentence pointing north-east. "Name: words"
      // is the log's own shape, deliberately, so the same line read in two
      // places reads as the same line rather than as two events.
      //
      // Noises are named on the same terms, though a Munch. is not
      // something anybody said. The narrow reading — words get a name,
      // sounds do not — gets it backwards: you can usually see who is
      // eating beside you, and the one time a chewing noise is a mystery
      // is when it comes out of the dark, which is the only case this
      // branch draws. The console has printed "Fenwick: Munch." for as
      // long as noises have had a kind of their own and nobody has read it
      // as speech; the orange is what keeps the two apart, and it still
      // does.
      //
      // The name goes through fitSpeech with the words rather than being
      // drawn as a line of its own above them, so it is wrapped and shrunk
      // by the same rules and cannot overrun a box the words respected.
      //
      // Except that a noise is never named at all, on screen or pinned to
      // the edge (2026-09-28: "when creatures make noises, on screen don't
      // show what kind of creature is making it, like "Dragon: Rumble.",
      // should just be "Rumble""): what is making it is for the player to
      // work out, and a rumble from off the edge that says Dragon has
      // given the game away. The console still names it.
      const said = onScreen || !b.from || b.kind === "noise" ? b.text : `${b.from}: ${b.text}`;
      const { lines, size, lh } = this.fitSpeech(said, onScreen ? 1 : SPEECH_OFFSCREEN_SCALE);
      let cx: number, baseY: number;
      if (onScreen) {
        const { sx, sy } = toScreen(b.x, b.y, b.z);
        cx = Math.round(sx) + TILE / 2;
        baseY = Math.round(sy) - 28;
        // Twenty-eight over the tile is where a bubble has always sat, and
        // it is a guess about where the head is — the same guess the
        // nameplate made until it was taught to measure the sprite. So
        // where the speaker is somebody we are drawing, the bubble is
        // lifted to clear their nameplate, and the two labels stop
        // fighting over the same few pixels. The fixed height stays as
        // the floor: a rat's name rides low and its bubble has no reason
        // to come down with it.
        //
        // The measurement is taken off the speaker's standing frame rather
        // than the one they are on mid-step, because a walk cycle that
        // moves the ink up and down a pixel would make the words bob.
        // It is also taken off the sprite alone — the offset a nameplate
        // sits at above its own tile — and not off where that creature is
        // now: the bubble hangs on the tile the words were said on, and
        // the speaker may well have walked out from under it.
        const speaker = g.creatures.get(b.id);
        if (speaker) {
          const over = nameplateY(0, creatureSpriteNow(speaker, false, now), speaker.maxhp);
          baseY = Math.min(baseY, Math.round(sy) + over - SPEECH_NAME_CLEAR);
        }
      } else {
        // Where the line from the middle of the screen out to the speaker
        // leaves the viewport. Taking the crossing rather than clamping each
        // axis on its own is what makes a corner read as a corner: somebody
        // away to the north-east lands on the diagonal, where clamping would
        // put them squarely north or squarely east depending on which
        // distance happened to be larger.
        const { sx, sy } = toScreen(b.x + 0.5, b.y + 0.5);
        const midX = this.worldW() / 2, midY = this.worldH() / 2;
        const dx = sx - midX, dy = sy - midY;
        // The half-extents the text is allowed to reach, its own size kept
        // inside so a line at the edge is never half off it.
        const limX = midX - SPEECH_EDGE_INSET - (SPEECH_TILES_W * TILE * SPEECH_OFFSCREEN_SCALE) / 2;
        const limY = midY - SPEECH_EDGE_INSET - lines.length * lh;
        const t = Math.min(
          Math.abs(dx) > 0.0001 ? limX / Math.abs(dx) : Infinity,
          Math.abs(dy) > 0.0001 ? limY / Math.abs(dy) : Infinity,
        );
        if (!isFinite(t)) continue; // said on the very tile the camera sits on
        cx = midX + dx * t;
        baseY = midY + dy * t;
      }
      lines.forEach((line, i) => {
        const ly = baseY - (lines.length - 1 - i) * lh;
        this.text(line, cx, ly, size, color, "center");
      });
    }

    // Floating damage / heal numbers. Their position stays fractional so they drift
    // smoothly now that the canvas has sub-world-pixel resolution to spare.
    //
    // Over whoever it landed on, where they are drawn, for as long as they
    // are there to follow (see FloatingText.on): the tile is where the
    // server had them, and a walker is drawn up to a step behind it.
    for (const f of g.floats) {
      const age = (now - f.born) / f.ttl;
      const on = f.on !== undefined ? g.creatures.get(f.on) : undefined;
      if (on && on.hp > 0 && (on.z ?? 0) === (f.z ?? 0)) {
        const p = creaturePos(on, now);
        f.ax = p.x;
        f.ay = p.y;
      }
      const ax = f.ax ?? f.x, ay = f.ay ?? f.y;
      const lit = 1 - dark(ax, ay);
      if (lit <= 0) continue;
      const pose = stand(ax, ay, f.z ?? g.me()?.z ?? 0);
      const { sx, sy } = toScreen(pose.x, pose.y, pose.h);
      const fy = sy + 6 + f.drop - age * 28;
      ctx.globalAlpha = (1 - age * age) * lit;
      this.text(f.text, sx + TILE / 2, fy, f.size ?? 12, f.color, "center");
      ctx.globalAlpha = 1;
    }

    // Sparks: death puff, miss puff, armor block, blood from a blow that
    // landed, level-up glow, breaking gear.
    for (const s of g.sparks) {
      // Fire and a rift each burn to their own, longer clock; everything
      // else is a puff.
      const age = (now - s.born) / sparkMs(s);
      // A spark can be due to start later than now: the whip's coils wait
      // for the lash to arrive.
      if (age < 0) continue;
      // Fire, a rift, a homecoming, a fire nova and a light spell are
      // light and are seen in the dark; everything else is
      // seen only by it.
      // A blood bond's thread is lit by where it lands, which is the
      // person bound: a thread running off into the black to nobody that
      // can be seen would say nothing about who is on the end of it.
      const lit = s.kind === "fire" || s.kind === "rift" || s.kind === "beam" || s.kind === "nova" || s.kind === "shriek" || s.kind === "lull" || s.kind === "crown" || s.kind === "coiled" || s.kind === "elbereth" || s.kind === "convince" || s.kind === "light" || s.kind === "sanctify" ? 1 : 1 - dark(s.x, s.y);
      if (lit <= 0) continue;
      const { sx, sy } = toScreen(s.x, s.y, s.z);
      const cx = Math.round(sx) + TILE / 2, cy = Math.round(sy) + TILE / 2;
      ctx.globalAlpha = (1 - age) * lit;
      if (s.kind === "levelup") {
        ctx.strokeStyle = "#ff8";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(cx, cy, 6 + age * 26, 0, Math.PI * 2);
        ctx.stroke();
      } else if (s.kind === "fire") {
        // Over whatever is standing in it: what a wave has caught is the
        // thing worth seeing, and a figure showing through flames is a
        // figure on fire.
        this.fireTile(ctx, Math.round(sx), Math.round(sy), s.x, s.y, age, now);
      } else if (s.kind === "rift") {
        // The rift fades itself, from the inside out, so it is drawn at
        // full strength and left to do it: the alpha the other sparks are
        // faded by would take the black of the gap with it and leave a
        // glow lying on the floor with no hole under it.
        ctx.globalAlpha = 1;
        this.riftTile(ctx, Math.round(sx), Math.round(sy), s.x, s.y, age, now);
      } else if (s.kind === "beam") {
        // A flight by the Eagles (a homecoming until 2026-10-10), at
        // either end of it: a shaft of pale gold standing
        // on the tile, brightest at the floor and thinning away upwards,
        // with motes going up through it. The temple's own colour rather
        // than a colour of its own, because where the light leads is the
        // whole of what it means. It draws over whatever is on the tile:
        // at the near end the person has just gone and at the far end they
        // have just arrived, and in both cases the light is the news.
        const h = TILE * 3;
        const half = (TILE / 2) * (1 - age * 0.5);
        const grad = ctx.createLinearGradient(0, cy - h, 0, cy + TILE / 2);
        grad.addColorStop(0, "rgba(232, 220, 160, 0)");
        grad.addColorStop(1, "rgba(232, 220, 160, 0.8)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.moveTo(cx - half * 0.35, cy - h);
        ctx.lineTo(cx + half * 0.35, cy - h);
        ctx.lineTo(cx + half, cy + TILE / 2);
        ctx.lineTo(cx - half, cy + TILE / 2);
        ctx.closePath();
        ctx.fill();
        // Motes going up the shaft, each starting from its own height so
        // the column is full of them from the moment it appears.
        ctx.fillStyle = "#fff6d0";
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2 + 1.1;
          const my = cy + TILE / 2 - ((age + i / 6) % 1) * (h + TILE);
          ctx.beginPath();
          ctx.arc(cx + Math.cos(a) * half * 0.7, my, 1.6, 0, Math.PI * 2);
          ctx.fill();
        }
      } else if (s.kind === "splash") {
        // A cast landing. Rings spreading out from where the line went in
        // and fading as they widen, which is the one shape everybody
        // already reads as water being hit by something — it is what a
        // stone in a pond does, and nothing else in this game draws it.
        //
        // Three of them, each started a little after the last rather than
        // all at once: one ring is a hoop lying on the floor, three
        // chasing each other outwards are a surface still moving. The
        // later ones are still small when the first is nearly gone, so
        // the whole thing has a middle as well as an edge.
        //
        // They are ellipses rather than circles, squashed to about half
        // height. Every tile in this world is drawn as a square seen from
        // slightly above — that is why a shadow is an oval and a fish is
        // wider than it is tall — so a true circle would stand up out of
        // the lake like a ring held on its side.
        //
        // Drawn over whatever is on the tile, fish included. A splash is
        // the surface, and the surface is above them.
        ctx.lineCap = "round";
        ctx.strokeStyle = "#cfe9ff";
        for (let i = 0; i < 3; i++) {
          const t = age - i * 0.2;
          if (t <= 0) continue;
          const r = 3 + t * TILE * 0.62;
          // Each ring fades on its own age as well as the splash's, so a
          // young ring in an old splash is faint rather than sharp: the
          // water is settling, not being hit again.
          ctx.globalAlpha = (1 - age) * (1 - t) * 0.95;
          ctx.lineWidth = 2.4 - t * 1.6;
          ctx.beginPath();
          ctx.ellipse(cx, cy, r, r * 0.55, 0, 0, Math.PI * 2);
          ctx.stroke();
        }
        // The water thrown up at the strike: a bright crown for the first
        // third and a handful of drops arcing out of it and falling back,
        // which is what makes the rings read as a consequence of
        // something arriving rather than as a ripple that was always
        // there. Gone well before the rings are, because it should look
        // like the moment of impact and not like a fountain.
        if (age < 0.45) {
          const t = 1 - age / 0.45;
          ctx.globalAlpha = t;
          ctx.fillStyle = "#eaf6ff";
          ctx.beginPath();
          ctx.ellipse(cx, cy, 1.5 + t * 4, (1.5 + t * 4) * 0.7, 0, 0, Math.PI * 2);
          ctx.fill();
          for (let i = 0; i < 6; i++) {
            const a = (i / 6) * Math.PI * 2 + 0.5;
            const r = 3 + age * 22;
            // Up first and then down, the same weighted arc a drop of
            // blood takes, since both of them are thrown liquid.
            const fall = age * age * 40 - age * 12;
            ctx.beginPath();
            ctx.arc(cx + Math.cos(a) * r, cy + Math.sin(a) * r * 0.55 + fall, 1.8 * t + 0.6, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      } else if (s.kind === "crown") {
        // Durin's Crown in the Mirrormere (2026-10-16, Azanulbizar): seven
        // stars in a ring on the water where you looked, come up one by one
        // out of the deep, held a while, then sunk again; a pale ring of
        // light round them like the circlet they hang from.
        const rise = Math.min(1, age / 0.3), sink = Math.max(0, (age - 0.75) / 0.25);
        const shown = rise * (1 - sink);
        const rx = TILE * 1.5, ry = TILE * 0.8;
        ctx.globalAlpha = 0.35 * shown;
        ctx.strokeStyle = "rgb(200, 215, 255)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        ctx.stroke();
        for (let i = 0; i < 7; i++) {
          const a = -Math.PI / 2 + (i * Math.PI * 2) / 7;
          const own = Math.min(1, Math.max(0, (age - i * 0.03) / 0.12)) * (1 - sink);
          if (own <= 0) continue;
          const twinkle = 0.75 + 0.25 * Math.sin(now / 180 + i * 1.7);
          const x = cx + Math.cos(a) * rx, y = cy + Math.sin(a) * ry;
          const r = 3.2 * twinkle;
          ctx.globalAlpha = own * 0.5;
          ctx.fillStyle = "rgb(170, 195, 255)";
          ctx.beginPath();
          ctx.arc(x, y, r * 2.2, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = own;
          ctx.fillStyle = "rgb(245, 248, 255)";
          ctx.beginPath(); // a four-pointed star
          ctx.moveTo(x, y - r * 1.8);
          ctx.lineTo(x + r * 0.45, y - r * 0.45);
          ctx.lineTo(x + r * 1.8, y);
          ctx.lineTo(x + r * 0.45, y + r * 0.45);
          ctx.lineTo(x, y + r * 1.8);
          ctx.lineTo(x - r * 0.45, y + r * 0.45);
          ctx.lineTo(x - r * 1.8, y);
          ctx.lineTo(x - r * 0.45, y - r * 0.45);
          ctx.closePath();
          ctx.fill();
        }
      } else if (s.kind === "sanctify") {
        // A sanctification going off over a pile of bones (asked for on
        // 2026-09-27: "when sanctification is cast, do a spell effect for
        // it"). Light coming down rather than anything going up or out: a
        // narrow shaft of the temple's pale gold drops onto the pile, a
        // flat ring of it spreads along the floor where it lands, and a
        // few motes lift off the bones and fade as the shaft goes. It is
        // light, so it is seen in the dark, which is where bones are.
        const gold = "232, 220, 160";
        // The shaft: in fast, held, out slowly.
        const shaft = Math.min(1, age / 0.12) * (1 - Math.max(0, (age - 0.4) / 0.6));
        if (shaft > 0) {
          const top = cy - TILE * 2.4;
          const w = TILE * (0.34 + 0.12 * Math.sin(age * Math.PI));
          const grad = ctx.createLinearGradient(0, top, 0, cy + TILE * 0.2);
          grad.addColorStop(0, `rgba(${gold}, 0)`);
          grad.addColorStop(0.6, `rgba(${gold}, 0.55)`);
          grad.addColorStop(1, `rgba(255, 250, 225, 0.9)`);
          ctx.globalAlpha = shaft;
          ctx.fillStyle = grad;
          ctx.fillRect(cx - w / 2, top, w, cy + TILE * 0.2 - top);
          // A glow on the floor where it lands.
          const glow = ctx.createRadialGradient(cx, cy + TILE * 0.2, 0, cx, cy + TILE * 0.2, TILE * 0.6);
          glow.addColorStop(0, `rgba(255, 250, 225, 0.8)`);
          glow.addColorStop(1, `rgba(${gold}, 0)`);
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.ellipse(cx, cy + TILE * 0.2, TILE * 0.6, TILE * 0.3, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        // The ring on the floor, spreading from under the shaft.
        const ring = Math.max(0, (age - 0.1) / 0.8);
        if (ring > 0 && ring < 1) {
          ctx.globalAlpha = 1 - ring;
          ctx.strokeStyle = `rgb(${gold})`;
          ctx.lineWidth = 2 - ring;
          ctx.beginPath();
          ctx.ellipse(cx, cy + TILE * 0.2, 3 + ring * TILE * 0.7, (3 + ring * TILE * 0.7) * 0.4, 0, 0, Math.PI * 2);
          ctx.stroke();
        }
        // The motes, lifting off the pile on a stagger.
        ctx.fillStyle = "rgb(255, 248, 215)";
        for (let i = 0; i < 6; i++) {
          const u = (age - 0.25 - ((i * 5) % 6) * 0.05) / 0.6;
          if (u <= 0 || u >= 1) continue;
          ctx.globalAlpha = Math.sin(u * Math.PI);
          const x = cx + ((i / 5) - 0.5) * TILE * 0.6 + Math.sin(u * 5 + i) * 1.5;
          const y = cy + TILE * 0.15 - u * TILE * 1.1;
          ctx.beginPath();
          ctx.arc(x, y, 1.6 - u * 0.8, 0, Math.PI * 2);
          ctx.fill();
        }
      } else if (s.kind === "cleanse") {
        // An antidote going off, asked for on 2026-09-24: "give the
        // antidote spell a visible effect on the player". The poison
        // drawn up and out: a handful of drops leaving the body one after
        // another, rising clear of the head, and paling from the venom's
        // own green — the colour a beat of poison lands in — to a clean
        // near-white as they go. The colour change is the whole of the
        // meaning. Green going up and out of a person is the poison
        // leaving, and green turning white on the way is it being made
        // harmless; a drop that stayed green would read as the caster
        // giving off venom, which is the opposite.
        //
        // Drops and not a ring or a number, so it cannot be mistaken for
        // either thing it sits closest to: a level's gold ring spreads
        // along the floor, and mending is a green number that climbs.
        // This is several small shapes climbing at once, each one its own.
        //
        // Each is a teardrop with its point hanging below it, the bulb
        // leading, which is how a thing being pulled upwards looks: the
        // tail is where it came from. They set off on a stagger rather
        // than together, so the column is never empty and never all
        // launched at once, and each rises slowly at first and then
        // faster, as though drawn rather than floating. They start inside
        // the body's width and keep to it, since what is being drawn out
        // is in the person standing on the tile and nowhere else.
        //
        // Pinned to the tile, like the splash, and seen only by the light
        // like every spark that is not itself a light.
        for (let i = 0; i < 7; i++) {
          // When this drop sets off, in a scattered order so neighbours
          // do not leave in a line, and how far up it has got: u runs
          // from nought at the body to one where it is gone.
          const start = ((i * 3) % 7) / 7 * 0.35;
          const u = (age - start) / 0.62;
          if (u <= 0 || u >= 1) continue;
          const rise = u * u * 0.6 + u * 0.4;
          const x = cx + ((i / 6) - 0.5) * TILE * 0.55 + Math.sin(u * Math.PI * 2 + i) * 1.5;
          const y = cy + TILE * 0.25 - rise * TILE * 2;
          const r = 3.3 - u * 2.1;
          // Venom green (#7ed957) to a pale mint, as it rises.
          const g0 = Math.round(126 + (228 - 126) * u);
          const g1 = Math.round(217 + (255 - 217) * u);
          const g2 = Math.round(87 + (232 - 87) * u);
          ctx.fillStyle = `rgb(${g0}, ${g1}, ${g2})`;
          // In quickly and out late: a drop appears as it leaves the body,
          // holds its strength while it climbs and thins away near the
          // top, so the part that is read — green going up — is bright.
          ctx.globalAlpha = Math.min(1, u / 0.15) * (1 - u * u * u * u) * lit;
          ctx.beginPath();
          ctx.moveTo(x - r, y);
          ctx.arc(x, y, r, Math.PI, 0);
          ctx.lineTo(x, y + r * 2.8);
          ctx.closePath();
          // A dark edge, because venom green on grass is green on green:
          // without it the drops are lost on exactly the ground a spider
          // is most often met on. It goes as the drop pales, or the pale
          // drops high up read as dark specks rather than clean ones.
          ctx.strokeStyle = `rgba(16, 40, 12, ${(0.55 * (1 - u)).toFixed(2)})`;
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.fill();
        }
      } else if (s.kind === "mend") {
        // A heal going off (2026-09-27: "add a healing spell that heals
        // instantly"): a few pale green crosses rising off the caster and
        // fading, the sign on the spell's own button. Crosses and not the
        // antidote's drops, since what is happening is the opposite of a
        // thing being drawn out; and rising, since the green number that
        // says how much was closed climbs the same way.
        for (let i = 0; i < 4; i++) {
          const start = ((i * 3) % 4) / 4 * 0.3;
          const u = (age - start) / 0.65;
          if (u <= 0 || u >= 1) continue;
          const x = cx + ((i / 3) - 0.5) * TILE * 0.5;
          const y = cy + TILE * 0.2 - u * TILE * 1.3;
          const r = 3.2 - u * 1.4, w = r * 0.45;
          ctx.globalAlpha = Math.min(1, u / 0.15) * (1 - u * u * u) * lit;
          ctx.fillStyle = "#9ef0a0";
          ctx.strokeStyle = "rgba(12, 48, 16, 0.6)";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.rect(x - w, y - r, w * 2, r * 2);
          ctx.rect(x - r, y - w, r * 2, w * 2);
          ctx.stroke();
          ctx.fill();
        }
      } else if (s.kind === "shriek") {
        // The Witch-king's shriek (2026-10-12): three pale, cold fronts
        // riding out of him to the edge of what it caught, `r` tiles. A
        // square front for the nova's reason: it catches by the larger of
        // the two axes, so a ring would show the corners spared when they
        // are not. Seen in the dark, since it is heard rather than seen.
        const reach = ((s.r ?? 0) + 0.5) * TILE;
        ctx.lineJoin = "round";
        for (let i = 0; i < 3; i++) {
          const t = Math.min(1, Math.max(0, (age - i * 0.12) / 0.6));
          if (t <= 0) continue;
          const h = 6 + (reach - 6) * t;
          ctx.globalAlpha = (1 - age) * (1 - i * 0.25) * lit;
          ctx.strokeStyle = i === 0 ? "#f2f6ff" : "#b9c4e6";
          ctx.lineWidth = 3 - i * 0.8;
          ctx.beginPath();
          ctx.rect(cx - h, cy - h, h * 2, h * 2);
          ctx.stroke();
        }
      } else if (s.kind === "regen") {
        // Fast mending (2026-10-17): a few soft green motes drifting up off
        // the body past the head, each with a faint glow round it. Smaller
        // and slower than the heal spell's crosses, since this comes every
        // beat and the spell is a moment. Each beat sets them off at
        // different places, from when it was born, so a steady mending
        // reads as a gentle shimmer rather than the same picture again.
        const seed = Math.floor(s.born / 97);
        for (let i = 0; i < 5; i++) {
          const start = i * 0.12;
          const u = (age - start) / 0.7;
          if (u <= 0 || u >= 1) continue;
          const off = ((((seed + i * 3) % 7) / 6) - 0.5) * TILE * 0.7;
          const x = cx + off + Math.sin(u * 6 + i * 2) * 2;
          const y = cy + TILE * 0.25 - u * TILE * 1.25;
          const r = 2.6 - u * 1.1;
          const a = Math.min(1, u / 0.2) * (1 - u * u) * lit;
          ctx.globalAlpha = a * 0.35;
          ctx.fillStyle = "#7cff86";
          ctx.beginPath();
          ctx.arc(x, y, r * 2.4, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = a * 0.95;
          ctx.fillStyle = "#e4ffd8";
          ctx.strokeStyle = "rgba(20, 90, 30, 0.7)";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.stroke();
          ctx.fill();
        }
      } else if (s.kind === "vanish") {
        // An admin's /vanish (2026-10-27: "go invisible with a fancy
        // flair"): a violet flash that is gone in the first fifth, and a
        // ring of dark smoke puffs bursting out of where they stood,
        // swelling and thinning as they go, with sparks thrown up through
        // it. Seen in the dark: it is meant to be noticed.
        const flash = Math.max(0, 1 - age * 5);
        if (flash > 0) {
          ctx.globalAlpha = flash * 0.8;
          ctx.fillStyle = "#c9a6ff";
          ctx.beginPath();
          ctx.arc(cx, cy - TILE * 0.2, TILE * (0.3 + age * 2), 0, Math.PI * 2);
          ctx.fill();
        }
        const out = 1 - (1 - age) * (1 - age);
        for (let i = 0; i < 9; i++) {
          const ang = (i / 9) * Math.PI * 2 + 0.4;
          const d = out * TILE * (0.7 + (i % 3) * 0.12);
          const x = cx + Math.cos(ang) * d;
          const y = cy - TILE * 0.2 + Math.sin(ang) * d * 0.7 - age * TILE * 0.3;
          const r = TILE * (0.22 + age * 0.3);
          ctx.globalAlpha = (1 - age) * 0.75;
          ctx.fillStyle = i % 2 ? "#3a3440" : "#5a5260";
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = Math.max(0, 1 - age * 1.5);
        ctx.fillStyle = "#efe2ff";
        for (let k = 0; k < 6; k++) {
          const ang = k * 1.9 + 0.7;
          const d = out * TILE * 0.5;
          ctx.fillRect(cx + Math.cos(ang) * d - 1, cy - TILE * 0.3 - age * TILE * 0.9 + Math.sin(ang) * d * 0.5, 2, 2);
        }
      } else if (s.kind === "summon") {
        // An admin's /summon (2026-10-27: "when summoning using /summon,
        // there should be a visual effect"): /vanish's burst run the other
        // way, in light rather than smoke. A violet ring on the ground
        // draws in for the first half, motes wheeling in along it, then
        // flares up into a pillar of pale light with sparks thrown up out
        // of it. A thing (r 1) gets a smaller one than a creature.
        const k = s.r === 1 ? 0.6 : 1;
        const gy = cy + TILE * 0.25;
        const draw = Math.min(1, age / 0.55);
        const inward = draw * draw;
        if (age < 0.6) {
          const rr = TILE * k * (0.95 - inward * 0.6);
          ctx.globalAlpha = Math.min(1, age * 6) * (age < 0.5 ? 0.9 : (0.6 - age) * 9);
          ctx.strokeStyle = "#b48cff";
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.ellipse(cx, gy, rr, rr * 0.45, 0, 0, Math.PI * 2);
          ctx.stroke();
          ctx.fillStyle = "#efe2ff";
          for (let i = 0; i < 8; i++) {
            const ang = (i / 8) * Math.PI * 2 + inward * 3;
            ctx.fillRect(cx + Math.cos(ang) * rr - 1.5, gy + Math.sin(ang) * rr * 0.45 - 1.5, 3, 3);
          }
        }
        if (age > 0.45) {
          const u = (age - 0.45) / 0.55;
          const fade = 1 - u;
          const h = TILE * k * (0.6 + Math.min(1, u * 4) * 1.0);
          const wd = TILE * k * 0.5 * (1 - u * 0.6);
          ctx.globalAlpha = fade * 0.7;
          ctx.fillStyle = "#d9c4ff";
          ctx.fillRect(cx - wd / 2, gy - h, wd, h);
          ctx.globalAlpha = fade * 0.9;
          ctx.fillStyle = "#f6f0ff";
          ctx.fillRect(cx - wd / 6, gy - h, wd / 3, h);
          const flash = Math.max(0, 1 - u * 3);
          if (flash > 0) {
            ctx.globalAlpha = flash * 0.75;
            ctx.fillStyle = "#c9a6ff";
            ctx.beginPath();
            ctx.ellipse(cx, gy, TILE * k * (0.35 + u), TILE * k * (0.35 + u) * 0.45, 0, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.globalAlpha = fade;
          ctx.fillStyle = "#efe2ff";
          for (let j = 0; j < 6; j++) {
            const ang = j * 1.9 + 0.3;
            const d = TILE * k * 0.35 * u;
            ctx.fillRect(cx + Math.cos(ang) * d - 1, gy - TILE * k * (0.3 + u * 1.2) * (0.6 + (j % 3) * 0.2), 2, 2);
          }
        }
      } else if (s.kind === "smoke") {
        // A pipe's smoke ring (Longbottom, 2026-10-16): a grey ring rising
        // off the smoker's head, widening and thinning as it goes, with a
        // wisp under it.
        const rise = age * TILE * 0.9;
        const ry = cy - TILE * 0.55 - rise;
        ctx.globalAlpha = Math.min(1, age / 0.1) * (1 - age) * 0.9 * lit;
        ctx.strokeStyle = "rgba(40, 40, 40, 0.5)";
        ctx.lineWidth = 4.5 - age * 2;
        ctx.beginPath();
        ctx.ellipse(cx + 4 + age * 6, ry, 6 + age * 9, 3 + age * 3.5, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = "#ecece4";
        ctx.lineWidth = 3 - age * 1.5;
        ctx.stroke();
        ctx.fillStyle = "#c8c8c0";
        for (let k = 0; k < 3; k++) {
          const t = Math.max(0, age - k * 0.15);
          ctx.globalAlpha = (1 - t) * 0.5 * lit;
          ctx.fillRect(cx + 3 + Math.sin(t * 6 + k) * 3, cy - TILE * 0.45 - t * TILE * 0.5, 3, 3);
        }
      } else if (s.kind === "lull") {
        // Old Man Willow's song (2026-10-12): soft grey-green rings
        // drifting out of him to the edge of what it caught, `r` tiles,
        // square for the shriek's reason, with a few motes riding on them.
        const reach = ((s.r ?? 0) + 0.5) * TILE;
        ctx.lineJoin = "round";
        for (let i = 0; i < 3; i++) {
          const t = Math.min(1, Math.max(0, (age - i * 0.18) / 0.7));
          if (t <= 0) continue;
          const h = 8 + (reach - 8) * t;
          ctx.globalAlpha = (1 - age) * (0.7 - i * 0.15) * lit;
          ctx.strokeStyle = i === 1 ? "#d8e2b0" : "#a6b588";
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.rect(cx - h, cy - h, h * 2, h * 2);
          ctx.stroke();
          ctx.fillStyle = "#eef2d0";
          for (let k = 0; k < 8; k++) {
            const a = (k / 8) * Math.PI * 2 + i;
            const px = cx + Math.max(-h, Math.min(h, Math.cos(a) * h * 1.3));
            const py = cy + Math.max(-h, Math.min(h, Math.sin(a) * h * 1.3)) - t * 6;
            ctx.fillRect(px - 1, py - 1, 2, 2);
          }
        }
      } else if (s.kind === "sweep") {
        // A tile of Sauron's mace sweep (2026-11-01): a dark streak with a
        // red edge crossing the tile square to the line from him, so the
        // three together make a swing, and a burst of grit behind it.
        const fx = s.fx ?? s.x, fy = s.fy ?? s.y;
        let ux = s.x - fx, uy = s.y - fy;
        const len = Math.hypot(ux, uy) || 1;
        ux /= len; uy /= len;
        const px = -uy, py = ux;
        const sw = (age - 0.5) * TILE * 1.1;
        ctx.globalAlpha = Math.max(0, 1 - age);
        ctx.lineCap = "round";
        for (const [lw, col] of [[10, "rgba(20,10,10,0.8)"], [5, "rgba(200,40,24,0.9)"], [1.5, "rgba(255,190,120,1)"]] as const) {
          ctx.lineWidth = lw;
          ctx.strokeStyle = col;
          ctx.beginPath();
          ctx.moveTo(cx + px * (sw - TILE * 0.35) - ux * TILE * 0.15, cy + py * (sw - TILE * 0.35) - uy * TILE * 0.15);
          ctx.quadraticCurveTo(cx + px * sw + ux * TILE * 0.1, cy + py * sw + uy * TILE * 0.1, cx + px * (sw + TILE * 0.35) - ux * TILE * 0.15, cy + py * (sw + TILE * 0.35) - uy * TILE * 0.15);
          ctx.stroke();
        }
        ctx.fillStyle = "#6a5a48";
        for (let k = 0; k < 6; k++) {
          const d = TILE * (0.2 + age * 0.5) * (0.6 + (k % 3) * 0.2);
          const a = (k - 2.5) * 0.35;
          const gx = cx + (ux * Math.cos(a) - uy * Math.sin(a)) * d;
          const gy = cy + (uy * Math.cos(a) + ux * Math.sin(a)) * d;
          ctx.fillRect(gx - 1, gy - 1, 2, 2);
        }
      } else if (s.kind === "knockdown") {
        // Knocked down by Sauron's sweep (2026-11-01): a ring of dust round
        // their feet and three stars going round their head while they
        // are held.
        ctx.globalAlpha = Math.min(1, (1 - age) * 4);
        ctx.strokeStyle = "rgba(150,130,100,0.7)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.ellipse(cx, cy + TILE * 0.3, TILE * (0.3 + Math.min(age * 3, 1) * 0.2), TILE * 0.1, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = "#ffe27a";
        for (let k = 0; k < 3; k++) {
          const a = now * 0.008 + (k * Math.PI * 2) / 3;
          const sx2 = cx + Math.cos(a) * TILE * 0.28, sy2 = cy - TILE * 0.45 + Math.sin(a) * TILE * 0.08;
          ctx.beginPath();
          for (let j = 0; j < 5; j++) {
            const b = (j * 4 * Math.PI) / 5 - Math.PI / 2;
            const fn = j === 0 ? "moveTo" : "lineTo";
            ctx[fn](sx2 + Math.cos(b) * 3, sy2 + Math.sin(b) * 3);
          }
          ctx.closePath();
          ctx.fill();
        }
      } else if (s.kind === "coiled") {
        // The Balrog's whip wound round the legs of whoever it holds
        // (2026-10-20), first where it caught them and then where it
        // dragged them: three loops of fire, flickering, fading over the
        // last fifth. Fire, so it is its own light.
        ctx.globalAlpha = Math.min(1, (1 - age) * 5);
        ctx.lineCap = "round";
        for (let i = 0; i < 3; i++) {
          const ly = cy + TILE * (0.08 + i * 0.1);
          const rx = TILE * (0.3 - i * 0.03), ry = TILE * 0.08;
          const flick = Math.sin(now * 0.02 + i * 2.1) * 0.6;
          for (const [lw, col] of [[3.2, "rgba(59,14,14,0.85)"], [1.8, "rgba(255,122,28,0.95)"], [0.7, "rgba(255,226,140,1)"]] as const) {
            ctx.lineWidth = lw;
            ctx.strokeStyle = col;
            ctx.beginPath();
            ctx.ellipse(cx, ly, rx, ry, flick * 0.1, 0, Math.PI * 2);
            ctx.stroke();
          }
        }
      } else if (s.kind === "webbed") {
        // Shelob's web lying over whoever it caught (2026-10-12): it lands
        // when the throw arrives, holds as long as the server said, and
        // fades over the last fifth. Spokes and two rings, grey-white.
        const since = now - s.born;
        if (since < WEB_FLIGHT_MS) continue;
        const grow = Math.min(1, (since - WEB_FLIGHT_MS) / 120);
        ctx.globalAlpha = Math.min(1, (1 - age) * 5) * 0.85 * lit;
        ctx.strokeStyle = "#e8e8e0";
        ctx.lineWidth = 1.2;
        const rad = TILE * 0.48 * grow;
        ctx.beginPath();
        for (let i = 0; i < 8; i++) {
          const a = (i / 8) * Math.PI * 2 + 0.2;
          ctx.moveTo(cx, cy);
          ctx.lineTo(cx + Math.cos(a) * rad, cy + Math.sin(a) * rad);
        }
        for (const k of [0.45, 0.85]) {
          for (let i = 0; i <= 8; i++) {
            const a = (i / 8) * Math.PI * 2 + 0.2;
            const x = cx + Math.cos(a) * rad * k, y = cy + Math.sin(a) * rad * k;
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
      } else if (s.kind === "nova") {
        // A fire nova. The ground round the caster catches and the fire
        // goes out to the reach the server sent in `r`.
        //
        // A SQUARE FRONT, and not a ring, which is the one decision here
        // that matters. Every instinct says a nova is a circle, and a
        // circle would be prettier. But the spell catches everything
        // within r tiles by the distance the whole of this world measures
        // with — the larger of the two axes, so the tiles it covers are a
        // square — and a circle drawn over a square's worth of ground
        // would show the four corners being spared when they are not. The
        // one thing a player has to be able to read off this picture is
        // how far it went, and a drawing that lies about the corners is
        // a picture of a rule the spell does not have. Thick strokes with
        // rounded joins take the hardness off the shape without moving
        // its edges.
        //
        // Two fronts a beat apart rather than one, so the fire has a body
        // and not just an edge: a white-hot leading edge and a slower
        // orange one behind it, which is what the eye already reads as
        // heat travelling outwards. The burn behind them is a dim orange
        // wash over everything the fire covered, so that at the moment the
        // front arrives at its limit the whole square is briefly lit and
        // the extent is legible in one glance.
        //
        // It is its own light, like the wave and the rift and the arc, so
        // the dark is not asked about it. Fire going up in a black cave
        // is seen, and the room round it besides.
        const reach = ((s.r ?? 0) + 0.5) * TILE;
        const face = (h: number) => {
          ctx.beginPath();
          ctx.rect(cx - h, cy - h, h * 2, h * 2);
        };
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        // The ground that burned, coming up as the front crosses it and
        // going out with the rest.
        const spread = Math.min(1, age / NOVA_OUT);
        ctx.globalAlpha = (1 - age) * 0.35 * lit;
        ctx.fillStyle = "#e0541a";
        face(reach * spread);
        ctx.fill();
        for (let i = 0; i < 2; i++) {
          const t = age - i * 0.16;
          if (t <= 0) continue;
          // The front is out to its limit a third of the way through the
          // clock and then stands there burning down, so two thirds of
          // the life is the finished square sitting still — which is the
          // part that is actually read, and the part that has to still be
          // bright. Spending more of the clock on the travel looked
          // better in the abstract and was worse on screen: the shape
          // only became complete at the moment it had half faded.
          const h = reach * Math.min(1, t / NOVA_OUT);
          ctx.globalAlpha = (1 - age) * (i === 0 ? 1 : 0.8) * lit;
          ctx.strokeStyle = i === 0 ? "#ffe6a6" : "#ff7a1e";
          ctx.lineWidth = (i === 0 ? 2.5 : 6) * (1 - age * 0.5);
          face(h);
          ctx.stroke();
        }
        // Tongues going out along the eight ways, standing on the front
        // and leaning outwards. Eight because eight is how many
        // directions this world has, and they land on the corners as well
        // as the sides — which is the same fact the square is drawn for.
        if (age < 0.8) {
          const h = reach * Math.min(1, age / NOVA_OUT);
          ctx.globalAlpha = (1 - age / 0.8) * lit;
          ctx.strokeStyle = "#ffc24d";
          ctx.lineWidth = 2;
          for (let i = 0; i < 8; i++) {
            const a = (i / 8) * Math.PI * 2;
            // Out to the front along this way, and a short lick past it.
            const ux = Math.cos(a), uy = Math.sin(a);
            const edge = h / Math.max(Math.abs(ux), Math.abs(uy));
            ctx.beginPath();
            ctx.moveTo(cx + ux * edge * 0.75, cy + uy * edge * 0.75);
            ctx.lineTo(cx + ux * (edge + 4), cy + uy * (edge + 4));
            ctx.stroke();
          }
        }
        // And the flash at the caster's own feet, where it started. Gone
        // in the first third, because this is the moment of ignition and
        // not a fire left burning on the tile.
        if (age < 0.3) {
          const t = 1 - age / 0.3;
          ctx.globalAlpha = t * lit;
          ctx.fillStyle = "#fff3cf";
          ctx.beginPath();
          ctx.arc(cx, cy, 3 + t * 7, 0, Math.PI * 2);
          ctx.fill();
        }
      } else if (s.kind === "find") {
        // A find person going off, the first of the spells that had nothing
        // to show until 2026-09-24, when it was asked for: "make sure all
        // spells have a graphical effect when cast". Each of them is its own
        // shape in its own colour, so that nobody watching mixes one up
        // with another or with anything else on this list; and each is
        // pinned to the tile, like the splash, and gone in about a second.
        //
        // This one is a golden compass needle on the caster's
        // tile, spinning and then settling. On the caster's own screen it
        // settles on the way to whoever was asked after, with a wobble as
        // it finds it, and a chevron runs out past its tip the way to go;
        // on every other screen it has no way to settle on and just spins
        // down, since the bearing is the asker's business and was never
        // sent. See the server's note on findBearing.
        //
        // Gold, and a darker, warmer gold than the temple's pale beam: a
        // needle is a thing of brass. It lies flat and points across the
        // map rather than up it, so it is drawn at the tile's true angles,
        // unflattened, where the rings on this list are squashed to lie on
        // the floor; a north-east drawn at thirty degrees would point at
        // the wrong tiles.
        const d = s.dir ?? 0;
        let th: number;
        if (d > 0) {
          // Clockwise from north, which on the screen is up.
          const want = ((d - 1) * Math.PI) / 4;
          const spin = Math.pow(1 - Math.min(1, age / 0.5), 3) * Math.PI * 4;
          const wobble = age > 0.5 ? 0.3 * Math.sin((age - 0.5) * 30) * Math.exp(-(age - 0.5) * 9) : 0;
          th = want + spin + wobble;
        } else {
          th = (1 - Math.pow(1 - age, 2)) * Math.PI * 5;
        }
        const ux = Math.sin(th), uy = -Math.cos(th);
        const px = -uy, py = ux;
        const L = TILE * 1.05, tail = TILE * 0.4, wd = 4.2;
        const env = Math.min(1, age / 0.12) * (age > 0.78 ? (1 - age) / 0.22 : 1);
        // The compass ring it swings in, on the floor.
        ctx.globalAlpha = env * 0.6 * lit;
        ctx.strokeStyle = "#e0a92a";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.ellipse(cx, cy, TILE * 0.7, TILE * 0.7 * 0.55, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = env * lit;
        // The pointing half, bright, and the tail, dark, so which end is
        // the answer is never in doubt.
        ctx.fillStyle = "#ffd24a";
        ctx.beginPath();
        ctx.moveTo(cx + ux * L, cy + uy * L);
        ctx.lineTo(cx + px * wd, cy + py * wd);
        ctx.lineTo(cx - px * wd, cy - py * wd);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = "#7a5410";
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = "#8a5e14";
        ctx.beginPath();
        ctx.moveTo(cx - ux * tail, cy - uy * tail);
        ctx.lineTo(cx + px * wd, cy + py * wd);
        ctx.lineTo(cx - px * wd, cy - py * wd);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = "#fff3c4";
        ctx.beginPath();
        ctx.arc(cx, cy, 1.8, 0, Math.PI * 2);
        ctx.fill();
        // The way to go, run out past the tip once the needle has found it.
        if (d > 0 && age > 0.5) {
          const u = (age - 0.5) / 0.5;
          const at = L + 6 + u * TILE * 1.1;
          const hx = cx + ux * at, hy = cy + uy * at;
          ctx.globalAlpha = Math.sin(u * Math.PI) * lit;
          ctx.strokeStyle = "#ffd24a";
          ctx.lineWidth = 3.2;
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          ctx.beginPath();
          ctx.moveTo(hx - ux * 7 + px * 7, hy - uy * 7 + py * 7);
          ctx.lineTo(hx, hy);
          ctx.lineTo(hx - ux * 7 - px * 7, hy - uy * 7 - py * 7);
          ctx.stroke();
        }
      } else if (s.kind === "provoke" || s.kind === "challenge") {
        // A provoke going off, or a challenge, which is drawn the same and
        // only sounds different (a horn: see sound.ts): a challenge shouted. Two bold red rings
        // burst off the caster and out along the floor, fast and hard, and
        // short strokes fly off their head for the shout itself. Red and
        // thick, where every other ring on this list is pale and thin,
        // because this is the one spell whose whole point is to be noticed:
        // it is the caster making themselves the loudest thing in the room.
        // A red that is not the blood's: brighter and hotter than any
        // spatter, and a ring rather than drops.
        ctx.lineCap = "round";
        for (let i = 0; i < 2; i++) {
          const t = (age - i * 0.2) / 0.8;
          if (t <= 0 || t >= 1) continue;
          // Out fast and slowing, which is what a blast does.
          const r = 5 + (1 - Math.pow(1 - t, 2)) * TILE * 2.2;
          ctx.globalAlpha = (1 - t) * lit;
          ctx.strokeStyle = "#d91e14";
          ctx.lineWidth = 6 * (1 - t) + 1;
          ctx.beginPath();
          ctx.ellipse(cx, cy + TILE * 0.2, r, r * 0.6, 0, 0, Math.PI * 2);
          ctx.stroke();
          ctx.strokeStyle = "#ff9a7a";
          ctx.lineWidth = 1.3;
          ctx.stroke();
        }
        if (age < 0.55) {
          const u = age / 0.55;
          const hx = cx, hy = cy - TILE * 0.55;
          ctx.globalAlpha = (1 - u) * lit;
          ctx.strokeStyle = "#ff3b2f";
          ctx.lineWidth = 3 * (1 - u) + 1;
          for (let k = 0; k < 7; k++) {
            // Up and out from the head, fanned over the top half: a
            // shout goes out of a mouth, not up out of the floor.
            const a = Math.PI + (k / 6) * Math.PI;
            const r0 = 9 + u * 9, r1 = r0 + 6;
            ctx.beginPath();
            ctx.moveTo(hx + Math.cos(a) * r0, hy + Math.sin(a) * r0);
            ctx.lineTo(hx + Math.cos(a) * r1, hy + Math.sin(a) * r1);
            ctx.stroke();
          }
        }
      } else if (s.kind === "convince") {
        // A convince taking (2026-10-26): a ring of warm gold drawing in
        // on the creature won over, and a soft glow over it as it closes.
        const t = Math.min(1, age / 0.6), fade = age < 0.5 ? 1 : Math.max(0, 1 - (age - 0.5) / 0.4);
        const r = TILE * 1.6 * (1 - t) + 6;
        ctx.globalAlpha = 0.85 * fade;
        ctx.strokeStyle = "rgb(255, 214, 120)";
        ctx.lineWidth = 2 + 2 * t;
        ctx.beginPath();
        ctx.ellipse(cx, cy + TILE * 0.2, r, r * 0.6, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 0.35 * t * fade;
        ctx.fillStyle = "rgb(255, 230, 160)";
        ctx.beginPath();
        ctx.arc(cx, cy - TILE * 0.2, TILE * 0.45, 0, Math.PI * 2);
        ctx.fill();
      } else if (s.kind === "elbereth") {
        // Elbereth said (2026-10-21): a white star kindled over the
        // caster's head, and a pale silver ring of its light going out
        // across the floor, the way Sam's phial blazed at Shelob. Its own
        // light, like the lamp's, so it is seen in the dark.
        const kindle = Math.min(1, age / 0.2), fade = age < 0.5 ? 1 : 1 - (age - 0.5) / 0.5;
        const t = Math.min(1, age / 0.7);
        const r = 6 + (1 - Math.pow(1 - t, 2)) * TILE * 2.4;
        ctx.globalAlpha = (1 - t) * 0.8;
        ctx.strokeStyle = "rgb(200, 215, 255)";
        ctx.lineWidth = 3 * (1 - t) + 1;
        ctx.beginPath();
        ctx.ellipse(cx, cy + TILE * 0.2, r, r * 0.6, 0, 0, Math.PI * 2);
        ctx.stroke();
        const sx = cx, sy = cy - TILE * 0.9 - age * 4;
        const sr = 5 * kindle * (0.9 + 0.1 * Math.sin(now / 90));
        ctx.globalAlpha = 0.45 * kindle * fade;
        ctx.fillStyle = "rgb(170, 195, 255)";
        ctx.beginPath();
        ctx.arc(sx, sy, sr * 2.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = kindle * fade;
        ctx.fillStyle = "rgb(250, 252, 255)";
        ctx.beginPath(); // a four-pointed star
        ctx.moveTo(sx, sy - sr * 1.9);
        ctx.lineTo(sx + sr * 0.4, sy - sr * 0.4);
        ctx.lineTo(sx + sr * 1.9, sy);
        ctx.lineTo(sx + sr * 0.4, sy + sr * 0.4);
        ctx.lineTo(sx, sy + sr * 1.9);
        ctx.lineTo(sx - sr * 0.4, sy + sr * 0.4);
        ctx.lineTo(sx - sr * 1.9, sy);
        ctx.lineTo(sx - sr * 0.4, sy - sr * 0.4);
        ctx.closePath();
        ctx.fill();
      } else if (s.kind === "haste") {
        // A haste going off: pale yellow-white wind whipping round the
        // caster from the feet up, four streaks at four heights going
        // round fast, each a bright head with a tail thinning behind it.
        // The part of each turn that is behind the body is drawn fainter,
        // so the wind reads as going round the caster and not across them.
        // Streaks and not a ring, and moving round and not out, because a
        // haste changes how the caster goes and nothing about the ground.
        ctx.lineCap = "round";
        const env = Math.min(1, age / 0.15) * (1 - age);
        for (let i = 0; i < 4; i++) {
          const h = cy + TILE * 0.35 - i * TILE * 0.3;
          const rx = TILE * (0.7 + 0.12 * (i % 2)), ry = rx * 0.34;
          const head = age * Math.PI * 4.5 + i * 1.9;
          const len = 2.4;
          for (let j = 0; j < 8; j++) {
            const a0 = head - (len * (j + 1)) / 8, a1 = head - (len * j) / 8;
            const behind = Math.sin((a0 + a1) / 2) < 0 ? 0.45 : 1;
            ctx.globalAlpha = env * (1 - j / 8) * behind * lit;
            ctx.strokeStyle = j < 2 ? "#ffffff" : "#fff1a0";
            ctx.lineWidth = 3.6 - j * 0.35;
            ctx.beginPath();
            ctx.ellipse(cx, h, rx, ry, 0, a0, a1);
            ctx.stroke();
          }
        }
      } else if (s.kind === "light") {
        // A light spell going off: a warm bloom of light swelling round
        // the caster, over-reaching a little and settling back, then
        // fading into the lamp the state carries from here on. It is its
        // own light, like the beam and the nova, so the dark is not asked
        // about it (see lit above): the one thing a lamp being lit in a
        // black cave must be is seen.
        //
        // Round and soft and warm, where the beam is a shaft and a level's
        // glow is a thin yellow ring. Painted over what is under it rather
        // than added onto it: added, it was a warm gold in the dark and a
        // sickly lime on grass, which is where most lights are lit.
        const swell = age < 0.35 ? 1 - Math.pow(1 - age / 0.35, 2) : 1 - 0.2 * Math.min(1, (age - 0.35) / 0.3);
        const fade = age < 0.35 ? 1 : 1 - (age - 0.35) / 0.65;
        const R = Math.max(1, TILE * 1.9 * swell);
        const ly = cy - TILE * 0.15;
        const grad = ctx.createRadialGradient(cx, ly, 0, cx, ly, R);
        grad.addColorStop(0, "rgba(255, 244, 200, 0.8)");
        grad.addColorStop(0.4, "rgba(255, 205, 110, 0.45)");
        grad.addColorStop(1, "rgba(255, 180, 70, 0)");
        ctx.globalAlpha = fade;
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(cx, ly, R, 0, Math.PI * 2);
        ctx.fill();
        // Motes thrown off it, drifting out and up as the light settles.
        ctx.fillStyle = "#fff4c8";
        for (let i = 0; i < 8; i++) {
          const a = (i / 8) * Math.PI * 2 + 0.3;
          const r = 6 + age * TILE * 1.3;
          ctx.globalAlpha = fade * 0.9;
          ctx.beginPath();
          ctx.arc(cx + Math.cos(a) * r, ly + Math.sin(a) * r * 0.7 - age * 8, 1.9, 0, Math.PI * 2);
          ctx.fill();
        }
      } else if (s.kind === "bond") {
        // A blood bond laid: a dark red thread drawn out of the caster and
        // across to the person taken on, sagging a little between them the
        // way a thread does, with a drop of blood running ahead of it; then
        // two more drops running down it the same way, caster to friend,
        // which is the way the spell sends the wounds' cost, and a knot
        // pulled tight round the friend's feet. The thread is a line from
        // one to the other because what there is to see is who is bound to
        // whom. A dark red, deeper than a blow's blood and nothing like a
        // provoke's bright one.
        const knotAt = (x: number, y: number, t: number) => {
          const kr = 13 - t * 5;
          ctx.strokeStyle = "#9e0f24";
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.ellipse(x, y, kr, kr * 0.5, 0, 0, Math.PI * 2);
          ctx.stroke();
        };
        const fade = age < 0.5 ? 1 : 1 - (age - 0.5) / 0.5;
        const reach = Math.min(1, age / 0.4);
        if (s.fx !== undefined && s.fy !== undefined) {
          const f = toScreen(s.fx, s.fy, s.z);
          const ax = Math.round(f.sx) + TILE / 2, ay = Math.round(f.sy) + TILE / 2 - TILE * 0.2;
          const bx = cx, by = cy - TILE * 0.2;
          const len = Math.hypot(bx - ax, by - ay);
          const mx = (ax + bx) / 2, my = (ay + by) / 2 + Math.min(14, len * 0.3);
          const at = (u: number) => ({
            x: (1 - u) * (1 - u) * ax + 2 * (1 - u) * u * mx + u * u * bx,
            y: (1 - u) * (1 - u) * ay + 2 * (1 - u) * u * my + u * u * by,
          });
          const thread = () => {
            ctx.beginPath();
            const n = 24;
            for (let i = 0; i <= n; i++) {
              const p = at((i / n) * reach);
              if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
            }
            ctx.stroke();
          };
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          ctx.globalAlpha = fade * 0.55 * lit;
          ctx.strokeStyle = "#3a0008";
          ctx.lineWidth = 4.5;
          thread();
          ctx.globalAlpha = fade * lit;
          ctx.strokeStyle = "#b3122a";
          ctx.lineWidth = 1.8;
          thread();
          ctx.fillStyle = "#e0203a";
          const drop = (u: number) => {
            const p = at(u);
            ctx.beginPath();
            ctx.arc(p.x, p.y, 2.6, 0, Math.PI * 2);
            ctx.fill();
          };
          if (reach < 1) drop(reach);
          else for (let k = 0; k < 2; k++) {
            const u = (age - 0.4) / 0.45 - k * 0.3;
            if (u > 0 && u < 1) drop(u);
          }
        }
        if (reach >= 1) {
          ctx.globalAlpha = fade * lit;
          knotAt(cx, cy + TILE * 0.3, Math.min(1, (age - 0.4) / 0.25));
        }
      } else if (s.kind === "unbond") {
        // A blood bond let go of: a short length of the same dark red
        // thread over the caster, parting in the middle, the two ends
        // dropping away and a few drops falling out of the break. It is
        // drawn on the caster alone, and the server's note says why: the
        // person let go of may be somewhere this screen has no business
        // being shown.
        // Over the head rather than across the body, so it is not lost in
        // the figure's own colours.
        const y0 = cy - TILE * 0.95;
        const gap = 1 + age * 12, droop = age * age * 20;
        ctx.globalAlpha = (1 - age) * lit;
        ctx.lineCap = "round";
        for (const side of [-1, 1]) {
          const strand = () => {
            ctx.beginPath();
            ctx.moveTo(cx + side * 22, y0 - 3);
            ctx.quadraticCurveTo(cx + side * (10 + gap * 0.3), y0 + 4, cx + side * gap, y0 + droop);
            ctx.stroke();
          };
          ctx.strokeStyle = "rgba(58, 0, 8, 0.6)";
          ctx.lineWidth = 4.5;
          strand();
          ctx.strokeStyle = "#b3122a";
          ctx.lineWidth = 2;
          strand();
        }
        // The snap itself, a flash where it parted.
        if (age < 0.25) {
          ctx.globalAlpha = (1 - age / 0.25) * lit;
          ctx.fillStyle = "#ffb3b3";
          ctx.beginPath();
          ctx.arc(cx, y0 + 2, 3.5, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = (1 - age) * lit;
        ctx.fillStyle = "#e0203a";
        for (let k = 0; k < 3; k++) {
          const t = age - k * 0.12;
          if (t <= 0) continue;
          ctx.beginPath();
          ctx.arc(cx + (k - 1) * 4, y0 + 3 + t * t * 44, 2.2, 0, Math.PI * 2);
          ctx.fill();
        }
      } else if (s.kind === "break") {
        // Shards flung outwards and tumbling: straight splinters rather than
        // the round puffs a miss or a death makes, so a snapped weapon reads
        // differently from a dodged blow.
        ctx.strokeStyle = "#c9b48a";
        ctx.lineWidth = 2;
        for (let i = 0; i < 7; i++) {
          const a = (i / 7) * Math.PI * 2 + 0.4;
          const r = 4 + age * 18;
          const px = cx + Math.cos(a) * r, py = cy + Math.sin(a) * r - age * 6;
          const spin = a + age * 5;
          ctx.beginPath();
          ctx.moveTo(px - Math.cos(spin) * 3, py - Math.sin(spin) * 3);
          ctx.lineTo(px + Math.cos(spin) * 3, py + Math.sin(spin) * 3);
          ctx.stroke();
        }
      } else if (s.kind === "spark") {
        // Steel skidding off armor: nothing was raised to meet the blow,
        // it arrived and went off the plate. Thin bright streaks thrown
        // out low and short-lived, against the round soft puff of a blow
        // that something was actually put in the way of.
        ctx.lineCap = "round";
        for (let i = 0; i < 9; i++) {
          const a = (i / 9) * Math.PI * 2 + 0.7;
          const r = 2 + age * 15;
          const px = cx + Math.cos(a) * r, py = cy + Math.sin(a) * r * 0.7;
          // White at the strike, cooling to orange as it flies.
          ctx.strokeStyle = age < 0.35 ? "#fff6d0" : age < 0.7 ? "#ffd257" : "#e08a2a";
          ctx.lineWidth = 2 - age;
          ctx.beginPath();
          ctx.moveTo(px, py);
          ctx.lineTo(px + Math.cos(a) * 4, py + Math.sin(a) * 4 * 0.7);
          ctx.stroke();
        }
      } else if (s.kind === "blood") {
        // A blow that went in, in the colour the thing bleeds — the same
        // colours its marks on the ground are drawn in, since they are
        // the same fluid a moment apart. It is a puff like a block's,
        // because the three things a swing can look like should read as
        // three of a kind: something met it, it skidded off, it went in.
        // What sets this one apart is the weight. Drops are thrown
        // outwards and then fall, so they arc rather than spreading in a
        // ring, and there are more of them, smaller.
        const rgb = SPATTER_RGB[s.id ?? 0] ?? [190, 24, 24];
        ctx.fillStyle = `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
        for (let i = 0; i < 9; i++) {
          const a = (i / 9) * Math.PI * 2 + 0.35;
          const r = 2 + age * 16;
          // Up and out, then down: the flight of something wet and heavy.
          const fall = age * age * 18 - age * 7;
          ctx.beginPath();
          ctx.arc(cx + Math.cos(a) * r, cy + Math.sin(a) * r * 0.75 + fall, 2.6 - age * 1.8, 0, Math.PI * 2);
          ctx.fill();
        }
      } else {
        ctx.fillStyle = s.kind === "death" ? "#a00" : s.kind === "block" ? "#9cc" : "#ccc";
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2;
          const r = 3 + age * 14;
          ctx.beginPath();
          ctx.arc(cx + Math.cos(a) * r, cy + Math.sin(a) * r, 3 - age * 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
    }
  }

}

/**
 * Wrap to a width in pixels rather than in characters, measured in whatever
 * font the context is currently set to. A word too long for the box on its
 * own is broken across lines: nothing else can make it fit, and a name of
 * two hundred letters is a thing someone will type.
 */
function wrapToWidth(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const lines: string[] = [];
  let cur = "";
  const push = () => {
    if (cur) lines.push(cur);
    cur = "";
  };
  for (const word of text.split(" ")) {
    let w = word;
    while (ctx.measureText(w).width > maxW && w.length > 1) {
      let n = 1;
      while (n < w.length && ctx.measureText(w.slice(0, n + 1)).width <= maxW) n++;
      push();
      lines.push(w.slice(0, n));
      w = w.slice(n);
    }
    if (!w) continue;
    const next = cur ? cur + " " + w : w;
    if (cur && ctx.measureText(next).width > maxW) {
      push();
      cur = w;
    } else cur = next;
  }
  push();
  return lines;
}

/**
 * An Istari light's shaft (2026-10-26): a straight beam from the caster's
 * hand out past the middle of the far tile to its edge, a wide soft glow
 * round a white-hot core, and a flare at the hand. It is its own light,
 * like the chain's arcs, so the dark is not asked about it. It flashes out
 * at once and fades, the glow a little slower than the core, because the
 * light is thrown in an instant and what lingers is the eye's memory of it.
 */
function drawShaft(
  ctx: CanvasRenderingContext2D,
  a: Arc,
  toScreen: (tx: number, ty: number, z?: number) => { sx: number; sy: number },
  t: number,
) {
  const from = toScreen(a.fx, a.fy, a.z), to = toScreen(a.x, a.y, a.z);
  const ax = from.sx + TILE / 2, ay = from.sy + TILE / 2 - 6;
  let bx = to.sx + TILE / 2, by = to.sy + TILE / 2 - 6;
  const len = Math.hypot(bx - ax, by - ay);
  if (len >= 1) {
    // On to the far edge of the last tile, so the light fills it.
    bx += ((bx - ax) / len) * TILE * 0.45;
    by += ((by - ay) / len) * TILE * 0.45;
  }
  ctx.save();
  ctx.lineCap = "round";
  ctx.globalCompositeOperation = "lighter";
  const line = (w: number, style: string, alpha: number) => {
    if (len < 1 || alpha <= 0) return;
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = style;
    ctx.lineWidth = w;
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(bx, by);
    ctx.stroke();
  };
  // A swell for the first tenth, then a fade.
  const grow = Math.min(1, t / 0.1);
  line(TILE * 0.8 * grow, "rgba(255,250,225,0.28)", 1 - t);
  line(TILE * 0.4 * grow, "rgba(255,252,238,0.6)", 1 - t * 0.9);
  line(Math.max(2, TILE * 0.1), "#ffffff", 1 - t * 0.8);
  // The flare at the hand.
  ctx.globalAlpha = 1 - t;
  const r = TILE * (0.35 + 0.25 * grow);
  const g = ctx.createRadialGradient(ax, ay, 0, ax, ay, r);
  g.addColorStop(0, "rgba(255,255,255,0.95)");
  g.addColorStop(1, "rgba(255,250,225,0)");
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(ax, ay, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
